<template>
  <form @submit.prevent="onSubmit">
    <!-- ------------ -->
    <!--   DIALOGS    -->
    <!-- ------------ -->
    <contents-dialog ref="contentsDialog"></contents-dialog>
    <attached-dialog ref="attachedDialog"></attached-dialog>
    <confirm-dialog ref="confirmDialog"></confirm-dialog>
    <delete-artefact-dialog ref="deleteArtefactDialog"></delete-artefact-dialog>
    <clear-dialog ref="clearDialog"></clear-dialog>
    <!-- ----------------- -->
    <!-- TOP ROW (HEADER)  -->
    <!-- ----------------- -->
    <div class="row h-40">
      <p class="bb-page-title mt-3 mb-1">ITEM DETAILS</p>
    </div>
    <!-- -------------------------------------------------------------->
    <!-- MAIN CONTAINER -->
    <!-- -------------- -->
    <div
      v-if="isReady"
      class="row mt-0"
    >
      <!-- ------------------------------------------------------------>
      <!--    LEFT COL (BUTTONS)   -->
      <!-- ----------------------- -->
      <div class="col-3">
        <!-- ---------- -->
        <!-- BUTTONS    -->
        <!-- ---------- -->
        <div class="row mt-4">
          <div
            class="btn-group-vertical btn-group-lg"
            role="group"
          >
            <!-- ------------ -->
            <!--    cancel    -->
            <!-- ------------ -->
            <button
              class="btn btn-lg btn-warning"
              type="button"
              @click="manageCancel()"
            >
              Cancel
            </button>
            <!-- ------------ -->
            <!--    delete    -->
            <!-- ------------ -->
            <button
              v-if="!isNewItem && !this.delete"
              class="btn btn-primary btn-lg"
              type="button"
              @click="confirmDeletion()"
            >
              Delete
            </button>
            <!-- ------------ -->
            <!--     add      -->
            <!-- ------------ -->
            <button
              v-if="isNewItem && hasItemName"
              :disabled="
                wornLocationMissingForNew ||
                artefactTypeMissingForNew ||
                isError
              "
              class="btn btn-primary btn-lg"
              type="submit"
            >
              Add
            </button>
            <!-- ------------ -->
            <!--      ok      -->
            <!-- ------------ -->
            <button
              v-if="showOkButton"
              :disabled="isError"
              class="btn btn-primary btn-lg"
              type="submit"
            >
              OK
            </button>
            <button
              v-if="documentViewerUrl"
              class="btn btn-primary btn-lg"
              type="button"
              @click="openDocument()"
            >
              Document
            </button>
          </div>
        </div>
      </div>
      <!-- ------------------------------------------------------------>
      <!--    RIGHT COL (DATA)   -->
      <!-- --------------------- -->
      <div class="col m-3 mt-0 bb-tooltip-wrapper">
        <!-- ---------- -->
        <!--    type    -->
        <!-- ---------- -->
        <p class="bb-hdg-lg mb-1 mt-0 no-wrap">{{ itemHeading }}</p>
        <!-- ------------ -->
        <!--    divider   -->
        <!-- ------------ -->
        <hr
          v-if="hasItemName"
          class="bb-horizontal-divider"
        />
        <!-- -------------------- -->
        <!--   DATA TAB NAV       -->
        <!-- -------------------- -->
        <div v-if="hasItemName">
          <ul class="nav nav-pills mb-3 bb-item-tabs">
            <li
              class="nav-item"
              v-if="item_type !== 'Artefact'"
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'basic' }"
                @click="setActiveTab('basic')"
              >
                Basic
              </button>
            </li>
            <li
              class="nav-item"
              v-if="item_type === 'Artefact'"
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'artefact' }"
                @click="setActiveTab('artefact')"
              >
                Basic
              </button>
            </li>
            <li
              class="nav-item"
              v-if="showCountTab"
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'count' }"
                @click="setActiveTab('count')"
              >
                Quantities
              </button>
            </li>
            <li class="nav-item">
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'storage' }"
                @click="setActiveTab('storage')"
              >
                Location
              </button>
            </li>
            <li
              class="nav-item"
              v-if="resolvedItemType === 'Armour'"
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'armour' }"
                @click="setActiveTab('armour')"
              >
                Armour
              </button>
            </li>
            <li
              class="nav-item"
              v-if="
                resolvedItemType === 'Melee Weapon' ||
                resolvedItemType === 'Ranged Weapon'
              "
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'weapon' }"
                @click="setActiveTab('weapon')"
              >
                Weapon
              </button>
            </li>
            <li
              class="nav-item"
              v-if="activeItem.storage"
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'container' }"
                @click="setActiveTab('container')"
              >
                Container
              </button>
            </li>
            <li
              class="nav-item"
              v-if="
                item_type === 'Healing' ||
                resolvedItemType === 'Healing' ||
                activeItem?.artefact_type?.toLowerCase?.() === 'healing'
              "
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'healing' }"
                @click="setActiveTab('healing')"
              >
                Healing
              </button>
            </li>
            <li
              class="nav-item"
              v-if="
                item_type === 'Poison' ||
                resolvedItemType === 'Poison' ||
                activeItem?.artefact_type?.toLowerCase?.() === 'poison'
              "
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'poison' }"
                @click="setActiveTab('poison')"
              >
                Poison
              </button>
            </li>
            <li
              class="nav-item"
              v-if="
                item_type === 'Specialist' ||
                resolvedItemType === 'Specialist' ||
                activeItem?.artefact_type?.toLowerCase?.() === 'specialist'
              "
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'specialist' }"
                @click="setActiveTab('specialist')"
              >
                Specialist
              </button>
            </li>
            <li class="nav-item">
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'adjs' }"
                @click="setActiveTab('adjs')"
              >
                Adjs
              </button>
            </li>
            <li class="nav-item">
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'magic' }"
                @click="setActiveTab('magic')"
              >
                Magic
              </button>
            </li>
            <li
              class="nav-item"
              v-if="showArtefactTraitsTab"
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'traits' }"
                @click="setActiveTab('traits')"
              >
                Traits
              </button>
            </li>
            <li
              class="nav-item"
              v-if="item_type === 'Artefact'"
            >
              <button
                type="button"
                class="nav-link me-1"
                :class="{ active: activeTab === 'image' }"
                @click="setActiveTab('image')"
              >
                Image
              </button>
            </li>
          </ul>
        </div>
        <!-- ------------ -->
        <!--    divider   -->
        <!-- ------------ -->
        <hr
          v-if="hasItemName"
          class="mt-1 mb-1"
          style="border-top: 1.5px solid grey; opacity: 0.75"
        />
        <!-- ========================= -->
        <!-- TAB 1: BASIC              -->
        <!-- ========================= -->
        <!--   :short-description-readonly="shortDescriptionReadonly"
          :short-description-required="shortDescriptionRequired" -->
        <ItemBasicTab
          v-if="activeTab === 'basic'"
          :resolvedItemType="resolvedItemType"
          :active-item="activeItem"
          :local-item="local_item"
          :static-item="selectedStaticItem"
          :has-item-name="hasItemName"
          :row-number="rowNumber"
          :item-name="itemName"
          :item-names="item_names"
          :error-fields="errorFields"
          :format-enc="formatEnc"
          :show-campaign-ref="showCampaignRef"
          :campaign-ref-value="campaignRefValue"
          :calc-qty-tot-enc="calcQtyTotEnc"
          :is-weapon-or-armour="isWeaponOrArmour"
          :compute-item-state="isWeaponOrArmour ? computeItemState : ''"
          :on-key-down="onKeyDown"
          :on-item-select="onItemSelect"
          :clear-input-error="clearInputError"
          :on-capitalise-first-letter="onCapitaliseFirstLetter"
          :delayed-emit-update="delayedEmitUpdate"
          :on-counter-changed="onCounterChanged"
        />

        <!-- ======================= -->
        <!-- TAB 2: ARTEFACT         -->
        <!-- ======================= -->
        <ItemArtefactTab
          v-if="activeTab === 'artefact'"
          :active-item="activeItem"
          :local-item="local_item"
          :is-campaign-artefact="isEffectiveCampaignArtefact"
          :short-description-readonly="shortDescriptionReadonly"
          :artefact-types="filteredArtefactTypes"
          :artefact-base-items="artefactBaseItemsForSelection"
          :item-name="itemName"
          :item-names="item_names"
          :dropped-artefact-options="droppedArtefactOptions"
          :materials="materials"
          :qualities="qualities"
          :ages="ages"
          :races="races"
          :currencies="currencies"
          :error-fields="errorFields"
          :show-campaign-ref="showCampaignRef"
          :campaign-ref-value="campaignRefValue"
          :display-value-lower="displayValueLower"
          :display-value-upper="displayValueUpper"
          :readonly-race-name="readonlyRaceName"
          :got-value="gotValue"
          :is-editable="isEditable"
          :is-new-item="isNewItem"
          :on-key-down="onKeyDown"
          :clear-input-error="clearInputError"
          :on-item-select="onItemSelect"
          :on-artefact-field-change="onArtefactFieldChange"
          :on-name-input="onNameInput"
          :on-description-input="onDescriptionInput"
          :on-notes-input="onNotesInput"
          :get-race-label="getRaceLabel"
          :get-artefact-base-item-label="getArtefactBaseItemLabel"
          :get-currency-label="getCurrencyLabel"
          :reduce-object="reduceObject"
          :handle-numeric-input="handleNumericInput"
          :on-estimated-value-toggle="onEstimatedValueToggle"
          :on-campaign-item-toggle="onCampaignItemToggle"
          :on-dropped-artefact-toggle="onDroppedArtefactToggle"
          :delayed-emit-update="delayedEmitUpdate"
          :on-capitalise-first-letter="onCapitaliseFirstLetter"
        />
        <!-- ------------------------------------ -->
        <!--   WHERE ITEM NAME PRESENT/SELECTED   -->
        <!-- ------------------------------------ -->
        <div v-if="hasItemName">
          <!-- ====================== -->
          <!-- TAB 3 : COUNT         -->
          <!-- ====================== -->
          <ItemQuantityTab
            v-if="activeTab === 'count' && showCountTab"
            :active-item="activeItem"
            :local-item="local_item"
            :units="units"
            :error-fields="errorFields"
            :format-enc="formatEnc"
            :calc-qty-tot-enc="calcQtyTotEnc"
            :on-counter-changed="onCounterChanged"
            :clear-input-error="clearInputError"
            :on-enc-input="onEncInput"
            :on-enc-blur="onEncBlur"
            :on-countable-toggle="onCountableToggle"
            :on-key-down="onKeyDown"
            :on-unit-select="onUnitSelect"
            :is-new-item="isNewItem"
            :show-cur-unit-count="true"
          />

          <!-- ============================ -->
          <!-- TAB 4: CONTAINER e.g. box    -->
          <!-- ============================ -->
          <ItemContainerTab
            v-if="activeTab === 'container'"
            :active-item="activeItem"
            :format-enc="formatEnc"
            :display-direct-cnt="displayDirectCnt"
            :display-total-cnt="displayTotalCnt"
            :display-container-enc="displayContainerEnc"
            :display-capacity-enc="displayCapacityEnc"
            :display-contents-enc="displayContentsEnc"
            :display-tot-enc="displayTotEnc"
            :display-available-enc="displayAvailableEnc"
            :manage-contents="manageContents"
          />

          <!-- ========================= -->
          <!-- TAB 5: LOCATION           -->
          <!-- ========================= -->
          <ItemLocationTab
            v-if="activeTab === 'storage'"
            :active-item="activeItem"
            :local-item="local_item"
            :containers="containers"
            :worn-locations="worn_locations"
            :attach-locations="attach_locations"
            :error-fields="errorFields"
            :item-type="resolvedItemType"
            :on-held-toggle="onHeldToggle"
            :on-key-down="onKeyDown"
            :get-container-label="getContainerLabel"
            :reduce-object="reduceObject"
            :clear-input-error="clearInputError"
            :on-container-select="onContainerSelect"
            :on-worn-location-select="onWornLocationSelect"
            :get-attach-location-label="getAttachLocationLabel"
            :on-attached-location-select="onAttachedLocationSelect"
            :manage-attached="manageAttached"
            :attached-count="getAttachedItemsCount(activeItem)"
          />

          <!-- ======================= -->
          <!-- TAB 6: ARMOUR           -->
          <!-- ======================= -->
          <ItemArmourTab
            v-if="activeTab === 'armour'"
            :active-item="activeItem"
            :local-item="local_item"
            :armour-locations="armour_locations"
            :error-fields="errorFields"
            :max-ap="max_ap"
            :max-hp="max_hp"
            :compute-item-state="computeItemState"
            :dex-pen-display="armourDexPenDisplay"
            :reduce-object="reduceObject"
            :on-key-down="onKeyDown"
            :on-armour-location-select="onArmourLocationSelect"
            :on-equipped-toggle="onEquippedToggle"
            :on-counter-changed="onCounterChanged"
            :on-artefact-field-change="onArtefactFieldChange"
          />

          <!-- ======================= -->
          <!-- TAB 7: WEAPON           -->
          <!-- ======================= -->
          <ItemWeaponTab
            v-if="activeTab === 'weapon'"
            :active-item="activeItem"
            :local-item="local_item"
            :effective-item-type="resolvedItemType"
            :worn-locations="worn_locations"
            :show-worn-location-field="showWeaponWornLocationField"
            :error-fields="errorFields"
            :max-ap="max_ap"
            :max-hp="max_hp"
            :is-weapon-or-armour="isWeaponOrArmour"
            :compute-item-state="computeItemState"
            :character="character"
            :weapon-skill-rows="weaponSkillRows"
            :reduce-object="reduceObject"
            :on-key-down="onKeyDown"
            :on-worn-location-select="onWornLocationSelect"
            :on-counter-changed="onCounterChanged"
            :on-worn-toggle="onWornToggle"
          />

          <!-- ======================= -->
          <!-- TAB 8: HEALING          -->
          <!-- ======================= -->
          <ItemHealingTab
            v-if="activeTab === 'healing'"
            :active-item="activeItem"
            :local-item="local_item"
            :is-new-item="isNewItem"
            :units="units"
            :healing-types="healing_types"
            :error-fields="errorFields"
            :on-field-changed="onArtefactFieldChange"
            :read-only-fields="
              getArtefactDetailReadOnlyFields([
                'healing_type',
                'recovers',
                'delay',
              ])
            "
            :on-counter-changed="onCounterChanged"
          />

          <ItemPoisonTab
            v-if="activeTab === 'poison'"
            :active-item="activeItem"
            :local-item="local_item"
            :is-new-item="isNewItem"
            :poison-types="poison_types"
            :error-fields="errorFields"
            :on-field-changed="onArtefactFieldChange"
            :read-only-fields="
              getArtefactDetailReadOnlyFields([
                'poison_type',
                'pot',
                'effect',
                'delay',
                'damage_dice',
                'duration',
                'resist',
              ])
            "
          />

          <ItemSpecialistTab
            v-if="activeTab === 'specialist'"
            :active-item="activeItem"
          />

          <!-- ========================= -->
          <!-- TAB 10: ADJUSTMENTS       -->
          <!-- ========================= -->
          <ItemAdjustmentsTab
            v-if="activeTab === 'adjs'"
            :item_characteristic_adj_reasons="item_characteristic_adj_reasons"
            :item_ability_adj_reasons="item_ability_adj_reasons"
            :item_skill_adj_reasons_applied="item_skill_adj_reasons_applied"
            :item_skill_adj_reasons_optional="item_skill_adj_reasons_optional"
          />

          <!-- ========================= -->
          <!-- TAB 11: MAGIC             -->
          <!-- ========================= -->
          <ItemMagicTab
            v-if="activeTab === 'magic'"
            :has-item-name="hasItemName"
            :item_runes_integrated="item_runes_integrated"
            :item_runes_unintegrated="item_runes_unintegrated"
            :item_spells_sorcery="item_spells_sorcery"
            :item_spells_divine="item_spells_divine"
          />

          <ItemTraitsTab
            v-if="activeTab === 'traits'"
            :traits="artefactTraitRows"
            :all-traits="traits"
            :selected-trait-id="selectedArtefactTraitId"
            :allow-trait-selection="canSelectArtefactTrait"
            :on-trait-select="onTraitSelect"
          />

          <!-- ========================= -->
          <!-- TAB 12: IMAGE             -->
          <!-- ========================= -->
          <ItemImageTab
            v-if="activeTab === 'image'"
            :has-item-name="hasItemName"
            :image="image"
            :show-change-image="showChangeImageLink"
            :show-clear-image="showClearImageLink"
            :on-preview-image="previewImage"
            :on-clear-image="clearImage"
          />
        </div>
        <!-- ============ -->
        <!--    ERRORS    -->
        <!-- ============ -->
        <div v-if="errors.length > 0">
          <div class="row mt-2 mb-3">
            <div class="form-group">
              <div class="row">
                <div class="col text-end me-3">
                  <div
                    v-for="err in errors"
                    :key="err"
                    class="bb-error-wrap"
                    style="white-space: pre-line"
                  >
                    <div class="row mb-3">
                      {{ err }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import DeleteArtefactDialog from './DeleteArtefactDialog.vue'
import ItemAdjustmentsTab from './ItemAdjustmentsTab.vue'
import ItemSpecialistTab from './ItemSpecialistTab.vue'
import ItemContainerTab from './ItemContainerTab.vue'
import ItemArtefactTab from './ItemArtefactTab.vue'
import ItemQuantityTab from './ItemQuantityTab.vue'
import ItemLocationTab from './ItemLocationTab.vue'
import ContentsDialog from './ContentsDialog.vue'
import AttachedDialog from './AttachedDialog.vue'
import ItemHealingTab from './ItemHealingTab.vue'

import ConfirmDialog from './ConfirmDialog.vue'
import ItemArmourTab from './ItemArmourTab.vue'
import ItemWeaponTab from './ItemWeaponTab.vue'
import ItemPoisonTab from './ItemPoisonTab.vue'
import ItemTraitsTab from './ItemTraitsTab.vue'
import ItemImageTab from './ItemImageTab.vue'
import ItemMagicTab from './ItemMagicTab.vue'
import ItemBasicTab from './ItemBasicTab.vue'
import ClearDialog from './ClearDialog.vue'

import defaultImage from '../assets/images/default-item-image.png'
import contentsImg from '../assets/images/bbContents.png'

import normaliseLeadingLetterText from '../helpers/normaliseLeadingLetterText'
import calcContainerAvailability from '../helpers/calcContainerAvailability'
import getStringAfterLastSlash from '../helpers/getStringAfterLastSlash'
import buildAdjReasonsString from '../helpers/buildAdjReasonsString'
import capitaliseFirstLetter from '../helpers/capitaliseFirstLetter'
import sanitisePositiveInt from '../helpers/sanitisePositiveInt'
import getWeaponDamageDice from '../helpers/getWeaponDamageDice'
import buildRunesString from '../helpers/buildRunesString'
import isObjectChanged from '../helpers/isObjectChanged'
import copyObjectNoRef from '../helpers/copyObjectNoRef'
import isAlphanumeric from '../helpers/isAlphanumeric'
import setItemValues from '../helpers/setItemValues'
import createTempId from '../helpers/createTempId'
import isEmpty from '../helpers/isEmpty'
import isUUID from '../helpers/isUUID'

import {
  getEffectiveItemType,
  getItemPropertySource,
  getNormalisedEquipmentType,
} from '../helpers/getEffectiveItemType'

import { evaluateWeaponUsability } from '../helpers/weaponUsabilityCore'

import { inject } from 'vue'

export default {
  name: 'ItemCard',
  emits: [
    'add-character-item',
    'update-character-item',
    'delete-character-item',
    'close-item-panel',
    'confirm-item-ok',
  ],
  //========================================================
  // components
  //------------
  components: {
    ConfirmDialog,
    DeleteArtefactDialog,
    ContentsDialog,
    AttachedDialog,
    ClearDialog,
    ItemArtefactTab,
    ItemQuantityTab,
    ItemLocationTab,
    ItemContainerTab,
    ItemBasicTab,
    ItemArmourTab,
    ItemWeaponTab,
    ItemHealingTab,
    ItemPoisonTab,
    ItemSpecialistTab,
    ItemAdjustmentsTab,
    ItemMagicTab,
    ItemTraitsTab,
    ItemImageTab,
  },
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // props
  //-------
  props: {
    item: {
      type: Object,
      default: null,
    },
    item_type: {
      type: String,
      required: true,
    },
    character: {
      type: Object,
      required: true,
    },
  },
  //========================================================
  // data
  //------
  data() {
    return {
      defaultImage,
      original_item: {
        tally_qty: 0,
        player_notes: '',
        short_description: '',
        container: null,
        worn_location: null,
        attach_location: null,
        armour_location: null,
        default_currency: null,
        ap: 0,
        hp: 0,
        enc: 0.01,
        worn: false,
        held: false,
        equipped: false,
        save_item_id: null,
        runes: [],
        spells: [],
        max_unit_count: null,
        cur_unit_count: null,
        value_lower: null,
        value_upper: null,
        estimated_value: false,
        artefact_type: '',
        trait: null,
        material: '',
        quality: '',
        age: '',
        pwr_storage: 0,
        image: null,
        race: null,
        currency: null,
      },
      local_item: {},
      save_item_id: null,
      // last valid container selection (for reverting v-select when
      // capacity validation fails)
      previousContainerId: null,
      pendingTimers: {}, // per-field debounce timers (key -> timeout id)
      //
      errors: [],
      errorFields: [],
      items: [],
      item_names: [],
      containers: [],
      artefact_types: [],
      artefact_type_records: [],
      traits: [],
      qualities: [],
      ages: [],
      races: [],
      currencies: [],
      materials: [],
      units: [],
      poison_types: [],
      healing_types: [],
      worn_locations: [],
      attach_locations: [],
      armour_locations: [],
      item_skill_adj_reasons_applied: '',
      item_skill_adj_reasons_optional: '',
      item_characteristic_adj_reasons: '',
      item_ability_adj_reasons: '',
      item_runes_integrated: '',
      item_runes_unintegrated: '',
      item_spells_sorcery: '',
      item_spells_divine: '',
      //
      delete: false,
      // flag to indicate a tally_qty -> 0 change has been
      // explicitly confirmed via ClearDialog
      tallyZeroConfirmed: false,
      // baseline armour stats for dex-pen recalculation
      armour_base_ap: 0,
      armour_base_dex_pen: 0,
      // which data tab is active
      activeTab: '',
      contentsImg,
      // for flexible armour: remember that the user toggled
      // equipped=true but we are deferring the emit until a
      // specific armour_location is selected.
      pendingFlexibleEquip: false,
      isHydratingExistingArtefact: false,
      hasInitialisedExistingItem: false,
      showWeaponWornLocationField: false,
      max_hp: 0,
      max_ap: 0,
    }
  },
  //==========================================================
  // watch
  //-------
  watch: {
    //========================================================
    item: {
      handler() {
        this.getLinkedAdjustments()
        this.getLinkedRunes()
        this.getLinkedSpells()
      },
      immediate: true,
    },
    //========================================================
    character: {
      handler() {
        console.log('=====================================')
        console.log('WATCH CHARACTER IN ITEM CARD....')
        //-----------------------------------------------------------------------------------------------------------------------------------------
        // get drop down values (items, containers, worn_locations (item/armour), attach_locations, currencies, artefact_types, traits, qualities)
        //-----------------------------------------------------------------------------------------------------------------------------------------
        this.getDropDownValues()
        //------------------------------
        // get linked skill adjustments
        //------------------------------
        this.getLinkedAdjustments()
        //------------------
        // get linked runes
        //------------------
        this.getLinkedRunes()
        //-------------------
        // get linked spells
        //-------------------
        this.getLinkedSpells()
      },
      immediate: true,
    },
    'character.id': {
      handler(newId, oldId) {
        if (!newId || newId === oldId) {
          return
        }

        if (this.item_type !== 'Artefact') {
          return
        }

        const wantsCampaignArtefacts = this.isCampaignSelectionEnabled()
        const wantsDroppedArtefacts =
          this.isDroppedNonCampaignSelectionEnabled()

        if (!wantsCampaignArtefacts && !wantsDroppedArtefacts) {
          return
        }

        this.getDropDownValues()
      },
    },
    //========================================================
    'character.runes': {
      handler() {
        this.getLinkedRunes()
        this.getLinkedAdjustments()
      },
      deep: true,
    },
    //========================================================
    'character.spells': {
      handler() {
        this.getLinkedSpells()
      },
      deep: true,
    },
    //========================================================
    artefactNameParts: {
      immediate: true,
      handler(newParts, oldParts) {
        // only applies to Artefact items
        if (this.item_type !== 'Artefact') return

        // Existing artefacts hydrate descriptor fields in several steps
        // during mount; ignore those transient changes so opening the
        // panel does not rewrite the saved name or mark the item dirty.
        if (!this.isNewItem && this.isHydratingExistingArtefact) return

        // Campaign artefacts selected from the static-item dropdown
        // should keep that selected item name rather than rebuilding
        // a derived name from quality/age/material/type parts.
        if (this.local_item?.campaign) return

        const banned = ['unknown', 'other', 'n/a']

        // For EXISTING artefacts, require a valid artefact_type
        // (selected and not banned) before auto-constructing a
        // name. For NEW artefacts, allow the name to be built
        // from whichever parts are currently set so that clearing
        // artefact_type (or any other part) immediately updates
        // the heading.
        const rawType = (this.local_item?.artefact_type || '').trim()
        const isExisting = !this.isNewItem
        if (
          isExisting &&
          (!rawType || banned.includes(rawType.toLowerCase()))
        ) {
          return
        }

        const parts = (newParts || [])
          .map((v) => (v || '').trim())
          .filter((v) => v && !banned.includes(v.toLowerCase()))

        const previousParts = (oldParts || [])
          .map((v) => (v || '').trim())
          .filter((v) => v && !banned.includes(v.toLowerCase()))

        if (!parts.length) {
          // For new artefacts, when all parts are cleared out,
          // reset the working name so the heading falls back
          // to the base type label (e.g. just "Artefact").
          if (this.isNewItem) {
            this.local_item.name = ''
          }
          return
        }

        const name = parts.join(' ')
        const uniqueName = this.getUniqueArtefactName(name)
        const previousComputedName = previousParts.join(' ')

        // NEW items: always auto-construct the name from parts
        if (this.isNewItem) {
          this.local_item.name = uniqueName
          return
        }

        // EXISTING artefacts: only update the name when parts
        // actually change (skip the initial immediate run where
        // oldParts is undefined).
        if (typeof oldParts === 'undefined') return

        // If the current name has been manually customised (for example
        // by adding a suffix), preserve it instead of forcing the newly
        // computed descriptor-based name back onto the item. This also
        // covers reopen/hydration passes where the previous computed name
        // may still be blank.
        const currentName = (this.local_item?.name || '').trim()
        if (
          currentName &&
          currentName !== name &&
          (!previousComputedName || currentName !== previousComputedName)
        ) {
          return
        }

        this.local_item.name = uniqueName

        // ensure existing artefact name changes propagate back to
        // the sheet and change-tracking so the table label updates
        // when quality/material/age/type are edited.
        if (!this.isNewItem) {
          this.emitUpdate('name', uniqueName)
        }
      },
    },
    //========================================================
    'activeItem.hp': {
      handler(newVal, oldVal) {
        console.log('------------------------------------------------------')
        console.log(
          'WATCH 1. activeItem.hp: newVal = ',
          newVal,
          ' oldVal = ',
          oldVal,
        )
        if (!this.activeItem) return

        // Guard: ignore the initialisation from setItemValues where a new
        // item's hp is set from undefined/null to 0 on first open.
        const oldIsUnset = oldVal === undefined || oldVal === null
        const newIsZero = newVal === 0 || newVal === '0'
        if (this.isNewItem && oldIsUnset && newIsZero) {
          console.log('WATCH 1a. initial hp=0 for new item; ignoring')
          return
        }

        const numericNew = Number(newVal)
        const numericOld = Number(oldVal)

        if (!Number.isNaN(numericNew) && !Number.isNaN(numericOld)) {
          if (numericNew === 0 && numericOld > 0) {
            console.log(
              'WATCH 2. activeItem.hp: transitioned to zero, marking broken',
            )
            this.activeItem.broken = true
            if (!this.isNewItem) this.emitUpdate('broken', true)
          } else if (numericNew > 0 && this.activeItem.broken) {
            console.log('WATCH 3. activeItem.hp: above zero, clearing broken')
            this.activeItem.broken = false
            // no need to send update as calc'ed in sheetitems and in backend
            // if (!this.isNewItem) this.emitUpdate('broken', false)
          }
        }
      },
    },
    //========================================================
    'activeItem.tally_qty': {
      handler(newVal) {
        console.log('------------------------------------------------------')
        console.log('WATCH 1. activeItem.tally_qty: newVal = ', newVal)
        if (!this.activeItem) return
        //----------------------
        // where tally_qty zero
        //----------------------
        if (newVal == 0 && this.tallyZeroConfirmed) {
          console.log(
            'WATCH 2. activeItem.tally_qty (confirmed): newVal = ',
            newVal,
          )
          const worn_location = this.activeItem.worn_location || null
          const container = this.activeItem.container || null
          const attach_location = this.activeItem.attach_location || null
          //---------------------
          // clear worn_location
          //---------------------
          if (!isEmpty(worn_location)) {
            this.activeItem.worn_location = null
            console.log(
              'WATCH 3. worn_location cleared from ',
              this.activeItem.worn_location,
            )
            // emit the new value (null), not the previous id
            if (!this.isNewItem) this.emitUpdate('worn_location', null)
          }
          //-----------------
          // clear container
          //-----------------
          if (!isEmpty(container)) {
            this.activeItem.container = null
            console.log(
              'WATCH 4. container cleared from ',
              this.activeItem.container,
            )
            if (!this.isNewItem) this.emitUpdate('container', null)
          }
          //-----------------------
          // clear attach_location
          //-----------------------
          if (!isEmpty(attach_location)) {
            this.activeItem.attach_location = null
            console.log(
              'WATCH 5. attach_location cleared from ',
              this.activeItem.attach_location,
            )
          }
          // reset flag so subsequent edits to zero
          // require a fresh explicit confirmation
          this.tallyZeroConfirmed = false
        }
      },
    },
    showCountTab: {
      immediate: true,
      handler(newVal) {
        if (!newVal && this.activeTab === 'count') {
          this.activeTab = this.item_type === 'Artefact' ? 'artefact' : 'basic'
        }
      },
    },
    hasArtefactTraits: {
      immediate: true,
      handler(newVal) {
        if (
          !newVal &&
          this.isEffectiveCampaignArtefact &&
          this.activeTab === 'traits'
        ) {
          this.activeTab = 'artefact'
        }
      },
    },
    //========================================================
    'activeItem.container': {
      handler(newVal) {
        console.log('------------------------------------------------------')
        console.log('WATCH 1. container: newVal = ', newVal)
        if (!this.activeItem) return

        // Any change of container invalidates the previous tally-vs-container
        // capacity check, so clear that error and let future tally/container
        // actions recalculate against the current location.
        this.clearInputError('tally_qty')
        //----------
        // declares
        //----------
        let worn_location = this.activeItem.worn_location || null
        let attach_location = this.activeItem.attach_location || null
        let armour_location = this.activeItem.armour_location || null
        console.log('WATCH 2. container: worn_location = ', worn_location)
        console.log('WATCH 3. container: container = ', newVal)
        //--------------------------
        // where container selected
        //--------------------------
        if (!isEmpty(newVal)) {
          this.activeItem.stored = true
          console.log('WATCH 5. container: stored = ', this.activeItem.stored)
          //----------------------------------------------------------------
          // container v worn_location v attach_location mutually exclusive
          //----------------------------------------------------------------
          if (!isEmpty(worn_location)) {
            this.activeItem.worn_location = null
            console.log(
              'WATCH 6. container: worn_location cleared from ',
              this.activeItem.worn_location,
            )
            if (!this.isNewItem) this.emitUpdate('worn_location', null)
          }
          if (!isEmpty(attach_location)) {
            this.activeItem.attach_location = null
            console.log(
              'WATCH 7. container: attach_location cleared from ',
              this.activeItem.attach_location,
            )
            if (!this.isNewItem) this.emitUpdate('attach_location', null)
          }
          if (!isEmpty(armour_location)) {
            this.activeItem.armour_location = null
            if (!this.isNewItem) this.emitUpdate('armour_location', null)
          }
          // container selected => item is not worn
          if (this.activeItem.worn) {
            this.activeItem.worn = false
            if (!this.isNewItem) this.emitUpdate('worn', false)
          }
          // selecting a container means the item is not "held"
          if (this.activeItem.held) {
            this.activeItem.held = false
            if (!this.isNewItem) this.emitUpdate('held', false)
          }
        } else {
          //----------------------------------------------------------------
          // container v worn_location v attach_location mutually exclusive
          //----------------------------------------------------------------
          this.activeItem.stored = false
          // if (!this.isNewItem) this.emitUpdate('stored', this.activeItem.stored)
          console.log('WATCH 7. container: stored = ', this.activeItem.stored)
        }
      },
    },
    //==========================================================
    'activeItem.worn_location': {
      handler(newVal) {
        console.log('------------------------------------------------------')
        console.log('WATCH 1. worn_location newVal = ', newVal)
        if (!this.activeItem) return
        //----------
        // declares
        //----------
        let container = this.activeItem.container || null
        let attach_location = this.activeItem.attach_location || null
        console.log('WATCH 2. worn_location: container = ', container)
        console.log('WATCH 3. worn_location: worn_location = ', newVal)
        //------------------------------
        // where worn_location selected
        //------------------------------
        if (!isEmpty(newVal)) {
          this.activeItem.worn = true
          console.log('WATCH 5. worn_location: worn = ', this.activeItem.worn)
          //----------------------------------------------
          // container v worn_location v attach_location mutually exclusive
          //----------------------------------------------
          if (!isEmpty(container)) {
            this.activeItem.container = null
            console.log(
              'WATCH 6. worn_location: container = ',
              this.activeItem.container,
            )
            if (!this.isNewItem) this.emitUpdate('container', null)
          }
          if (!isEmpty(attach_location)) {
            this.activeItem.attach_location = null
            console.log(
              'WATCH 7. worn_location: attach_location = ',
              this.activeItem.attach_location,
            )
            if (!this.isNewItem) this.emitUpdate('attach_location', null)
          }
          // selecting a worn location means the item is not "held"
          if (this.activeItem.held) {
            this.activeItem.held = false
            if (!this.isNewItem) this.emitUpdate('held', false)
          }
        } else if (
          this.item_type !== 'Melee Weapon' &&
          this.item_type !== 'Ranged Weapon'
        ) {
          this.activeItem.worn = false
          console.log('WATCH 6. worn_location: worn = ', this.activeItem.worn)
        }

        // keep worn-location error in sync for new items
        this.updateWornLocationErrorForNew()
      },
    },
    //==========================================================
    // armour_location (armour items): mutually exclusive with
    // container, attach_location and held, and sets worn flag.
    //--------------------------------------------------------
    'activeItem.armour_location': {
      handler(newVal) {
        if (!this.activeItem) return
        let container = this.activeItem.container || null
        let attach_location = this.activeItem.attach_location || null

        if (!isEmpty(newVal)) {
          this.activeItem.worn = true

          // container v armour_location v attach_location mutually exclusive
          if (!isEmpty(container)) {
            this.activeItem.container = null
            if (!this.isNewItem) this.emitUpdate('container', null)
          }
          if (!isEmpty(attach_location)) {
            this.activeItem.attach_location = null
            if (!this.isNewItem) this.emitUpdate('attach_location', null)
          }

          // selecting an armour location means the item is not "held"
          if (this.activeItem.held) {
            this.activeItem.held = false
            if (!this.isNewItem) this.emitUpdate('held', false)
          }
        } else {
          this.activeItem.worn = false
        }
      },
    },
    //==========================================================
    'activeItem.attach_location': {
      handler(newVal) {
        if (!this.activeItem) return
        //----------
        // declares
        //----------
        let container = this.activeItem.container || null
        let worn_location = this.activeItem.worn_location || null
        let armour_location = this.activeItem.armour_location || null
        //------------------------------
        // where attach_location selected
        //------------------------------
        if (!isEmpty(newVal)) {
          //----------------------------------------------
          // container v worn_location v attach_location mutually exclusive
          //----------------------------------------------
          if (!isEmpty(container)) {
            this.activeItem.container = null
            if (!this.isNewItem) this.emitUpdate('container', null)
          }
          if (!isEmpty(worn_location)) {
            this.activeItem.worn_location = null
            if (!this.isNewItem) this.emitUpdate('worn_location', null)
          }
          if (!isEmpty(armour_location)) {
            this.activeItem.armour_location = null
            if (!this.isNewItem) this.emitUpdate('armour_location', null)
          }
          // selecting an attached location means the item is not "held"
          if (this.activeItem.held) {
            this.activeItem.held = false
            if (!this.isNewItem) this.emitUpdate('held', false)
          }
        }
      },
    },
    //========================================================
    // when worn is toggled (weapons): keep mutually exclusive
    //--------------------------------------------------------
    'activeItem.worn': {
      handler(newVal) {
        if (!this.activeItem) return

        // Only enforce these rules for weapons
        if (
          this.item_type !== 'Melee Weapon' &&
          this.item_type !== 'Ranged Weapon'
        ) {
          // even for non-weapon wearables, keep worn-location error up to date
          this.updateWornLocationErrorForNew()
          return
        }

        if (newVal) {
          // weapon worn => cannot be held
          if (this.activeItem.held) {
            this.activeItem.held = false
            if (!this.isNewItem) this.emitUpdate('held', false)
          }

          // weapon worn => cannot be in a container
          const container = this.activeItem.container || null
          if (!isEmpty(container)) {
            this.activeItem.container = null
            this.activeItem.stored = false
            if (!this.isNewItem) this.emitUpdate('container', null)
          }
        } else {
          // weapon no longer worn => clear any worn_location
          const worn_location = this.activeItem.worn_location || null
          if (!isEmpty(worn_location)) {
            this.activeItem.worn_location = null
            if (!this.isNewItem) this.emitUpdate('worn_location', null)
          }
        }

        // keep worn-location error in sync for new items
        this.updateWornLocationErrorForNew()
      },
    },
    //========================================================
    // when held is toggled on, clear all other locations
    //----------------------------------------------------
    'activeItem.held': {
      handler(newVal) {
        if (!this.activeItem) return

        // only need to act when switching to held=true
        if (newVal) {
          const container = this.activeItem.container || null
          const worn_location = this.activeItem.worn_location || null
          const attach_location = this.activeItem.attach_location || null
          const armour_location = this.activeItem.armour_location || null

          if (!isEmpty(container)) {
            this.activeItem.container = null
            if (!this.isNewItem) this.emitUpdate('container', null)
          }
          if (!isEmpty(worn_location)) {
            this.activeItem.worn_location = null
            if (!this.isNewItem) this.emitUpdate('worn_location', null)
          }
          if (!isEmpty(attach_location)) {
            this.activeItem.attach_location = null
            if (!this.isNewItem) this.emitUpdate('attach_location', null)
          }
          if (!isEmpty(armour_location)) {
            this.activeItem.armour_location = null
            if (!this.isNewItem) this.emitUpdate('armour_location', null)
          }

          // held=true implies not worn
          if (this.activeItem.worn) {
            this.activeItem.worn = false
            if (!this.isNewItem) this.emitUpdate('worn', false)
          }
        }
      },
    },
    //========================================================
    // when enc changes, clear any enc-specific error
    //------------------------------------------------
    'activeItem.enc': {
      handler(newVal) {
        this.clearInputError('enc')

        // for existing items, emit realtime ENC updates so
        // backend stays in sync with edits on the artefact tab
        if (!this.isNewItem && this.item_type !== 'Artefact') {
          const num = Number(newVal)
          const value = Number.isNaN(num) ? null : num
          this.emitUpdate('enc', value)
        }
      },
    },
  },
  //==========================================
  // computed
  //-----------
  computed: {
    showClearImageLink() {
      if (this.hasLockedCampaignArtefactImage) {
        return false
      }

      const itemImage = this.activeItem?.image ?? this.local_item?.image ?? null
      return !!itemImage
      // return false
    },
    activeItem() {
      const hasLocal =
        this.local_item && Object.keys(this.local_item).length > 0
      return hasLocal ? this.local_item : (this.item ?? {})
    },
    isNewItem() {
      const i = this.activeItem ?? null
      const id = i?.id ?? null
      return !this.activeItem || id === 0 || id == null
    },
    rowNumber() {
      return (this.activeItem?.originalIndex ?? 0) + 1
    },
    itemName() {
      // Prefer the working name from local_item so that headings
      // immediately reflect unsaved edits (including auto-built
      // artefact names from quality/age/material/type parts).
      const localName = this.local_item?.name || ''
      if (localName) return localName

      const i = this.activeItem || {}
      return i.display_name || i.name || ''
    },
    droppedArtefactOptions() {
      const getAvailableDroppedArtefacts =
        this.$store.getters['characters/getAvailableDroppedArtefacts']
      const artefacts = Array.isArray(getAvailableDroppedArtefacts)
        ? getAvailableDroppedArtefacts
        : typeof getAvailableDroppedArtefacts === 'function'
          ? getAvailableDroppedArtefacts()
          : []
      const characterItems = Array.isArray(this.character?.items)
        ? this.character.items
        : []
      const currentCharacterItemId = this.activeItem?.id ?? null
      const pendingDroppedArtefactIds = new Set()
      const pendingDroppedArtefactNames = new Set()

      characterItems.forEach((item) => {
        if (!item) {
          return
        }

        if (currentCharacterItemId && item.id === currentCharacterItemId) {
          return
        }

        const pendingId = item?.id
        const isPendingCharacterItem =
          typeof pendingId === 'string' && pendingId.startsWith('TMP-')

        if (!isPendingCharacterItem) {
          return
        }

        const linkedArtefactId = item?.item ?? item?.static_id ?? null
        if (
          linkedArtefactId !== null &&
          linkedArtefactId !== undefined &&
          linkedArtefactId !== ''
        ) {
          pendingDroppedArtefactIds.add(String(linkedArtefactId))
        }

        const linkedArtefactName = String(
          item?.name ?? item?.display_name ?? '',
        )
          .trim()
          .toLowerCase()
        if (linkedArtefactName) {
          pendingDroppedArtefactNames.add(linkedArtefactName)
        }
      })

      const seenNames = new Set()

      return (Array.isArray(artefacts) ? artefacts : [])
        .filter((item) => {
          if (item?.character_name || item?.player_name) {
            return false
          }

          const status = String(item?.status ?? '')
            .trim()
            .toLowerCase()
          if (status && status !== 'dropped') {
            return false
          }

          const artefactId = item?.id ?? null
          if (
            artefactId !== null &&
            artefactId !== undefined &&
            artefactId !== '' &&
            pendingDroppedArtefactIds.has(String(artefactId))
          ) {
            return false
          }

          const name = (item?.name || '').trim()
          if (!name) {
            return false
          }

          if (pendingDroppedArtefactNames.has(name.toLowerCase())) {
            return false
          }

          const key = name.toLowerCase()
          if (seenNames.has(key)) {
            return false
          }
          seenNames.add(key)
          return true
        })
        .slice()
        .sort((a, b) => {
          const nameA = (a?.name || '').toLowerCase()
          const nameB = (b?.name || '').toLowerCase()
          if (nameA < nameB) return -1
          if (nameA > nameB) return 1
          return 0
        })
        .map((item) => ({
          label: item.name,
          value: item.name,
        }))
    },
    hasItemName() {
      const name = this.itemName || ''
      return name !== ''
    },
    resolvedItemType() {
      return (
        this.activeItem?.effective_type ||
        this.effectiveStaticItemType ||
        this.activeItem?.type ||
        this.item_type
      )
    },
    isArtefactItem() {
      const candidateTypes = [
        this.local_item?.actual_type,
        this.activeItem?.actual_type,
        this.selectedStaticItem?.type,
        this.activeItem?.type,
        this.resolvedItemType,
        this.item_type,
      ]

      return candidateTypes.some((type) => type === 'Artefact')
    },
    showCountTab() {
      if (this.isArtefactItem) {
        return true
      }

      const isTallyable = this.toBooleanFlag(
        this.local_item?.tallyable ??
          this.activeItem?.tallyable ??
          this.propertySourceStaticItem?.tallyable ??
          this.selectedStaticItem?.tallyable,
      )
      const isCountable = this.toBooleanFlag(
        this.local_item?.countable ??
          this.activeItem?.countable ??
          this.propertySourceStaticItem?.countable ??
          this.selectedStaticItem?.countable,
      )

      return isTallyable || isCountable
    },
    artefactStatusOptions() {
      const lookups = this.$store.getters['characters/getItemLookups'] || {}
      const statuses = Array.isArray(lookups.status) ? lookups.status : []

      return statuses
        .filter((status) => status && status !== 'n/a' && status !== 'Active')
        .map((status) => ({ value: status, label: status }))
    },
    artefactTraitRows() {
      const traitSources = [
        this.activeItem?.item_traits,
        this.selectedStaticItem?.item_traits,
      ]
      const rawTraits = traitSources.find(
        (traits) => Array.isArray(traits) && traits.length > 0,
      )

      if (!Array.isArray(rawTraits)) {
        return []
      }

      const seen = new Set()
      return rawTraits
        .filter((trait) => trait && (trait.name || trait.description))
        .filter((trait) => {
          const key = `${trait.id ?? ''}:${trait.name ?? ''}:${trait.description ?? ''}`
          if (seen.has(key)) {
            return false
          }
          seen.add(key)
          return true
        })
        .map((trait) => ({
          id: trait.id ?? null,
          name: trait.name ?? '',
          description: trait.description ?? '',
        }))
    },
    hasArtefactTraits() {
      return this.artefactTraitRows.length > 0
    },
    canSelectArtefactTrait() {
      return this.item_type === 'Artefact' && !this.isEffectiveCampaignArtefact
    },
    showArtefactTraitsTab() {
      return (
        this.item_type === 'Artefact' &&
        (this.canSelectArtefactTrait || this.hasArtefactTraits)
      )
    },
    selectedArtefactTraitId() {
      // 1. If the user cleared the trait, respect that
      if (this.local_item?.trait === null) {
        return null
      }

      // 2. If the item has a trait, use it
      if (this.local_item?.trait) {
        return typeof this.local_item.trait === 'object'
          ? this.local_item.trait.id
          : this.local_item.trait
      }

      // 3. If activeItem has a trait, use it
      if (this.activeItem?.trait) {
        return typeof this.activeItem.trait === 'object'
          ? this.activeItem.trait.id
          : this.activeItem.trait
      }

      // 4. Only use static trait if no trait has ever been set
      if (this.selectedStaticItem?.trait) {
        return this.selectedStaticItem.trait.id ?? null
      }

      // 5. No trait anywhere
      return null
    },
    // selectedArtefactTraitId() {
    //   const candidateTrait =
    //     this.local_item?.trait ??
    //     this.activeItem?.trait ??
    //     this.selectedStaticItem?.trait ??
    //     null

    //   if (candidateTrait && typeof candidateTrait === 'object') {
    //     return candidateTrait.id ?? null
    //   }

    //   if (candidateTrait) {
    //     return candidateTrait
    //   }

    //   if (this.canSelectArtefactTrait && this.artefactTraitRows.length === 1) {
    //     return this.artefactTraitRows[0]?.id ?? null
    //   }

    //   return null
    // },
    image() {
      const resolvedImage =
        this.local_item?.image ??
        this.activeItem?.image ??
        this.selectedStaticItem?.image ??
        null

      return resolvedImage != null
        ? this.buildAbsoluteUrl(resolvedImage)
        : this.defaultImage
    },
    hasLockedCampaignArtefactImage() {
      if (!this.isEffectiveCampaignArtefact) {
        return false
      }

      const persistedImage =
        this.selectedStaticItem?.image ?? this.item?.image ?? null
      return !!persistedImage
    },
    showChangeImageLink() {
      return !this.hasLockedCampaignArtefactImage
      // return false
    },
    // display-only race name for read-only artefact view
    readonlyRaceName() {
      const raw = this.activeItem?.race

      if (this.activeItem?.race_name) {
        return this.activeItem.race_name
      }

      // If backend already sent a name string, use it directly
      if (typeof raw === 'string' && raw && !raw.includes('-')) {
        return raw
      }

      // If we have an object with a name, use that
      if (raw && typeof raw === 'object' && raw.name) {
        return raw.name
      }

      // Otherwise treat raw as an id and resolve via store
      const id = typeof raw === 'string' || typeof raw === 'number' ? raw : null
      const getRaceById = this.$store.getters['races/getRaceById']
      const raceObj =
        typeof getRaceById === 'function' && id ? getRaceById(id) : null

      return raceObj?.name || 'Unknown'
    },
    artefactNameParts() {
      // order: quality, age, material, artefact type
      return [
        this.local_item.quality,
        this.local_item.age,
        this.local_item.material,
        this.local_item.artefact_type,
      ]
    },
    gotValue() {
      if (!this.activeItem) return false

      const rawLower = this.activeItem.value_lower
      const rawUpper = this.activeItem.value_upper

      const lowerNum =
        rawLower === '' || rawLower === null || rawLower === undefined
          ? null
          : Number(rawLower)
      const upperNum =
        rawUpper === '' || rawUpper === null || rawUpper === undefined
          ? null
          : Number(rawUpper)

      const hasLower =
        typeof lowerNum === 'number' &&
        !Number.isNaN(lowerNum) &&
        lowerNum !== 0
      const hasUpper =
        typeof upperNum === 'number' &&
        !Number.isNaN(upperNum) &&
        upperNum !== 0

      return hasLower || hasUpper
    },
    computeItemState() {
      console.log('computeItemState: activeItem = ', this.activeItem)
      const itemType = this.resolvedItemType

      if (
        !this.activeItem ||
        (itemType !== 'Melee Weapon' &&
          itemType !== 'Ranged Weapon' &&
          itemType !== 'Armour')
      ) {
        return
      }
      if (this.activeItem.broken) {
        return 'Broken'
      }
      return 'Functional'
    },
    armourDexPenDisplay() {
      const item = this.activeItem || {}
      const itemId = item.id ?? null
      const rawItemDexPen = this.local_item?.dex_pen ?? item.dex_pen ?? 0
      const itemDexPen = Number(rawItemDexPen)

      if (
        !item.equipped ||
        itemId == null ||
        !Array.isArray(this.character?.armour_locations)
      ) {
        return Number.isNaN(itemDexPen) ? 0 : itemDexPen
      }

      let matchedTotal = 0
      let foundMatch = false

      this.character.armour_locations.forEach((loc) => {
        if (!loc || !Array.isArray(loc.equipped_armour)) return

        loc.equipped_armour.forEach((entry) => {
          if (!entry || entry.armour !== itemId) return

          const entryDexPen = Number(entry.dex_pen)
          if (!Number.isNaN(entryDexPen)) {
            matchedTotal += entryDexPen
            foundMatch = true
          }
        })
      })

      if (foundMatch) {
        return matchedTotal
      }

      return Number.isNaN(itemDexPen) ? 0 : itemDexPen
    },
    isWeaponOrArmour() {
      const itemType = this.resolvedItemType

      return (
        this.activeItem &&
        (itemType === 'Melee Weapon' ||
          itemType === 'Ranged Weapon' ||
          itemType === 'Armour')
      )
    },
    calcQtyTotEnc() {
      const encPerItem = parseFloat(this.activeItem.enc || 0)
      const qty = parseInt(this.activeItem.tally_qty || 0, 10)

      if (Number.isNaN(encPerItem) || Number.isNaN(qty)) {
        console.log(
          'calcQtyTotEnc: invalid inputs enc=',
          this.activeItem.enc,
          ' qty=',
          this.activeItem.tally_qty,
        )
        return 0
      }

      // multiply and round to 2 decimal places to avoid
      // floating point tails like 0.12999999999999998
      const rawTotal = encPerItem * qty
      const roundedTotal = Math.round((rawTotal + Number.EPSILON) * 100) / 100

      console.log('calcQtyTotEnc = ', this.formatEnc(roundedTotal))
      return roundedTotal
    },
    needsClearing() {
      if (this.isNewItem || !this.activeItem) {
        return false
      }
      console.log(
        'this.activeItem.attach_location = ',
        this.activeItem.attach_location,
      )
      //-----------------------------
      // check for linked item spells
      //-----------------------------
      const itemId = this.activeItem?.id ?? null
      const hasItemSpells =
        itemId !== null &&
        Array.isArray(this.character?.spells) &&
        this.character.spells.some((s) => {
          if (!s) {
            return false
          }

          const linkedItemId =
            s.character_item_id ??
            (typeof s.character_item === 'object'
              ? (s.character_item?.id ?? null)
              : (s.character_item ?? null))

          return (
            linkedItemId !== null && String(linkedItemId) === String(itemId)
          )
        })
      // Any linked values here must represent secondary records that are
      // actually removed when the item is deleted. Simple location/storage
      // relations like worn_location, attach_location, and container are
      // only unlinked and should not trigger the linked-values dialog.
      if (
        this.item_runes_integrated ||
        this.item_runes_unintegrated ||
        this.item_characteristic_adj_reasons ||
        this.item_ability_adj_reasons ||
        this.item_skill_adj_reasons_applied ||
        this.item_skill_adj_reasons_optional ||
        hasItemSpells
      ) {
        return true
      }
      return false
    },
    showOkButton() {
      return (
        !this.isNewItem && this.hasInitialisedExistingItem && this.isChanged
      )
    },
    displayAvailableEnc() {
      return `Capacity (${this.formatEnc(this.activeItem.container_capacity)}) - Contents Enc (${this.formatEnc(this.activeItem.contents_enc)})`
    },
    displayContainerEnc() {
      return `Enc of this container`
    },
    displayCapacityEnc() {
      return `How much enc this container can hold`
    },
    displayContentsEnc() {
      return `Total enc of all contents within this container (incl. nested items/ quantities)`
    },
    displayDirectCnt() {
      return `Count of items in this container (NOT incl. nested items)`
    },
    displayTotalCnt() {
      return `Count of ALL items in this container (incl. nested items)`
    },
    // displayAvailableEnc() {
    //   return `Container Capacity (${this.formatEnc(this.container.container_capacity)}) - Contents Enc (${this.formatEnc(this.overallContentsEnc)})`;
    // },
    displayTotEnc() {
      return `Container Enc (${this.formatEnc(this.activeItem?.enc)}) + Contents Enc (${this.formatEnc(this.activeItem?.contents_enc)})`
    },
    itemCampaignRef() {
      const rawValue =
        this.activeItem?.campaign_ref ?? this.selectedStaticItem?.campaign_ref

      if (rawValue === null || rawValue === undefined || rawValue === '') {
        return 0
      }

      const value = Number(rawValue)
      return Number.isNaN(value) ? 0 : value
    },
    showCampaignRef() {
      const itemType =
        this.activeItem?.actual_type ||
        this.activeItem?.type ||
        this.selectedStaticItem?.type

      return itemType === 'Artefact' && this.itemCampaignRef > 0
    },
    campaignRefValue() {
      return this.showCampaignRef ? this.itemCampaignRef : ''
    },
    // generic numeric-display helpers (render 0/null as blank string)
    displayValueLower: {
      get() {
        return this.getDisplayNumericField('value_lower')
      },
      set(v) {
        this.setDisplayNumericField('value_lower', v)
      },
    },
    displayValueUpper: {
      get() {
        return this.getDisplayNumericField('value_upper')
      },
      set(v) {
        this.setDisplayNumericField('value_upper', v)
      },
    },
    itemType() {
      if (this.item_type === 'All') {
        return 'All Items'
      } else {
        return this.item_type
      }
    },
    itemHeading() {
      // If there is no effective item name (including the
      // auto-built artefact name), fall back to the base
      // type label with no trailing hyphen.
      if (isEmpty(this.itemName)) {
        return this.itemType
      }
      // When opening the card with item_type === 'All', once an
      // actual item is selected prefer its concrete type for the
      // heading (so we show e.g. "Melee Weapon - Broadsword"
      // instead of "All - Broadsword"). For all other cases,
      // fall back to the prop-based item_type.
      const derivedType =
        this.item_type === 'All'
          ? this.effectiveStaticItemType ||
            this.activeItem?.type ||
            this.item_type
          : this.item_type

      return (derivedType || this.item_type) + ' - ' + this.itemName
    },
    isReady() {
      return !isEmpty(this.activeItem) && !isEmpty(this.local_item)
    },
    selectedStaticItem() {
      const staticItemId =
        this.save_item_id ??
        this.local_item?.item ??
        this.activeItem?.static_id ??
        this.activeItem?.item ??
        this.item?.static_id ??
        this.item?.item ??
        null
      const getStaticItemById =
        this.$store.getters['characters/getStaticItemById']

      if (typeof getStaticItemById === 'function' && staticItemId) {
        const staticItem = getStaticItemById(staticItemId)
        if (staticItem) return staticItem
      }

      const fallbackName =
        this.local_item?.name ?? this.activeItem?.name ?? this.item?.name ?? ''
      const getStaticItemByName =
        this.$store.getters['characters/getStaticItemByName']

      return typeof getStaticItemByName === 'function' && fallbackName
        ? (getStaticItemByName(fallbackName) ?? null)
        : null
    },
    documentViewerUrl() {
      return (
        this.selectedStaticItem?.reference_document_viewer_url ??
        this.selectedStaticItem?.reference_document_url ??
        this.selectedStaticItem?.reference_document ??
        ''
      )
    },
    shortDescriptionReadonly() {
      if (!this.isEffectiveCampaignArtefact) {
        return false
      }

      const sourceValue =
        this.selectedStaticItem?.short_description ??
        (!this.isNewItem
          ? (this.item?.short_description ??
            this.activeItem?.short_description ??
            this.local_item?.short_description ??
            '')
          : '')

      return typeof sourceValue === 'string' && sourceValue.trim() !== ''
    },
    // shortDescriptionRequired() {
    //   const requiredTypes = ['Healing', 'Poison', 'Specialist']
    //   const candidateTypes = [
    //     this.activeItem?.type,
    //     this.item_type,
    //     this.selectedStaticItem?.type,
    //     this.resolvedItemType,
    //   ]

    //   return candidateTypes.some((type) => requiredTypes.includes(type))
    // },
    effectiveStaticItemType() {
      const getStaticItemById =
        this.$store.getters['characters/getStaticItemById']
      return getEffectiveItemType(this.selectedStaticItem, getStaticItemById)
    },
    propertySourceStaticItem() {
      const getStaticItemById =
        this.$store.getters['characters/getStaticItemById']
      return getItemPropertySource(this.selectedStaticItem, getStaticItemById)
    },
    // total ENC for this item, using global one-decimal formatter
    encDisplay() {
      const tot_enc = this.activeItem?.tot_enc
      return this.formatEnc(tot_enc)
    },
    // weapon usability details derived from shared helper
    weaponUsabilityDetails() {
      const type = this.resolvedItemType

      if (type !== 'Melee Weapon' && type !== 'Ranged Weapon') {
        return null
      }

      const staticSkills = Array.isArray(this.local_item?.skills)
        ? this.local_item.skills
        : []

      if (!staticSkills.length) return null

      const characterSkills = Array.isArray(this.character?.skills)
        ? this.character.skills
        : []

      // always use the character's current Str/Dex rather than
      // any snapshot stored on the item so that mins-met and
      // usability stay in sync when characteristics change
      const strCur = this.character?.str_cur ?? null
      const dexCur = this.character?.dex_cur ?? null

      return evaluateWeaponUsability({
        staticSkills,
        characterSkills,
        itemType: type,
        strCur,
        dexCur,
      })
    },
    // rows for the weapon skills table (name, got skill, mins met)
    weaponSkillRows() {
      const details = this.weaponUsabilityDetails
      if (!details || !Array.isArray(details.perSkill)) return []
      const staticSkills = Array.isArray(this.local_item?.skills)
        ? this.local_item.skills
        : []

      return details.perSkill.map((ps) => {
        const sourceSkill = staticSkills.find(
          (skill) => skill?.name === ps.name,
        )
        const skillId =
          sourceSkill?.skill ??
          sourceSkill?.skill_id ??
          sourceSkill?.id ??
          ps.name

        return {
          name: ps.name,
          skill_id: skillId,
          skill: ps.hasSkill ? 'Yes' : 'No',
          min_str: ps.min_str,
          min_dex: ps.min_dex,
          damage_dice: sourceSkill?.damage_dice ?? '',
          // in the UI, "Mins Met" should answer only
          // "do current Str/Dex meet the numeric mins?",
          // independent of whether the character has the skill
          met_req: ps.statsOk ? 'Yes' : 'No',
        }
      })
    },
    weaponUnusableReasons() {
      const details = this.weaponUsabilityDetails
      return details?.unusable_reasons || ''
    },
    isError() {
      return this.errors.length > 0
    },
    filteredArtefactTypes() {
      const excludedArtefactTypes = new Set([
        'armour',
        'melee weapon',
        'ranged weapon',
      ])
      const artefactTypes = Array.isArray(this.artefact_type_records)
        ? this.artefact_type_records
        : []

      return [...artefactTypes]
        .filter(
          (type) =>
            !excludedArtefactTypes.has(
              String(type?.name ?? '')
                .trim()
                .toLowerCase(),
            ),
        )
        .sort((left, right) => {
          const leftSeq = Number(left?.seq ?? 0)
          const rightSeq = Number(right?.seq ?? 0)

          if (leftSeq !== rightSeq) {
            return leftSeq - rightSeq
          }

          const leftName = String(left?.name ?? '').toLowerCase()
          const rightName = String(right?.name ?? '').toLowerCase()

          if (leftName < rightName) return -1
          if (leftName > rightName) return 1
          return 0
        })
        .map((type) => type?.name || '')
        .filter((type) => type !== '')
    },
    artefactBaseItemsForSelection() {
      if (this.item_type !== 'Artefact') {
        return []
      }

      const artefactSubtype = this.normaliseArtefactTypeName(
        this.local_item?.artefact_type ?? this.activeItem?.artefact_type,
      )

      if (!this.requiresItemCardArtefactBaseItem(artefactSubtype)) {
        return []
      }

      const getArtefactBaseItemsByType =
        this.$store.getters['characters/getArtefactBaseItemsByType']
      const baseItems =
        typeof getArtefactBaseItemsByType === 'function'
          ? getArtefactBaseItemsByType(artefactSubtype)
          : []

      if (!Array.isArray(baseItems) || baseItems.length === 0) {
        return []
      }

      return [...baseItems].sort((left, right) => {
        const leftLabel = this.getArtefactBaseItemLabel(left).toLowerCase()
        const rightLabel = this.getArtefactBaseItemLabel(right).toLowerCase()
        if (leftLabel < rightLabel) return -1
        if (leftLabel > rightLabel) return 1
        return 0
      })
    },
    isCampaignArtefact() {
      if (this.item_type !== 'Artefact') return false

      return this.isCampaignSelectionEnabled()
    },
    isEffectiveCampaignArtefact() {
      if (this.item_type !== 'Artefact') return false

      const selectedStaticCampaignRef = this.isNewItem
        ? this.selectedStaticItem?.campaign_ref
        : undefined

      const rawCampaignRef =
        this.local_item?.campaign_ref ??
        this.activeItem?.campaign_ref ??
        selectedStaticCampaignRef ??
        0
      const campaignRef = Number(rawCampaignRef)
      const hasCampaignRef = !Number.isNaN(campaignRef) && campaignRef > 0

      return this.isCampaignSelectionEnabled() || hasCampaignRef
    },
    isNonCampaignArtefact() {
      if (this.item_type !== 'Artefact') return false

      const selectedStaticCampaignRef = this.isNewItem
        ? this.selectedStaticItem?.campaign_ref
        : undefined

      const rawCampaignRef =
        this.local_item?.campaign_ref ??
        this.activeItem?.campaign_ref ??
        selectedStaticCampaignRef ??
        0
      const campaignRef = Number(rawCampaignRef)
      const hasCampaignRef = !Number.isNaN(campaignRef) && campaignRef > 0

      return !this.isCampaignSelectionEnabled() && !hasCampaignRef
    },
    // for new artefact items, require an artefact type selection
    // before enabling the Add button
    artefactTypeMissingForNew() {
      if (!this.isNewItem) return false
      if (this.item_type !== 'Artefact') return false

      const raw =
        (this.local_item && this.local_item.artefact_type) ||
        this.activeItem?.artefact_type ||
        ''
      const value = (raw || '').trim()

      // Treat only a blank value as "missing"; special
      // lookup entries like "Unknown" / "Other"
      // are still valid explicit choices.
      return !value
    },
    // for new non-armour wearable items, if marked as worn and
    // there are available worn_locations but none selected yet,
    // treat this as a blocking condition for enabling the Add
    // button, even before submit validation has run.
    wornLocationMissingForNew() {
      if (!this.isNewItem) return false
      if (this.resolvedItemType === 'Armour') return false

      const worn = this.activeItem?.worn ?? false
      if (!worn) return false

      const hasWornLocations =
        Array.isArray(this.worn_locations) && this.worn_locations.length > 0
      if (!hasWornLocations) return false

      const hasWornLocation =
        (this.local_item && this.local_item.worn_location) ||
        this.activeItem?.worn_location

      return !hasWornLocation
    },
    isChanged() {
      // new items...
      if (this.isNewItem) {
        return isObjectChanged(this.original_item, this.activeItem)
      }

      // existing items: compare only the editable fields, but do it
      // via JSON-based deep comparison so reactive proxies vs plain
      // arrays (runes/spells) don't cause false positives.

      console.log('isChanged original item = ', this.original_item)

      console.log('BUILDING ORIGINAL COMPARABLE')
      const originalComparable = this.buildComparableItemState(
        this.original_item,
      )

      console.log('BUILDING CURRENT COMPARABLE')
      const currentComparable = this.buildComparableItemState(
        this.local_item,
        this.activeItem,
      )

      const baseChanged = isObjectChanged(originalComparable, currentComparable)

      // For flexible armour, don't treat a bare equipped toggle
      // (without a selected armour_location) as a change. This
      // mirrors the deferred equip behaviour where we wait for a
      // specific location before committing the change.
      if (
        baseChanged &&
        this.resolvedItemType === 'Armour' &&
        Array.isArray(this.armour_locations) &&
        this.armour_locations.length > 0
      ) {
        const originalWithoutEquipped = { ...originalComparable }
        const currentWithoutEquipped = { ...currentComparable }

        delete originalWithoutEquipped.equipped
        delete currentWithoutEquipped.equipped

        const changedIgnoringEquipped = isObjectChanged(
          originalWithoutEquipped,
          currentWithoutEquipped,
        )

        if (!changedIgnoringEquipped) {
          const hasArmourLocation =
            (this.local_item && this.local_item.armour_location) ||
            this.activeItem?.armour_location

          if (!hasArmourLocation) {
            return false
          }
        }
      }

      return baseChanged
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    buildAbsoluteUrl(path) {
      if (!path) {
        return ''
      }

      if (/^(https?:|data:|blob:)/i.test(path)) {
        return path
      }

      const normalizedPath = path.startsWith('/') ? path : `/${path}`
      const baseUrl = import.meta.env.VITE_API_URL || window.location.origin

      try {
        return new URL(normalizedPath, baseUrl).toString()
      } catch {
        return normalizedPath
      }
    },
    clearImage() {
      this.local_item = this.local_item || {}
      this.local_item.image = null
      this.local_item.image_file = null
      this.local_item.image_cleared = true

      if (this.activeItem) {
        this.activeItem.image = null
        this.activeItem.image_file = null
        this.activeItem.image_cleared = true
      }

      this.emitUpdate('image', null)
      this.emitUpdate('image_file', null)
    },
    // expose imported helper for template usage
    isEmpty,
    resolveExistingArtefactPwrStorage(staticItem = null) {
      if (this.item_type !== 'Artefact') {
        return this.item?.pwr_storage ?? 0
      }

      return this.item?.pwr_storage ?? staticItem?.pwr_storage ?? 0
    },
    normaliseComparableOptionalText(value) {
      if (value === null || value === undefined) return null

      if (typeof value !== 'string') return value

      const trimmed = value.trim()
      if (!trimmed) return null

      return ['n/a', 'na', 'none'].includes(trimmed.toLowerCase())
        ? null
        : trimmed
    },
    buildComparableItemState(primarySource = {}, fallbackSource = {}) {
      //-------------------------------------------------------
      // use 1st item_traits entry (ID), else trait, else null
      //-------------------------------------------------------
      const rawItemTraits =
        primarySource?.item_traits ?? fallbackSource?.item_traits ?? null

      const traitFromArray =
        Array.isArray(rawItemTraits) && rawItemTraits.length > 0
          ? typeof rawItemTraits[0] === 'object'
            ? (rawItemTraits[0].id ?? null)
            : rawItemTraits[0]
          : null

      const rawTrait =
        traitFromArray ?? primarySource?.trait ?? fallbackSource?.trait ?? null

      const normalisedTrait =
        rawTrait && typeof rawTrait === 'object'
          ? (rawTrait.id ?? null)
          : rawTrait

      const normalisedRace = this.normaliseRaceId(
        primarySource?.race ?? fallbackSource?.race ?? null,
      )
      const normalisedCurrency = this.normaliseCurrencyId(
        primarySource?.currency ?? fallbackSource?.currency ?? null,
      )
      //---------------
      // return values
      //---------------
      return {
        name: primarySource?.name ?? fallbackSource?.name ?? '',
        tally_qty: primarySource?.tally_qty ?? fallbackSource?.tally_qty ?? 0,
        player_notes:
          primarySource?.player_notes ?? fallbackSource?.player_notes ?? '',
        short_description:
          primarySource?.short_description ??
          fallbackSource?.short_description ??
          '',
        description:
          primarySource?.description ?? fallbackSource?.description ?? '',
        worn_location:
          primarySource?.worn_location ?? fallbackSource?.worn_location ?? null,
        armour_location:
          primarySource?.armour_location ??
          fallbackSource?.armour_location ??
          null,
        attach_location:
          primarySource?.attach_location ??
          fallbackSource?.attach_location ??
          null,
        container:
          primarySource?.container ?? fallbackSource?.container ?? null,
        ap: primarySource?.ap ?? fallbackSource?.ap ?? 0,
        hp: primarySource?.hp ?? fallbackSource?.hp ?? 0,
        enc: primarySource?.enc ?? fallbackSource?.enc ?? 0,
        runes: primarySource?.runes ?? fallbackSource?.runes ?? [],
        spells: primarySource?.spells ?? fallbackSource?.spells ?? [],
        worn: primarySource?.worn ?? fallbackSource?.worn ?? false,
        held: primarySource?.held ?? fallbackSource?.held ?? false,
        equipped: primarySource?.equipped ?? fallbackSource?.equipped ?? false,
        max_unit_count:
          primarySource?.max_unit_count ??
          fallbackSource?.max_unit_count ??
          null,
        cur_unit_count:
          primarySource?.cur_unit_count ??
          fallbackSource?.cur_unit_count ??
          null,
        countable:
          primarySource?.countable ?? fallbackSource?.countable ?? false,
        unit_name:
          primarySource?.unit_name ?? fallbackSource?.unit_name ?? null,
        healing_type: this.normaliseComparableOptionalText(
          primarySource?.healing_type ?? fallbackSource?.healing_type ?? null,
        ),
        recovers: this.normaliseComparableOptionalText(
          primarySource?.recovers ?? fallbackSource?.recovers ?? null,
        ),
        poison_type: this.normaliseComparableOptionalText(
          primarySource?.poison_type ?? fallbackSource?.poison_type ?? null,
        ),
        pot: this.normaliseComparableOptionalText(
          primarySource?.pot ?? fallbackSource?.pot ?? null,
        ),
        effect: this.normaliseComparableOptionalText(
          primarySource?.effect ?? fallbackSource?.effect ?? null,
        ),
        delay: this.normaliseComparableOptionalText(
          primarySource?.delay ?? fallbackSource?.delay ?? null,
        ),
        damage_dice: this.normaliseComparableOptionalText(
          primarySource?.damage_dice ?? fallbackSource?.damage_dice ?? null,
        ),
        duration: this.normaliseComparableOptionalText(
          primarySource?.duration ?? fallbackSource?.duration ?? null,
        ),
        resist: this.normaliseComparableOptionalText(
          primarySource?.resist ?? fallbackSource?.resist ?? null,
        ),
        value_lower:
          primarySource?.value_lower ?? fallbackSource?.value_lower ?? null,
        value_upper:
          primarySource?.value_upper ?? fallbackSource?.value_upper ?? null,
        estimated_value:
          primarySource?.estimated_value ??
          fallbackSource?.estimated_value ??
          false,
        artefact_type:
          primarySource?.artefact_type ?? fallbackSource?.artefact_type ?? '',
        artefact_base_item:
          primarySource?.artefact_base_item ??
          fallbackSource?.artefact_base_item ??
          null,
        trait: normalisedTrait,
        material: primarySource?.material ?? fallbackSource?.material ?? '',
        quality: primarySource?.quality ?? fallbackSource?.quality ?? '',
        age: primarySource?.age ?? fallbackSource?.age ?? '',
        pwr_storage:
          primarySource?.pwr_storage ?? fallbackSource?.pwr_storage ?? 0,
        image: primarySource?.image ?? fallbackSource?.image ?? null,
        race: normalisedRace,
        currency: normalisedCurrency,
      }
    },
    syncOriginalItemComparableSnapshot({ force = false } = {}) {
      // run once after hydration
      if (this.isNewItem || (this.hasInitialisedExistingItem && !force)) return

      const currentItem = this.local_item ?? this.item ?? {}
      const staticItem = this.selectedStaticItem
      const initialArtefactPwrStorage =
        this.resolveExistingArtefactPwrStorage(staticItem)
      //-----------------------------------------------------------
      // get original item currency id (support change comparison)
      //-----------------------------------------------------------
      console.log('1. this.item.currency = ', this.item?.currency)
      console.log('2. staticItem?.currency = ', staticItem?.currency)

      const currency = this.normaliseCurrencyId(
        currentItem.currency ?? staticItem?.currency ?? null,
      )
      //-----------
      // set trait
      //-----------
      const trait = (() => {
        // 1. item has traits array with at least one entry
        if (
          Array.isArray(currentItem.item_traits) &&
          currentItem.item_traits.length > 0
        ) {
          return currentItem.item_traits[0]?.id ?? null
        }
        // 2. static has traits array with at least one entry
        else if (
          Array.isArray(staticItem?.item_traits) &&
          staticItem.item_traits.length > 0
        ) {
          return staticItem.item_traits[0]?.id ?? null
        }
        // 3. else no traits
        return null
      })()

      //----------------------------------------------------------
      // save editable fields snapshot for isChanged (and cancel)
      //----------------------------------------------------------
      this.original_item = this.$deepCopy({
        name: currentItem.name ?? '',
        tally_qty: currentItem.tally_qty ?? 0,

        player_notes: currentItem.player_notes ?? currentItem.notes ?? '',
        short_description:
          currentItem.short_description ?? staticItem?.short_description ?? '',
        description: currentItem.description ?? staticItem?.description ?? '',

        container: currentItem.container ?? null,
        worn_location: currentItem.worn_location ?? null,
        attach_location: currentItem.attach_location ?? null,
        armour_location: currentItem.armour_location ?? null,
        ap: currentItem.ap ?? 0,
        hp: currentItem.hp ?? 0,
        enc: currentItem.enc ?? staticItem?.enc ?? 0,
        worn: currentItem.worn ?? false,
        held: currentItem.held ?? false,
        equipped: currentItem.equipped ?? false,
        runes: currentItem.runes ?? [],
        spells: currentItem.spells ?? [],

        max_unit_count:
          currentItem.max_unit_count != null
            ? Number(currentItem.max_unit_count)
            : null,
        cur_unit_count:
          currentItem.cur_unit_count != null
            ? Number(currentItem.cur_unit_count)
            : null,

        value_lower: currentItem.value_lower ?? null,
        value_upper: currentItem.value_upper ?? null,
        estimated_value:
          currentItem.estimated_value ?? staticItem?.estimated_value ?? false,

        artefact_type: currentItem.artefact_type ?? '',
        artefact_base_item: currentItem.artefact_base_item ?? null,
        trait: trait,
        material: currentItem.material ?? '',
        quality: currentItem.quality ?? '',
        age: currentItem.age ?? '',
        pwr_storage: initialArtefactPwrStorage,
        image: currentItem.image ?? null,
        countable: currentItem.countable ?? staticItem?.countable ?? false,
        unit_name:
          currentItem.unit_name?.trim() ||
          staticItem?.unit_name?.trim() ||
          null,
        healing_type:
          currentItem.healing_type ?? staticItem?.healing_type ?? null,
        recovers: currentItem.recovers ?? staticItem?.recovers ?? null,
        poison_type: currentItem.poison_type ?? staticItem?.poison_type ?? null,
        pot: currentItem.pot ?? staticItem?.pot ?? null,
        effect: currentItem.effect ?? staticItem?.effect ?? null,
        delay: currentItem.delay ?? staticItem?.delay ?? null,
        damage_dice: currentItem.damage_dice ?? staticItem?.damage_dice ?? null,
        duration: currentItem.duration ?? staticItem?.duration ?? null,
        resist: currentItem.resist ?? staticItem?.resist ?? null,
        race: this.normaliseRaceId(currentItem.race ?? null),
        currency: currency,
      })

      console.log('6a. ORIGINAL = ', this.original_item)
      console.log('6b. FINAL ORIGINAL CURRENCY = ', this.original_item.currency)

      // freeze so cannot be mutated
      Object.freeze(this.original_item)
    },
    buildHealingShortDescription(recovers) {
      const value = typeof recovers === 'string' ? recovers.trim() : ''
      const maxLength = 100

      if (!value) {
        return ''
      }

      const description = `Recovers ${value}`
      if (description.length <= maxLength) {
        return description
      }

      return description.slice(0, maxLength).trimEnd()
    },
    buildPoisonShortDescription(effect, damageDice, duration) {
      const effectValue = typeof effect === 'string' ? effect.trim() : ''
      const damageValue =
        typeof damageDice === 'string' ? damageDice.trim() : ''
      const durationValue = typeof duration === 'string' ? duration.trim() : ''
      const maxLength = 100

      if (!effectValue) {
        return ''
      }

      const hasDamageValue =
        damageValue && !['n/a', 'na'].includes(damageValue.toLowerCase())
      const hasDurationValue =
        durationValue && !['n/a', 'na'].includes(durationValue.toLowerCase())
      const description = hasDamageValue
        ? `${effectValue} (${damageValue})`
        : hasDurationValue
          ? `${effectValue} (${durationValue})`
          : effectValue

      if (description.length <= maxLength) {
        return description
      }

      return description.slice(0, maxLength).trimEnd()
    },
    openDocument() {
      if (!this.documentViewerUrl) {
        return
      }

      window.open(this.documentViewerUrl, '_blank', 'noopener,noreferrer')
    },
    toBooleanFlag(value) {
      if (typeof value === 'string') {
        const normalised = value.trim().toLowerCase()
        if (normalised === 'true' || normalised === '1') return true
        if (normalised === 'false' || normalised === '0' || normalised === '')
          return false
      }

      if (typeof value === 'number') {
        return value === 1
      }

      return value === true
    },
    isCampaignSelectionEnabled() {
      const selectedStaticCampaign = this.isNewItem
        ? this.selectedStaticItem?.campaign
        : undefined

      return [
        this.local_item?.campaign,
        this.activeItem?.campaign,
        selectedStaticCampaign,
      ].some((value) => this.toBooleanFlag(value))
    },
    isDroppedNonCampaignSelectionEnabled() {
      return [
        this.local_item?._dropped_non_campaign_artefact,
        this.activeItem?._dropped_non_campaign_artefact,
      ].some((value) => this.toBooleanFlag(value))
    },
    getResolvedCharacterId() {
      const currentCharacter =
        this.$store?.getters?.['characters/getCurrentCharacter']
      const candidates = [
        this.character?.id,
        currentCharacter?.id,
        this.activeItem?.character,
        this.local_item?.character,
        this.item?.character,
      ]

      for (const candidate of candidates) {
        if (candidate === null || candidate === undefined) {
          continue
        }

        if (typeof candidate === 'number') {
          if (candidate > 0) {
            return candidate
          }
          continue
        }

        if (typeof candidate === 'string') {
          const trimmed = candidate.trim()
          if (!trimmed || trimmed === '0') {
            continue
          }
          return trimmed
        }

        return candidate
      }

      return null
    },
    getResolvedArenaId() {
      const currentCharacter =
        this.$store?.getters?.['characters/getCurrentCharacter']
      const candidates = [
        this.character?.arena,
        currentCharacter?.arena,
        this.activeItem?.arena,
        this.local_item?.arena,
        this.item?.arena,
      ]

      for (const candidate of candidates) {
        if (candidate === null || candidate === undefined) {
          continue
        }

        if (typeof candidate === 'number') {
          if (candidate > 0) {
            return candidate
          }
          continue
        }

        if (typeof candidate === 'string') {
          const trimmed = candidate.trim()
          if (!trimmed || trimmed === '0') {
            continue
          }
          return trimmed
        }

        return candidate
      }

      return null
    },
    findItemByName(options, name) {
      if (!name || !Array.isArray(options)) {
        return null
      }

      const rawName = String(name)
      const trimmedName = rawName.trim()
      if (!trimmedName) {
        return null
      }

      return (
        options.find((option) => option?.name === rawName) ??
        options.find((option) => option?.name === trimmedName) ??
        options.find(
          (option) =>
            typeof option?.name === 'string' &&
            option.name.trim().toLowerCase() === trimmedName.toLowerCase(),
        ) ??
        null
      )
    },
    findLookupStringValue(options, desiredValue) {
      if (typeof desiredValue !== 'string' || !desiredValue.trim()) {
        return desiredValue
      }

      const desiredNormalised = desiredValue.trim().toLowerCase()
      const values = Array.isArray(options) ? options : []
      const match = values.find(
        (option) =>
          typeof option === 'string' &&
          option.trim().toLowerCase() === desiredNormalised,
      )

      return match ?? desiredValue
    },
    getCharacterRaceId() {
      const raceName =
        typeof this.character?.race_name === 'string'
          ? this.character.race_name.trim()
          : ''

      if (!raceName) {
        return null
      }

      const getRaceByName = this.$store.getters['races/getRaceByName']
      const allRaces = this.$store.getters['races/getRaces'] || []
      const characterRaces =
        this.$store.getters['races/getCharacterRaces'] || []
      const availableRaces =
        characterRaces.length > 0 ? characterRaces : allRaces
      const race =
        (typeof getRaceByName === 'function'
          ? getRaceByName(raceName)
          : null) ||
        availableRaces.find((entry) => entry?.name === raceName) ||
        allRaces.find((entry) => entry?.name === raceName) ||
        null

      return race?.id ?? null
    },
    applyArtefactTypeDefaults(artefactType, previousArtefactType = null) {
      const normalisedArtefactType =
        typeof artefactType === 'string'
          ? artefactType.trim().toLowerCase()
          : ''
      const normalisedPreviousArtefactType =
        typeof previousArtefactType === 'string'
          ? previousArtefactType.trim().toLowerCase()
          : ''

      if (this.item_type !== 'Artefact') {
        return
      }

      const descriptorNaArtefactTypes = new Set(['runestone', 'tattoo'])
      const defaultsByArtefactType = {
        runestone: {
          material: this.findLookupStringValue(this.materials, 'Stone'),
          quality: this.findLookupStringValue(this.qualities, 'n/a'),
          age: this.findLookupStringValue(this.ages, 'n/a'),
          race: this.getCharacterRaceId(),
        },
        tattoo: {
          material: this.findLookupStringValue(this.materials, 'Skin'),
          quality: this.findLookupStringValue(this.qualities, 'n/a'),
          age: this.findLookupStringValue(this.ages, 'n/a'),
          race: this.getCharacterRaceId(),
        },
      }

      const defaults = defaultsByArtefactType[normalisedArtefactType]
      if (!defaults) {
        if (
          descriptorNaArtefactTypes.has(normalisedPreviousArtefactType) &&
          !descriptorNaArtefactTypes.has(normalisedArtefactType)
        ) {
          const unknownQuality = this.findLookupStringValue(
            this.qualities,
            'Unknown',
          )
          const unknownAge = this.findLookupStringValue(this.ages, 'Unknown')

          this.local_item.quality = unknownQuality
          this.local_item.age = unknownAge
          this.local_item._static_quality_unknown = true
          this.local_item._static_age_unknown = true
          this.local_item._session_quality_editable = true
          this.local_item._session_age_editable = true

          if (this.activeItem) {
            this.activeItem.quality = unknownQuality
            this.activeItem.age = unknownAge
            this.activeItem._static_quality_unknown = true
            this.activeItem._static_age_unknown = true
            this.activeItem._session_quality_editable = true
            this.activeItem._session_age_editable = true
          }

          if (!this.isNewItem) {
            this.emitUpdate('quality', unknownQuality)
            this.emitUpdate('age', unknownAge)
          }
        }

        return
      }

      Object.entries(defaults).forEach(([key, defaultValue]) => {
        this.local_item[key] = defaultValue

        if (this.activeItem) {
          this.activeItem[key] = defaultValue
        }

        if (!this.isNewItem) {
          this.emitUpdate(key, defaultValue)
        }
      })
    },
    //========================================================
    // is editable
    //-------------
    isEditable(prop) {
      // get item id
      const id = this.activeItem && this.activeItem.id

      // only allow editing of existing item (artefact) where value unknown
      if (prop === 'Unknown' || isEmpty(prop)) {
        // isempty for race
        return true
      }
      // where new item = editable
      if (this.isNewItem) return true

      // where tempid = editable
      if (typeof id === 'string' && id.includes('TMP')) return true

      return false
    },
    //========================================================
    // preview image
    //---------------
    previewImage(event) {
      console.log('---------------------')
      console.log('IN previewImage')
      const file = event.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = (e) => {
        // store preview on the local item so the computed
        // image getter (using local_item.image) will update
        this.local_item = this.local_item || {}
        this.local_item.image = e.target.result

        // keep the upload blob on the local item so that
        // add/submit payloads can carry image_file directly

        // create image file for uploading
        const formData = new FormData()
        formData.append('image', file)

        this.local_item.image_file = formData

        this.local_item.image_cleared = false
        if (this.activeItem) {
          this.activeItem.image_file = formData
          this.activeItem.image_cleared = false
        }

        // emit both the preview data URL and the formdata so
        // the parent/component handling saves can persist it
        this.emitUpdate('image', e.target.result)
        this.emitUpdate('image_file', formData)
        console.log('Image preview updated and file prepared for upload.')
      }
      reader.readAsDataURL(file)
    },
    //========================================================
    // artefact name input handler
    //--------------------------------
    onArtefactNameInput(event) {
      const rawValue = event && event.target ? (event.target.value ?? '') : ''
      const value = rawValue.replace(
        /(^|[\s\-_])([a-z])/g,
        (match, prefix, char) => {
          return `${prefix}${char.toUpperCase()}`
        },
      )
      const selectionStart = event?.target?.selectionStart
      const selectionEnd = event?.target?.selectionEnd

      if (this.activeItem) {
        this.activeItem.name = value
      }

      if (this.local_item) {
        this.local_item.name = value
      }

      if (event && event.target && event.target.value !== value) {
        event.target.value = value

        if (
          typeof selectionStart === 'number' &&
          typeof selectionEnd === 'number' &&
          typeof event.target.setSelectionRange === 'function'
        ) {
          event.target.setSelectionRange(selectionStart, selectionEnd)
        }
      }

      if (!this.isNewItem) {
        this.delayedEmitUpdate('name', value)
      }
    },
    //========================================================
    // artefact description input handler
    //------------------------------------
    onArtefactDescriptionInput(event) {
      const raw = event && event.target ? (event.target.value ?? '') : ''
      const value = normaliseLeadingLetterText(raw)

      if (event && event.target) {
        event.target.value = value
      }

      if (this.activeItem) {
        this.activeItem.description = value
      }

      if (this.local_item) {
        this.local_item.description = value
      }

      this.delayedEmitUpdate('description', value)
    },
    //========================================================
    // generic name input handler for child tabs
    //-------------------------------------------
    onNameInput(event) {
      this.onArtefactNameInput(event)
    },
    //========================================================
    // get unique artefact name for non-campaign artefacts
    //----------------------------------------------------
    getUniqueArtefactName(rawName) {
      const trimmedName = typeof rawName === 'string' ? rawName.trim() : ''
      const baseName = trimmedName ? capitaliseFirstLetter(trimmedName) : ''

      if (!baseName) {
        return ''
      }

      if (this.item_type !== 'Artefact') {
        return baseName
      }

      if (this.isEffectiveCampaignArtefact) {
        return baseName
      }

      const existingItems = Array.isArray(this.character?.items)
        ? this.character.items
        : []

      const currentItemId = this.activeItem?.id ?? null
      const existingNames = new Set()

      existingItems.forEach((item) => {
        if (!item) return
        if (currentItemId && item.id === currentItemId) return

        const existingName = (item.display_name || item.name || '')
          .toString()
          .trim()

        if (existingName) {
          existingNames.add(existingName.toLowerCase())
        }
      })

      if (!existingNames.has(baseName.toLowerCase())) {
        return baseName
      }

      const match = baseName.match(/^(.*?)(?:\s+(\d+))?$/)
      const unsuffixedBase = (match?.[1] || baseName).trim() || baseName

      let suffix = 2
      let candidate = `${unsuffixedBase} ${suffix}`

      while (existingNames.has(candidate.toLowerCase())) {
        suffix += 1
        candidate = `${unsuffixedBase} ${suffix}`
      }

      return candidate
    },
    //========================================================
    // normalise artefact descriptor payload value
    //--------------------------------------------
    normaliseArtefactDescriptorForSave(field, rawValue) {
      const fieldsDefaultingToUnknown = ['material', 'quality', 'age']

      if (!fieldsDefaultingToUnknown.includes(field)) {
        return rawValue
      }

      if (rawValue === null || rawValue === undefined) {
        return 'Unknown'
      }

      if (typeof rawValue === 'string') {
        const trimmed = rawValue.trim()
        return trimmed === '' ? 'Unknown' : trimmed
      }

      return rawValue
    },
    //========================================================
    // generic description input handler for child tabs
    //--------------------------------------------------
    onDescriptionInput(event) {
      this.onArtefactDescriptionInput(event)
    },
    //========================================================
    // generic notes input handler
    //----------------------------
    onNotesInput(event) {
      const raw = event && event.target ? (event.target.value ?? '') : ''
      const value = normaliseLeadingLetterText(raw)

      if (event && event.target) {
        event.target.value = value
      }

      if (this.activeItem) {
        this.activeItem.player_notes = value
      }

      this.delayedEmitUpdate('player_notes', value)
    },
    //========================================================
    // preload image
    //---------------
    preloadImage(image) {
      let image_name = getStringAfterLastSlash(image)
      console.log('-------------------------')
      console.log('IN preloadImage')
      //-----------------------------
      // get blob from src url fetch
      //-----------------------------
      fetch(image)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          return response.blob()
        })
        .then((blob) => {
          //---------------------------
          // create FormData with blob
          //---------------------------
          const formData = new FormData()
          formData.append('image', blob, image_name)

          // create an object URL for preview and store it
          // on the local item so the computed image getter
          // (local_item.image) can render it
          const url = URL.createObjectURL(blob)
          this.local_item = this.local_item || {}
          this.local_item.image = url
          this.local_item.image_cleared = false

          // keep the upload blob on the local item so that
          // add/submit payloads can carry image_file directly
          this.local_item.image_file = formData

          if (this.activeItem) {
            this.activeItem.image_file = formData
            this.activeItem.image_cleared = false
          }

          // emit both preview and upload blob to parent
          this.emitUpdate('image', url)
          this.emitUpdate('image_file', formData)
        })
        .catch((error) => {
          console.error('Failed to fetch and convert race image:', error)
        })
    },
    //========================================================
    // generic ENC formatter (two decimals)
    //-------------------------------------
    formatEnc(value) {
      const n = Number(value)
      if (Number.isNaN(n)) return ''
      if (n === 0) return '0.00'
      if (this.global && typeof this.global.twoDecimal === 'function') {
        return this.global.twoDecimal(n)
      }
      return n.toFixed(2)
    },
    //========================================================
    // set active data tab
    //---------------------
    setActiveTab(tab) {
      this.activeTab = tab

      if (tab === 'adjs') {
        this.getLinkedAdjustments()
      }

      if (tab === 'magic') {
        this.getLinkedRunes()
        this.getLinkedSpells()
      }
    },
    //========================================================
    // estimated value toggle (artefact tab)
    //---------------------------------------
    onEstimatedValueToggle(value) {
      this.local_item = this.local_item || {}
      this.local_item.estimated_value = !!value

      // keep active item in sync with toggle
      if (this.activeItem) {
        this.activeItem.estimated_value = !!value
      }

      this.syncArtefactValueDisplay()

      // existing items: emit realtime update
      if (!this.isNewItem) {
        this.emitUpdate('estimated_value', !!value)
      }
    },
    //========================================================
    // reset new artefact draft when selection mode changes
    //--------------------------------------------------------
    resetNewArtefactDraftForSelectionMode({
      campaign = false,
      droppedNonCampaignArtefact = false,
    } = {}) {
      this.local_item = this.local_item || {}
      this.save_item_id = null
      this.local_item.campaign = !!campaign
      this.local_item._dropped_non_campaign_artefact =
        !!droppedNonCampaignArtefact
      this.local_item._known_non_campaign_artefact = false

      if (this.activeItem) {
        this.activeItem.campaign = !!campaign
        this.activeItem._dropped_non_campaign_artefact =
          !!droppedNonCampaignArtefact
        this.activeItem._known_non_campaign_artefact = false
        this.activeItem.item = null
        this.activeItem.static_id = null
        this.activeItem.campaign_ref = null
        this.activeItem.artefact_base_item = null
      }

      if (this.isNewItem && this.item_type === 'Artefact') {
        this.local_item.item = null
        this.local_item.artefact_type = ''
        this.local_item.artefact_base_item = null
        this.local_item.actual_type = ''
        this.local_item.effective_type = ''
        this.local_item.material = 'Unknown'
        this.local_item.quality = 'Unknown'
        this.local_item.age = 'Unknown'
        this.local_item.race = null
        this.local_item.campaign_ref = null
        this.local_item._static_material_unknown = true
        this.local_item._static_quality_unknown = true
        this.local_item._static_age_unknown = true
        this.local_item._static_race_unknown = true
        this.local_item._static_pwr_storage_readonly = false
        this.local_item._session_material_editable = true
        this.local_item._session_quality_editable = true
        this.local_item._session_age_editable = true
        this.local_item._session_race_editable = true
        this.local_item._static_currency_unknown = true
        this.local_item._static_value_unknown = true
        this.local_item._static_value_lower_unknown = true
        this.local_item._static_value_upper_unknown = true
        this.local_item._static_estimated_value_readonly = false
        this.local_item._static_enc_readonly = false
        this.local_item._static_countable_readonly = false
        this.local_item._static_unit_name_readonly = false
        this.local_item._static_max_unit_count_readonly = false
        ;[
          'healing_type',
          'recovers',
          'poison_type',
          'pot',
          'effect',
          'delay',
          'damage_dice',
          'duration',
          'resist',
        ].forEach((field) => {
          this.local_item[`_static_${field}_readonly`] = false
        })
        this.local_item.value_lower = null
        this.local_item.value_upper = null
        this.local_item.currency = null
        this.local_item.estimated_value = !campaign
        this.local_item.description = ''
        this.local_item.name = ''
        this.local_item.pwr_storage = 0

        if (this.activeItem) {
          this.activeItem.description = ''
          this.activeItem.name = ''
          this.activeItem.pwr_storage = 0
        }

        this.clearInputError('name')
      }

      if (this.item_type === 'Artefact') {
        this.getDropDownValues()
      }
    },
    //========================================================
    // campaign item toggle (artefact tab)
    //-------------------------------------
    onCampaignItemToggle(value) {
      const boolVal = !!value
      this.resetNewArtefactDraftForSelectionMode({
        campaign: boolVal,
        droppedNonCampaignArtefact: false,
      })

      // existing items: emit realtime update
      if (!this.isNewItem) {
        this.emitUpdate('campaign', boolVal)
      }
    },
    //========================================================
    // dropped non-campaign artefact toggle (artefact tab)
    //------------------------------------------------------
    onDroppedArtefactToggle(value) {
      const boolVal = !!value

      this.resetNewArtefactDraftForSelectionMode({
        campaign: false,
        droppedNonCampaignArtefact: boolVal,
      })
    },
    handleCampaignArtefactsUpdated() {
      if (this.item_type !== 'Artefact') {
        return
      }

      const wantsCampaignArtefacts = this.isCampaignSelectionEnabled()

      if (!wantsCampaignArtefacts) {
        return
      }

      this.getDropDownValues()
    },
    //========================================================
    // artefact countable toggle (tally tab)
    //--------------------------------------
    onCountableToggle(value) {
      console.log('onCountableToggle value = ', value)

      this.local_item = this.local_item || {}
      const boolVal = !!value

      this.local_item.countable = boolVal
      if (this.activeItem) {
        this.activeItem.countable = boolVal
      }

      // When countable is turned off, clear unit_name and
      // ensure both unit-count fields are zeroed.
      if (!boolVal) {
        this.local_item.unit_name = null
        this.local_item.max_unit_count = 0
        this.local_item.cur_unit_count = 0

        if (this.activeItem) {
          this.activeItem.unit_name = null
          this.activeItem.max_unit_count = 0
          this.activeItem.cur_unit_count = 0
        }

        if (!this.isNewItem) {
          this.emitUpdate('unit_name', null)
          this.emitUpdate('max_unit_count', 0)
          this.emitUpdate('cur_unit_count', 0)
        }
      }

      if (!this.isNewItem) {
        this.emitUpdate('countable', boolVal)
      }
    },
    getInitialMaxUnitCount(unitName, candidateMax) {
      if (!unitName) {
        return 0
      }

      const parsedMax = Number(candidateMax)
      if (!Number.isNaN(parsedMax) && parsedMax > 0) {
        return parsedMax
      }

      return this.isNewItem ? 1 : 0
    },
    //========================================================
    // artefact unit-name select (tally tab)
    //---------------------------------------
    onUnitSelect(unit) {
      console.log('onUnitSelect unit = ', unit)
      this.local_item = this.local_item || {}

      // If cleared, treat as "no unit" and zero counts.
      if (!unit) {
        this.local_item.unit_name = null
        this.local_item.max_unit_count = 0
        this.local_item.cur_unit_count = 0

        if (this.activeItem) {
          this.activeItem.unit_name = null
          this.activeItem.max_unit_count = 0
          this.activeItem.cur_unit_count = 0
        }

        if (!this.isNewItem) {
          this.emitUpdate('unit_name', null)
          this.emitUpdate('max_unit_count', 0)
          this.emitUpdate('cur_unit_count', 0)
        }
        return
      }

      let value = unit
      if (typeof unit === 'object' && unit !== null) {
        value = unit.short_name || unit.name || unit.value || unit.id || null
      }

      this.local_item.unit_name = value
      const initialMax = this.getInitialMaxUnitCount(
        value,
        this.local_item.max_unit_count,
      )
      this.local_item.max_unit_count = initialMax
      if (this.activeItem) {
        this.activeItem.unit_name = value
        this.activeItem.max_unit_count = initialMax
      }

      const qtyValue = Number(
        this.local_item.tally_qty ?? this.activeItem?.tally_qty ?? 0,
      )
      if (!Number.isNaN(qtyValue) && qtyValue <= 0) {
        this.local_item.cur_unit_count = 0
        if (this.activeItem) {
          this.activeItem.cur_unit_count = 0
        }
        if (!this.isNewItem) {
          this.emitUpdate('cur_unit_count', 0)
        }
      }

      if (!this.isNewItem) {
        this.emitUpdate('unit_name', value)
        this.emitUpdate('max_unit_count', initialMax)
      }
    },
    //========================================================
    // artefact trait select
    //----------------------
    // onTraitSelect(traitId) {
    //   this.local_item = this.local_item || {}

    //   const selectedTraitId = traitId || null
    //   const selectedTrait =
    //     this.traits.find((trait) => trait?.id === selectedTraitId) ?? null
    //   const selectedTraitRows = selectedTrait
    //     ? [
    //         {
    //           id: selectedTrait.id ?? null,
    //           name: selectedTrait.name ?? '',
    //           description: selectedTrait.description ?? '',
    //         },
    //       ]
    //     : []

    //   this.local_item.trait = selectedTraitId
    //   this.local_item.item_traits = selectedTraitRows

    //   if (this.activeItem) {
    //     this.activeItem.trait = selectedTraitId
    //     this.activeItem.item_traits = selectedTraitRows
    //   }

    //   if (!this.isNewItem) {
    //     this.emitUpdate('trait', selectedTraitId)
    //   }
    // },
    onTraitSelect(traitId) {
      this.local_item = this.local_item || {}

      // When clearing, traitId is null â€” keep it null
      const selectedTraitId = traitId

      // Build the single trait row only if a trait is selected
      const selectedTrait = selectedTraitId
        ? (this.traits.find((t) => t?.id === selectedTraitId) ?? null)
        : null

      const selectedTraitRows = selectedTrait
        ? [
            {
              id: selectedTrait.id ?? null,
              name: selectedTrait.name ?? '',
              description: selectedTrait.description ?? '',
            },
          ]
        : []

      // Update local item
      this.local_item.trait = selectedTraitId
      this.local_item.item_traits = selectedTraitRows

      // Update active item if present
      if (this.activeItem) {
        this.activeItem.trait = selectedTraitId
        this.activeItem.item_traits = selectedTraitRows
      }

      // Emit update
      if (!this.isNewItem) {
        this.emitUpdate('trait', selectedTraitId)
      }
    },
    //========================================================
    // artefact lookup field change
    //------------------------------
    onArtefactFieldChange(field, value) {
      console.log(`onArtefactFieldChange field = ${field}, value = `, value)
      // keep local_item in sync with the select
      this.local_item = this.local_item || {}

      const descriptorFields = ['material', 'quality', 'age', 'race']
      const isUnknownDescriptorValue = (rawValue) => {
        if (rawValue === null || rawValue === undefined) {
          return true
        }

        if (typeof rawValue === 'string') {
          const trimmed = rawValue.trim()
          return trimmed === '' || trimmed === 'Unknown'
        }

        if (typeof rawValue === 'object') {
          if (typeof rawValue.name === 'string') {
            const trimmedName = rawValue.name.trim()
            if (trimmedName === '' || trimmedName === 'Unknown') {
              return true
            }
          }

          return rawValue.id === null || rawValue.id === undefined
        }

        return false
      }

      // Latch descriptor editability for the rest of the session if the
      // field was unknown when the user began editing it. This prevents the
      // control from immediately relocking when the newly selected value is
      // no longer Unknown.
      if (descriptorFields.includes(field)) {
        const currentValue = this.local_item[field] ?? this.activeItem?.[field]
        if (isUnknownDescriptorValue(currentValue)) {
          this.local_item[`_session_${field}_editable`] = true
        }
      }

      const previousArtefactType =
        field === 'artefact_type'
          ? this.normaliseArtefactTypeName(
              this.local_item[field] ?? this.activeItem?.[field],
            )
          : null

      // For id-based lookups (race, currency, etc.), treat "no
      // selection" as null so the v-select placeholder ("Select...")
      // can show correctly instead of an empty string.
      let normalisedValue = value === undefined || value === '' ? null : value

      if (field === 'artefact_type') {
        const artefactTypeName = this.normaliseArtefactTypeName(value)
        normalisedValue = artefactTypeName || null
      }

      this.local_item[field] = normalisedValue

      // for both new and existing items, mirror onto activeItem so
      // isChanged comparisons and submit payloads see the latest value
      if (this.activeItem) {
        this.activeItem[field] = normalisedValue
      }

      if (field === 'artefact_type') {
        this.applyArtefactTypeFlags(normalisedValue)
        this.applyArtefactTypeDefaults(normalisedValue, previousArtefactType)
        this.applyArtefactTypeEnc(normalisedValue)

        if (this.requiresItemCardArtefactBaseItem(normalisedValue)) {
          this.$store
            .dispatch('characters/loadArtefactBaseItems', normalisedValue)
            .catch(() => {})
        } else {
          this.local_item.artefact_base_item = null

          if (this.activeItem) {
            this.activeItem.artefact_base_item = null
          }
        }
      }

      // existing items: emit realtime update so backend stays in sync
      if (!this.isNewItem) {
        this.emitUpdate(field, normalisedValue)
      }

      // load container size details
      if (field === 'artefact_base_item' && value !== null) {
        console.log('Artefact base item selected, syncing derived props...')
        this.applyArtefactBaseItemDerivedProps(value)
      }

      // keep value/currency validation in sync whenever the
      // artefact currency field changes
      if (field === 'currency') {
        this.validateValueRange()
        this.syncArtefactValueDisplay()
      }
    },
    getArtefactBaseItemById(baseItemId) {
      if (!baseItemId) {
        return null
      }

      const getStaticItemById =
        this.$store.getters['characters/getStaticItemById']

      return typeof getStaticItemById === 'function'
        ? (getStaticItemById(baseItemId) ?? null)
        : null
    },
    applyArtefactBaseItemDerivedProps(baseItemId) {
      const baseItem = this.getArtefactBaseItemById(baseItemId)

      if (!baseItem) {
        return
      }

      this.local_item.wearable = this.toBooleanFlag(baseItem.wearable ?? false)
      this.local_item.storeable = this.toBooleanFlag(
        baseItem.storeable ?? false,
      )
      this.local_item.storage = this.toBooleanFlag(baseItem.storage ?? false)
      this.local_item.attach_point = this.toBooleanFlag(
        baseItem.attach_point ?? false,
      )
      this.local_item.attachable = this.toBooleanFlag(
        baseItem.attachable ?? false,
      )

      if (this.activeItem) {
        this.activeItem.wearable = this.local_item.wearable
        this.activeItem.storeable = this.local_item.storeable
        this.activeItem.storage = this.local_item.storage
        this.activeItem.attach_point = this.local_item.attach_point
        this.activeItem.attachable = this.local_item.attachable
      }

      if (this.local_item.storage) {
        this.local_item.contents_count_direct ??= 0
        this.local_item.contents_count_total ??= 0
      } else {
        this.local_item.contents_count_direct = null
        this.local_item.contents_count_total = null
      }

      this.loadArtefactBaseItemContainerSize(baseItemId)
    },
    hydrateExistingArtefactWeaponFields(source) {
      const fallbackDamageDice = getWeaponDamageDice(
        source,
        this.character?.skills,
      )

      this.local_item.hp = this.local_item.hp ?? 0
      this.local_item.damage_dice =
        this.local_item.damage_dice || fallbackDamageDice

      const weaponAp = source?.ap ?? 0
      this.max_ap = 10
      if (this.local_item.ap === null || this.local_item.ap === undefined) {
        this.local_item.ap = weaponAp
      }

      this.max_hp = 20
      this.local_item.impale = source?.impale ? 'Yes' : 'No'
      this.local_item.set_charge = source?.set_charge ? 'Yes' : 'No'
      this.local_item.load = source?.load ?? 0
      this.local_item.range = source?.range ?? 0
      this.local_item.skills = source?.skills ?? this.local_item.skills ?? []

      if (this.activeItem) {
        this.activeItem.damage_dice = this.local_item.damage_dice
        this.activeItem.ap = this.local_item.ap
        this.activeItem.hp = this.local_item.hp
        this.activeItem.impale = this.local_item.impale
        this.activeItem.set_charge = this.local_item.set_charge
        this.activeItem.load = this.local_item.load
        this.activeItem.range = this.local_item.range
        this.activeItem.skills = this.local_item.skills
      }
    },
    loadArtefactBaseItemContainerSize(baseItemId) {
      console.log(
        `Loading container size props for base item ID: ${baseItemId}...`,
      )
      if (!baseItemId) {
        return
      }

      const containerSize =
        this.$store.getters['characters/getContainerSizeByBaseItemId'](
          baseItemId,
        )

      this.local_item.container_size = containerSize ?? null

      if (this.activeItem) {
        this.activeItem.container_size = this.local_item.container_size
      }
    },
    validateValueRange() {
      const item = this.local_item || this.activeItem || {}

      // use the display* computed values so that validation
      // follows exactly what the user sees ("not empty" means
      // the input shows something other than blank)
      const displayLower = this.displayValueLower || ''
      const displayUpper = this.displayValueUpper || ''

      const rawCurrency = item.currency

      const hasLower = displayLower !== ''
      const hasUpper = displayUpper !== ''

      const lowerNum = hasLower ? Number(displayLower) : null
      const upperNum = hasUpper ? Number(displayUpper) : null

      // 1) If value upper provided, value lower must be present
      if (hasUpper && !hasLower) {
        const msg = window.bbUiErrorMsg(
          'Value lower required when value upper is set',
        )
        if (!this.errors.includes(msg)) {
          this.errors.push(msg)
        }
        if (!this.errorFields.includes('value_lower')) {
          this.errorFields.push('value_lower')
        }
      }

      // 2) If value upper provided, it must be >= value lower
      if (
        hasLower &&
        hasUpper &&
        !Number.isNaN(lowerNum) &&
        !Number.isNaN(upperNum) &&
        upperNum < lowerNum
      ) {
        const msg = window.bbUiErrorMsg(
          'Value upper must be greater than or equal to value lower',
        )
        if (!this.errors.includes(msg)) {
          this.errors.push(msg)
        }
        if (!this.errorFields.includes('value_upper')) {
          this.errorFields.push('value_upper')
        }
      }

      // 3) If either value lower or upper is set, currency must be selected
      const hasCurrency =
        rawCurrency !== null &&
        rawCurrency !== undefined &&
        String(rawCurrency) !== ''
      if ((hasLower || hasUpper) && !hasCurrency) {
        const msg = window.bbUiErrorMsg(
          'Currency required when value lower or upper is set',
        )
        if (!this.errors.includes(msg)) {
          this.errors.push(msg)
        }
        if (!this.errorFields.includes('currency')) {
          this.errorFields.push('currency')
        }
      }
    },
    validateRequiredArtefactBaseItem() {
      this.clearInputError('artefact_base_item')

      if (this.item_type !== 'Artefact') {
        return true
      }

      const selectedArtefactType =
        this.local_item?.artefact_type ?? this.activeItem?.artefact_type ?? ''

      if (!this.requiresItemCardArtefactBaseItem(selectedArtefactType)) {
        return true
      }

      const selectedBaseItemId =
        this.local_item?.artefact_base_item ??
        this.activeItem?.artefact_base_item ??
        null

      if (selectedBaseItemId) {
        return true
      }

      const msg = window.bbUiErrorMsg(
        'Base item must be selected for this artefact type',
      )

      if (!this.errors.includes(msg)) {
        this.errors.push(msg)
      }
      if (!this.errorFields.includes('artefact_base_item')) {
        this.errorFields.push('artefact_base_item')
      }

      return false
    },
    //========================================================
    // generic helpers for numeric display fields that should
    // show 0/null as blank but store the raw value on local_item
    //----------------------------------------------------------
    getDisplayNumericField(field) {
      const val = this.activeItem ? this.activeItem[field] : undefined
      if (val === 0 || val === '0' || val === null || val === undefined)
        return ''
      return String(val)
    },
    setDisplayNumericField(field, v) {
      this.local_item = this.local_item || {}
      const nextValue = v === '' ? '' : v

      this.local_item[field] = nextValue

      if (this.activeItem) {
        this.activeItem[field] = nextValue
      }
    },
    //========================================================
    // reduce object
    //----------------
    reduceObject(option) {
      if (!option) return null
      if (option.id === '__unknown_race__') return null
      return option.id ?? null
    },
    //========================================================
    // get container label
    //--------------------
    getContainerLabel(option) {
      if (!option) return ''
      return option.display_name || option.name || ''
    },
    //========================================================
    // get race label
    //----------------
    getRaceLabel(option) {
      if (!option) return ''
      return option.name || ''
    },
    //========================================================
    // get currency label
    //--------------------
    getCurrencyLabel(option) {
      if (!option) return ''
      return option.short_name || ''
    },
    formatValueNumber(value) {
      const numericValue = Number(value)

      if (!Number.isFinite(numericValue)) {
        return ''
      }

      return Number.isInteger(numericValue)
        ? `${numericValue}`
        : numericValue.toFixed(2).replace(/\.?0+$/, '')
    },
    getCurrencySuffix(rawCurrency) {
      if (
        rawCurrency === null ||
        rawCurrency === undefined ||
        rawCurrency === ''
      ) {
        return ''
      }

      if (typeof rawCurrency === 'object') {
        return (
          rawCurrency.short_name || rawCurrency.name || rawCurrency.id || ''
        )
      }

      const currencies = Array.isArray(this.currencies) ? this.currencies : []
      const matchedCurrency = currencies.find(
        (currency) =>
          currency &&
          (currency.id === rawCurrency ||
            currency.short_name === rawCurrency ||
            currency.name === rawCurrency),
      )

      return matchedCurrency?.short_name || matchedCurrency?.name || rawCurrency
    },
    buildArtefactValueDisplay(item = {}) {
      const lowerNumericValue = Number(item?.value_lower)
      const upperNumericValue = Number(item?.value_upper)
      const hasLower =
        item?.value_lower !== null &&
        item?.value_lower !== undefined &&
        item?.value_lower !== '' &&
        Number.isFinite(lowerNumericValue) &&
        lowerNumericValue !== 0
      const hasUpper =
        item?.value_upper !== null &&
        item?.value_upper !== undefined &&
        item?.value_upper !== '' &&
        Number.isFinite(upperNumericValue) &&
        upperNumericValue !== 0

      if (!hasLower) {
        return ''
      }

      const lowerValue = this.formatValueNumber(item.value_lower)
      if (!lowerValue) {
        return ''
      }

      const upperValue = hasUpper
        ? this.formatValueNumber(item.value_upper)
        : ''
      const currencySuffix = this.getCurrencySuffix(item?.currency)
      const valueDisplay = upperValue
        ? `${lowerValue}-${upperValue}${currencySuffix}`
        : `${lowerValue}${currencySuffix}`

      return item?.estimated_value ? `${valueDisplay} (E)` : valueDisplay
    },
    syncArtefactValueDisplay() {
      if (this.item_type !== 'Artefact') {
        return
      }

      const sourceItem = {
        ...(this.activeItem || {}),
        value_lower:
          this.local_item?.value_lower ?? this.activeItem?.value_lower ?? null,
        value_upper:
          this.local_item?.value_upper ?? this.activeItem?.value_upper ?? null,
        currency:
          this.local_item?.currency ?? this.activeItem?.currency ?? null,
        estimated_value:
          this.local_item?.estimated_value ??
          this.activeItem?.estimated_value ??
          false,
      }
      const valueDisplay = this.buildArtefactValueDisplay(sourceItem)

      if (this.activeItem) {
        this.activeItem.value = valueDisplay
      }

      if (this.local_item) {
        this.local_item.value = valueDisplay
      }
    },
    getArtefactBaseItemLabel(option) {
      if (!option) return ''

      const optionType = option.type || ''
      const optionName = option.name || ''

      if (
        optionType === 'Equipment' &&
        getNormalisedEquipmentType(option.equipment_type) === 'Container'
      ) {
        return `Container - ${optionName}`
      }

      return optionType ? `${optionType} - ${optionName}` : optionName
    },
    //========================================================
    // normalise artefact type to lookup name
    //----------------------------------------
    normaliseArtefactTypeName(raw) {
      if (raw === null || raw === undefined || raw === '') return ''

      const record = this.getArtefactTypeRecord(raw)
      if (record?.name) return record.name

      if (typeof raw === 'object') {
        return raw.name ?? ''
      }

      return String(raw).trim()
    },
    requiresItemCardArtefactBaseItem(rawArtefactType) {
      return ['Ammunition', 'Container'].includes(
        this.normaliseArtefactTypeName(rawArtefactType),
      )
    },
    //========================================================
    // get artefact type record
    //-------------------------
    getArtefactTypeRecord(raw) {
      const records = Array.isArray(this.artefact_type_records)
        ? this.artefact_type_records
        : []

      if (!records.length || raw === null || raw === undefined || raw === '') {
        return null
      }

      const rawId = typeof raw === 'object' ? (raw.id ?? null) : null
      if (rawId) {
        const byId = records.find((record) => record?.id === rawId)
        if (byId) return byId
      }

      const rawName =
        typeof raw === 'object' ? (raw.name ?? '') : String(raw).trim()
      const normalisedName = rawName.toLowerCase()

      return (
        records.find(
          (record) =>
            typeof record?.name === 'string' &&
            record.name.trim().toLowerCase() === normalisedName,
        ) ?? null
      )
    },
    isProvidedArtefactValue(value) {
      if (value === null || value === undefined) return false
      if (typeof value === 'string') return value.trim() !== ''
      return true
    },
    setArtefactDetailReadOnlyFlags(source = {}, fields = []) {
      this.local_item = this.local_item || {}
      fields.forEach((field) => {
        this.local_item[`_static_${field}_readonly`] =
          this.isNewItem &&
          this.toBooleanFlag(this.local_item?.campaign) &&
          this.isProvidedArtefactValue(source?.[field])
      })
    },
    clearArtefactDetailReadOnlyFlags() {
      const fields = [
        'healing_type',
        'recovers',
        'poison_type',
        'pot',
        'effect',
        'delay',
        'damage_dice',
        'duration',
        'resist',
      ]
      this.local_item = this.local_item || {}
      fields.forEach((field) => {
        this.local_item[`_static_${field}_readonly`] = false
      })
    },
    getArtefactDetailReadOnlyFields(fields = []) {
      if (!this.isNewItem || !this.toBooleanFlag(this.local_item?.campaign)) {
        return {}
      }

      return fields.reduce((readOnlyFields, field) => {
        readOnlyFields[field] = !!this.local_item?.[`_static_${field}_readonly`]
        return readOnlyFields
      }, {})
    },
    //========================================================
    // apply artefact type flag values
    //--------------------------------
    applyArtefactTypeFlags(rawArtefactType) {
      if (this.item_type !== 'Artefact') return

      const record = this.getArtefactTypeRecord(rawArtefactType)
      const flagFields = [
        'tallyable',
        'wearable',
        'storeable',
        'storage',
        'attachable',
        'attach_point',
        'countable',
      ]

      this.local_item = this.local_item || {}

      flagFields.forEach((field) => {
        const fieldValue = record ? !!record[field] : false
        this.local_item[field] = fieldValue
        if (this.activeItem) {
          this.activeItem[field] = fieldValue
        }
      })

      if (!this.local_item.countable) {
        this.local_item.unit_name = null
        this.local_item.max_unit_count = 0
        this.local_item.cur_unit_count = 0

        if (this.activeItem) {
          this.activeItem.unit_name = null
          this.activeItem.max_unit_count = 0
          this.activeItem.cur_unit_count = 0
        }
      }
    },
    //========================================================
    // derive ENC from selected artefact type
    //----------------------------------------
    getArtefactTypeDerivedEnc(rawArtefactType) {
      const record = this.getArtefactTypeRecord(rawArtefactType)
      if (!record) return null

      const directEnc = Number(record?.item_size_enc)
      if (!Number.isNaN(directEnc) && directEnc > 0) {
        return directEnc
      }

      const nestedEnc = Number(record?.item_size?.enc)
      if (!Number.isNaN(nestedEnc) && nestedEnc > 0) {
        return nestedEnc
      }

      return null
    },
    //========================================================
    // apply artefact type ENC value
    //--------------------------------
    applyArtefactTypeEnc(rawArtefactType) {
      if (this.item_type !== 'Artefact') return
      if (this.isEffectiveCampaignArtefact) return

      const derivedEnc = this.getArtefactTypeDerivedEnc(rawArtefactType)
      if (derivedEnc === null) return

      this.local_item = this.local_item || {}
      this.local_item.enc = derivedEnc

      if (this.activeItem) {
        this.activeItem.enc = derivedEnc
      }
    },
    //========================================================
    // normalise currency value to id
    //--------------------------------
    normaliseCurrencyId(raw) {
      if (raw === null || raw === undefined || raw === '') return null

      if (typeof raw === 'object') {
        return raw.id ?? null
      }

      if (typeof raw === 'string') {
        const trimmed = raw.trim()
        if (!trimmed) return null
        if (isUUID(trimmed)) return trimmed
        raw = trimmed
      }

      const list = Array.isArray(this.currencies) ? this.currencies : []
      const byId = list.find((currency) => currency && currency.id === raw)
      if (byId) return byId.id

      const byCode = list.find(
        (currency) =>
          currency && (currency.short_name === raw || currency.name === raw),
      )
      if (byCode) return byCode.id

      const getCurrencyByShortName =
        this.$store.getters['characters/getCurrencyByShortName']
      const byShortName =
        typeof getCurrencyByShortName === 'function'
          ? getCurrencyByShortName(raw)
          : null
      if (byShortName?.id) return byShortName.id

      return null
    },
    //========================================================
    // normalise race value to id
    //---------------------------
    normaliseRaceId(raw) {
      if (raw === null || raw === undefined || raw === '') return null

      if (typeof raw === 'object') {
        return raw.id ?? null
      }

      if (typeof raw !== 'string') {
        return raw
      }

      const trimmed = raw.trim()
      if (!trimmed) return null

      if (['unknown', 'n/a', 'na', 'none'].includes(trimmed.toLowerCase())) {
        return null
      }

      if (isUUID(trimmed)) {
        return trimmed
      }

      const getRaceById = this.$store.getters['races/getRaceById']
      const byId =
        typeof getRaceById === 'function' ? getRaceById(trimmed) : null
      if (byId?.id) return byId.id

      const getRaceByName = this.$store.getters['races/getRaceByName']
      const byName =
        typeof getRaceByName === 'function' ? getRaceByName(trimmed) : null
      if (byName?.id) return byName.id

      return null
    },
    resolveArtefactRaceId(staticItem = null) {
      return this.normaliseRaceId(
        this.item?.race ?? staticItem?.race?.id ?? staticItem?.race ?? null,
      )
    },
    //========================================================
    // sync artefact currency selection
    //----------------------------------
    syncArtefactCurrencySelection() {
      if (!this.isArtefactItem || !this.local_item) {
        return
      }

      const staticItem = this.selectedStaticItem
      const characterCurrencyId = this.normaliseCurrencyId(this.item?.currency)
      const staticCurrencyId = this.normaliseCurrencyId(staticItem?.currency)
      const localCurrencyId = this.normaliseCurrencyId(this.local_item.currency)

      this.local_item.currency =
        characterCurrencyId ??
        staticCurrencyId ??
        localCurrencyId ??
        this.default_currency?.id ??
        null
    },
    //========================================================
    // get attach location label
    //---------------------------
    getAttachLocationLabel(option) {
      if (!option) return ''
      return option.display_name || option.name || ''
    },
    //========================================================
    // on key down
    //-------------
    onKeyDown(e) {
      const tagName = e?.target?.tagName?.toLowerCase?.() || ''
      const inputType = e?.target?.type?.toLowerCase?.() || ''

      if (
        tagName === 'textarea' ||
        (tagName === 'input' &&
          ['text', 'search', 'email', 'url', 'tel', 'password'].includes(
            inputType,
          ))
      ) {
        return
      }

      if (!isAlphanumeric(e)) e.preventDefault()
    },
    //========================================================
    // prevent negative sign in number fields
    //----------------------------------------
    preventNegative(e) {
      // block minus, plus, decimal, and exponent keys
      const key = e.key
      if (
        key === '-' ||
        key === '+' ||
        key === 'Minus' ||
        key === 'Add' ||
        key === 'Subtract' ||
        key === '.' ||
        key === 'e' ||
        key === 'E'
      ) {
        e.preventDefault()
      }
    },
    //========================================================
    // capitalise first letter of text inputs
    //----------------------------------------
    onCapitaliseFirstLetter(field, event) {
      if (!event || !event.target) return
      const raw = event.target.value ?? ''
      const capitalised = normaliseLeadingLetterText(raw)
      const selectionStart = event.target.selectionStart
      const selectionEnd = event.target.selectionEnd

      // update the underlying item fields
      if (this.activeItem && field in this.activeItem) {
        this.activeItem[field] = capitalised
      }

      if (this.local_item && field in this.local_item) {
        this.local_item[field] = capitalised
      }

      // keep the input value in sync only when the transform changed it,
      // otherwise let the browser preserve the typed text as-is.
      if (capitalised !== raw) {
        event.target.value = capitalised

        if (
          typeof selectionStart === 'number' &&
          typeof selectionEnd === 'number' &&
          typeof event.target.setSelectionRange === 'function'
        ) {
          event.target.setSelectionRange(selectionStart, selectionEnd)
        }
      }

      // emit update
      this.delayedEmitUpdate(field, capitalised)
    },
    //========================================================
    // generic numeric input handler for display* fields
    //---------------------------------------------------
    handleNumericInput({
      displayProp,
      emitField,
      event,
      min = 0,
      max = 9999,
      zeroIfBlank = false,
    }) {
      if (!event || !event.target || !displayProp) return

      const raw = event.target.value ?? ''
      const cleaned = sanitisePositiveInt(raw, min, max)

      // update the corresponding display* computed, which in turn
      // writes through to local_item via its setter
      this[displayProp] = cleaned

      // optionally emit realtime updates for existing items
      if (emitField && !this.isNewItem) {
        let num
        if (cleaned === '') {
          num = zeroIfBlank ? 0 : null
        } else {
          num = parseInt(cleaned, 10)
        }
        this.delayedEmitUpdate(emitField, num)
      }

      // keep artefact value/currency validation in sync whenever
      // either value_lower or value_upper is edited
      if (
        displayProp === 'displayValueLower' ||
        displayProp === 'displayValueUpper'
      ) {
        this.validateValueRange()
        this.syncArtefactValueDisplay()
      }
    },
    //========================================================
    // armour overlap validation (layer/location)
    //--------------------------------------------
    checkArmourLayerConflict() {
      // only applies to armour items with a character + armour locations
      if (this.item_type !== 'Armour') return null
      if (!this.character || !Array.isArray(this.character.armour_locations))
        return null

      const item = this.activeItem || {}
      const layer = Number(item.layer)
      if (Number.isNaN(layer)) return null

      const coverageLocs = Array.isArray(item.coverage_locations)
        ? item.coverage_locations
        : []

      // determine which armour-location names this item will cover when equipped
      let targetNames = []
      if (item.armour_location) {
        const match = coverageLocs.find(
          (loc) => String(loc.id) === String(item.armour_location),
        )
        if (match && match.name) {
          targetNames.push(match.name)
        }
      } else {
        targetNames = coverageLocs
          .filter((loc) => loc && loc.fixed && loc.name)
          .map((loc) => loc.name)
      }

      if (!targetNames.length) return null

      const ignoreId = item.id ?? null

      for (const cal of this.character.armour_locations) {
        if (!cal || !targetNames.includes(cal.armour_location_name)) continue

        const equippedList = Array.isArray(cal.equipped_armour)
          ? cal.equipped_armour
          : []

        for (const ea of equippedList) {
          if (!ea) continue
          const existingLayer = Number(ea.layer)
          if (Number.isNaN(existingLayer)) continue
          // ignore the same item when editing
          if (ignoreId && ea.armour === ignoreId) continue

          if (existingLayer === layer) {
            const existingName = ea.armour_name || 'Existing armour'
            return {
              locationName: cal.armour_location_name,
              existingArmourName: existingName,
              message: window.bbUiErrorMsg(
                `Cannot equip armour: layer ${layer} already used at ${cal.armour_location_name} by ${existingName}`,
              ),
            }
          }
        }
      }

      return null
    },
    //========================================================
    // get parent container chain (immediate -> top)
    //-----------------------------------------------
    getParentContainerChain(containerId) {
      const chain = []
      if (
        !containerId ||
        !this.character ||
        !Array.isArray(this.character.items)
      ) {
        return chain
      }

      let current =
        this.character.items.find((c) => c.id === containerId) || null
      while (current && current.container) {
        const parentId = current.container
        const parent =
          this.character.items.find((c) => c.id === parentId) || null
        if (
          !parent ||
          !parent.container_size ||
          typeof parent.container_size.capacity !== 'number'
        ) {
          break
        }

        const capacity = parent.container_size.capacity
        const contentsEnc = parent.contents_enc ?? 0
        const rawAvailable = capacity - contentsEnc
        const availability =
          Math.round((rawAvailable + Number.EPSILON) * 100) / 100

        chain.push({
          id: parent.id,
          name: parent.display_name || parent.name || '',
          capacity,
          contents_enc: contentsEnc,
          availability,
        })

        current = parent
      }

      return chain
    },
    //========================================================
    // manage contents (container tab)
    //---------------------------------
    async manageContents(container) {
      try {
        await this.$refs.contentsDialog.show({
          container,
          character: this.character,
        })
      } catch (error) {
        console.error('Error managing contents:', error)
      }
    },
    //========================================================
    // manage attached items (attach_point host)
    //----------------------------------------
    async manageAttached(subjectItem) {
      try {
        await this.$refs.attachedDialog.show({
          subjectItem,
          character: this.character,
        })
      } catch (error) {
        console.error('Error managing attached items:', error)
      }
    },
    //========================================================
    // count directly attached items for an attach point
    //---------------------------------------------------
    getAttachedItemsCount(subjectItem) {
      const subjectId = subjectItem?.id ?? null

      if (!subjectId || !Array.isArray(this.character?.items)) {
        return 0
      }

      return this.character.items.filter(
        (item) => item && item.attach_location === subjectId,
      ).length
    },
    //========================================================
    // is container availability on tally change
    //-------------------------------------------
    isContainerAvailabilityOnTallyChange({ ref, count, prevCount }) {
      let availability = true

      console.log(
        'onCounterChanged: tally_qty change > 0 with container id = ',
        this.activeItem.container,
      )

      // get enc/capacity details for container
      const container = calcContainerAvailability({
        container_id: this.activeItem.container,
        character: this.character,
      })
      // guard error
      if (isEmpty(container)) {
        console.error(
          'Unable to calculate container availability for container id ',
          this.activeItem.container,
        )
      } else {
        const encPerItem = parseFloat(this.activeItem.enc || 0)
        if (Number.isNaN(encPerItem)) {
          console.warn(
            'isContainerAvailabilityOnTallyChange: invalid encPerItem = ',
            this.activeItem.enc,
          )
          return true
        }

        const capacity = container.capacity
        const contentsEnc = container.contents_enc

        console.log(
          'onCounterChanged: capacity = ',
          capacity,
          ' contentsEnc = ',
          contentsEnc,
          ' prevCount = ',
          prevCount,
          ' new count = ',
          count,
          ' isNewItem = ',
          this.isNewItem,
        )

        // ENC this item currently occupies and will occupy after the change
        const prevEncForThisItemRaw = encPerItem * prevCount
        const prevEncForThisItem =
          Math.round((prevEncForThisItemRaw + Number.EPSILON) * 100) / 100

        const newTotalEncForThisItemRaw = encPerItem * count
        const newTotalEncForThisItem =
          Math.round((newTotalEncForThisItemRaw + Number.EPSILON) * 100) / 100

        // incremental ENC change for this item
        const deltaEncRaw = newTotalEncForThisItem - prevEncForThisItem
        const deltaEnc = Math.round((deltaEncRaw + Number.EPSILON) * 100) / 100

        console.log(
          'onCounterChanged: prevEncForThisItem = ',
          prevEncForThisItem,
          ' newTotalEncForThisItem = ',
          newTotalEncForThisItem,
          ' deltaEnc = ',
          deltaEnc,
        )

        // if ENC is being reduced or unchanged, no capacity issue anywhere
        if (deltaEnc <= 0) {
          return true
        }

        // 1) check immediate container capacity using contents_enc + delta
        const newContentsEncRaw = contentsEnc + deltaEnc
        const newContentsEnc =
          Math.round((newContentsEncRaw + Number.EPSILON) * 100) / 100
        const availableBeforeRaw = capacity - contentsEnc
        const availableBefore =
          Math.round((availableBeforeRaw + Number.EPSILON) * 100) / 100

        console.log(
          'onCounterChanged: immediate container check: capacity = ',
          capacity,
          ' contentsEnc = ',
          contentsEnc,
          ' deltaEnc = ',
          deltaEnc,
          ' newContentsEnc = ',
          newContentsEnc,
        )

        if (newContentsEnc - capacity > 0) {
          this.clearInputError('tally_qty')

          this.errors.push(
            window.bbUiErrorMsg(
              `Cannot change tally qty to ${count}: ${container.name} capacity (${this.formatEnc(
                capacity,
              )}), available enc capacity (${this.formatEnc(
                availableBefore,
              )}).\nRequired extra (${this.formatEnc(deltaEnc)})`,
            ),
          )
          this.errorFields.push('tally_qty')

          this.local_item[ref] = prevCount
          if (
            this.$refs.tallyQtyCounter &&
            typeof this.$refs.tallyQtyCounter.setCount === 'function'
          ) {
            this.$refs.tallyQtyCounter.setCount(prevCount)
          }
          availability = false
        }

        // 2) if immediate container ok, ensure all parent containers
        //    up the chain also have enough spare capacity for deltaEnc
        if (availability) {
          const parentChain = this.getParentContainerChain(
            this.activeItem.container,
          )
          for (const parent of parentChain) {
            const parentCapacity = parent.capacity
            const parentContentsEnc = parent.contents_enc
            const parentNewContentsRaw = parentContentsEnc + deltaEnc
            const parentNewContents =
              Math.round((parentNewContentsRaw + Number.EPSILON) * 100) / 100
            const parentAvailableBeforeRaw = parentCapacity - parentContentsEnc
            const parentAvailableBefore =
              Math.round((parentAvailableBeforeRaw + Number.EPSILON) * 100) /
              100

            console.log(
              'onCounterChanged: parent container check: ',
              parent.name,
              ' capacity = ',
              parentCapacity,
              ' contentsEnc = ',
              parentContentsEnc,
              ' deltaEnc = ',
              deltaEnc,
              ' newContentsEnc = ',
              parentNewContents,
            )

            if (parentNewContents - parentCapacity > 0) {
              this.clearInputError('tally_qty')

              // build container chain string from the immediate container
              // up to the parent that exceeds capacity
              const immediateName = container.name || 'Container'
              const parentIndex = parentChain.findIndex(
                (p) => p.id === parent.id,
              )
              const chainUpToParent =
                parentIndex === -1
                  ? parentChain
                  : parentChain.slice(0, parentIndex + 1)
              const chainNames = [
                immediateName,
                ...chainUpToParent.map((p) => p.name).filter(Boolean),
              ]
              const chainLabel = chainNames.join(' -> ')

              this.errors.push(
                window.bbUiErrorMsg(
                  `Cannot change tally qty to ${count}: Container chain: ${chainLabel}.\nAvailable enc capacity of parent container (${this.formatEnc(
                    parentAvailableBefore,
                  )}). Required (${this.formatEnc(deltaEnc)})`,
                ),
              )
              this.errorFields.push('tally_qty')

              this.local_item[ref] = prevCount
              if (
                this.$refs.tallyQtyCounter &&
                typeof this.$refs.tallyQtyCounter.setCount === 'function'
              ) {
                this.$refs.tallyQtyCounter.setCount(prevCount)
              }
              availability = false
              break
            }
          }
        }
      }
      return availability
    },
    //========================================================
    // on counter changed
    //--------------------
    async onCounterChanged({ ref, count }) {
      console.log(
        'onCounterChanged ref = ',
        ref,
        ' count = ',
        count,
        ' isNewItem = ',
        this.isNewItem,
      )
      //--------------------------
      // ensure local item exists
      //--------------------------
      this.local_item = this.local_item || {}

      // handle tally_qty with container capacity and zero-clear logic
      if (ref === 'tally_qty') {
        const prevCount = this.activeItem?.tally_qty ?? 0
        console.log(
          'onCounterChanged: tally_qty, prevCount = ',
          prevCount,
          ' needsClearing = ',
          this.needsClearing,
        )

        //-------------------------------------------------------------------------
        // A. if count zero (existing items only)
        //    - for artefacts: confirm, then delete the item
        //    - for non-artefacts: (future) just clear linked values
        //-------------------------------------------------------------------------
        if (!this.isNewItem && count === 0) {
          const isArtefact =
            (this.activeItem && this.activeItem.type === 'Artefact') ||
            this.item_type === 'Artefact'
          let item_status = null

          // 1) Artefact-specific delete confirmation
          if (isArtefact) {
            item_status = await this.confirmArtefactDeleteStatus(
              this.artefactStatusOptions,
              this.getDeletionDisplayName(),
            )

            if (!item_status) {
              // user aborted delete; revert counter and bail out
              this.local_item[ref] = prevCount
              if (
                this.$refs.tallyQtyCounter &&
                typeof this.$refs.tallyQtyCounter.setCount === 'function'
              ) {
                this.$refs.tallyQtyCounter.setCount(prevCount)
              }
              return
            }
          }

          // 2) Where linked values exist, show ClearDialog so user
          //    can see exactly what will be removed.
          if (this.needsClearing) {
            const resp = await this.$refs.clearDialog.show({
              title: isArtefact
                ? 'Delete Artefact & Linked Values'
                : 'Clear Linked Values',
              subject: 'Tally quantity being set to zero:',
              message: isArtefact
                ? 'This will delete the artefact item and remove the following linked values:'
                : 'The following linked values will be removed from this item:',
              item: this.activeItem,
              character: this.character,
              adjustments: {
                item_characteristic_adj_reasons:
                  this.item_characteristic_adj_reasons,
                item_ability_adj_reasons: this.item_ability_adj_reasons,
                item_skill_adj_reasons_applied:
                  this.item_skill_adj_reasons_applied,
                item_skill_adj_reasons_optional:
                  this.item_skill_adj_reasons_optional,
              },
              okButton: isArtefact ? 'Delete' : 'OK',
              cancelButton: 'Cancel',
            })

            // user cancels
            if (resp && resp.cancel) {
              // revert counter to previous value
              this.local_item[ref] = prevCount
              if (
                this.$refs.tallyQtyCounter &&
                typeof this.$refs.tallyQtyCounter.setCount === 'function'
              ) {
                this.$refs.tallyQtyCounter.setCount(prevCount)
              }
              return
            }
          }

          if (isArtefact) {
            // For artefacts, treat tally_qty -> 0 as a real delete so
            // the same flow as the delete button (handleDeleteItem /
            // processDeleteItem) runs and the item is removed from the
            // character + change set. Also close the side panel to keep
            // behaviour consistent with confirmDeletion().
            this.$emit('delete-character-item', {
              item: this.activeItem,
              delete_payload: { status: item_status },
            })
            if (typeof this.closeSidePanel === 'function') {
              this.closeSidePanel()
            }
            return
          }

          // mark this zero-change as confirmed so any tally_qty watcher
          // can safely clear locations for non-artefacts.
          this.tallyZeroConfirmed = true
        }

        //-----------------------------------------------------------------------------------------
        // B. where container selected (tally qty > 0) - check container availability not exceeded
        //    for BOTH new and existing items
        //-----------------------------------------------------------------------------------------
        if (count > 0 && this.local_item.container) {
          const availability = this.isContainerAvailabilityOnTallyChange({
            ref,
            count,
            prevCount,
          })
          if (!availability) {
            // capacity exceeded: do not apply change
            return
          }
        }

        //-----------------------------------------------------------------------------------------
        // C. when tally moves from zero to positive, start the new stack at
        //    full units so current units match max units.
        //-----------------------------------------------------------------------------------------
        const numericPrevCount = Number(prevCount)
        const numericNextCount = Number(count)
        const unitName =
          this.local_item.unit_name ?? this.activeItem?.unit_name ?? null
        const hasUnitName =
          typeof unitName === 'string' ? unitName.trim() !== '' : !!unitName

        if (
          !Number.isNaN(numericNextCount) &&
          numericNextCount <= 0 &&
          hasUnitName
        ) {
          this.local_item.cur_unit_count = 0
          if (this.activeItem) {
            this.activeItem.cur_unit_count = 0
          }
          if (!this.isNewItem) {
            this.emitUpdate('cur_unit_count', 0)
          }
        }

        if (
          !Number.isNaN(numericPrevCount) &&
          !Number.isNaN(numericNextCount) &&
          numericPrevCount === 0 &&
          numericNextCount > 0 &&
          hasUnitName
        ) {
          const maxUnits = Number(
            this.local_item.max_unit_count ??
              this.activeItem?.max_unit_count ??
              0,
          )
          if (!Number.isNaN(maxUnits) && maxUnits > 0) {
            this.local_item.cur_unit_count = maxUnits
            if (!this.isNewItem) {
              this.emitUpdate('cur_unit_count', maxUnits)
            }
          }
        }

        //-------------------------
        // D. apply tally_qty change
        //-------------------------
        console.log('off to apply tally_qty change: count = ', count)
        this.local_item[ref] = count

        // emit realtime updates only for existing items
        if (!this.isNewItem) {
          this.emitUpdate(ref, count)
        }
        this.clearInputError('tally_qty')
        return
      }

      //-----------------------------
      // non-tally fields: simple set
      //-----------------------------
      // enforce cur_unit_count <= max_unit_count locally
      if (ref === 'max_unit_count') {
        const max = Number(count)
        if (!Number.isNaN(max)) {
          const currentCur = Number(
            this.local_item.cur_unit_count ??
              this.activeItem?.cur_unit_count ??
              0,
          )
          if (!Number.isNaN(currentCur) && currentCur > max) {
            // clamp current count down to new maximum
            this.local_item.cur_unit_count = max
            if (!this.isNewItem) {
              this.emitUpdate('cur_unit_count', max)
            }
          }
        }
      }

      if (ref === 'cur_unit_count') {
        const max = Number(
          this.local_item.max_unit_count ??
            this.activeItem?.max_unit_count ??
            0,
        )
        let cur = Number(count)

        // never allow cur_unit_count to exceed max_unit_count
        if (!Number.isNaN(max) && !Number.isNaN(cur) && cur > max) {
          cur = max
          count = max
        }
      }

      this.local_item[ref] = count
      if (this.activeItem) {
        this.activeItem[ref] = count
      }

      // When AP changes for armour, recompute dex_pen based on the
      // character-item AP while preserving the original static ratio.
      if (
        ref === 'ap' &&
        this.resolvedItemType === 'Armour' &&
        this.armour_base_ap > 0
      ) {
        const ratio = Number(count) / this.armour_base_ap
        if (!Number.isNaN(ratio) && this.armour_base_dex_pen >= 0) {
          const newDexPen = this.armour_base_dex_pen * ratio
          this.local_item.dex_pen = newDexPen
          if (this.activeItem) {
            this.activeItem.dex_pen = newDexPen
          }
          if (!this.isNewItem) {
            this.emitUpdate('dex_pen', newDexPen)
          }
        }
      }

      if (!this.isNewItem) {
        this.emitUpdate(ref, count)
      }
    },
    //========================================================
    // on item select
    //-----------------
    onItemSelect(name) {
      console.log('onItemSelect name = ', name)

      //---------------------------
      // ensure local item exists

      this.local_item = this.local_item || {}
      this.local_item.name = name || ''

      // changing the base item should clear any previous
      // validation errors (including equip-layer conflicts)
      this.errors = []
      this.errorFields = []

      if (!name) return

      //--------------------------
      // get specific static item

      const getItemByName =
        this.$store.getters['characters/getStaticItemByName']
      const getAvailableCampaignArtefactByName =
        this.$store.getters['characters/getAvailableCampaignArtefactByName']
      const getAvailableDroppedArtefactByName =
        this.$store.getters['characters/getAvailableDroppedArtefactByName']
      const getStaticItemById =
        this.$store.getters['characters/getStaticItemById']
      const getCampaignArtefactById =
        this.$store.getters['campaignArtefacts/getCampaignArtefactById']
      const getCampaignArtefactByName =
        this.$store.getters['campaignArtefacts/getCampaignArtefactByName']
      const resolveDropdownItem =
        typeof this.findItemByName === 'function'
          ? (options, candidateName) =>
              this.findItemByName(options, candidateName)
          : (options, candidateName) => {
              if (!candidateName || !Array.isArray(options)) {
                return null
              }

              const rawName = String(candidateName)
              const trimmedName = rawName.trim()
              if (!trimmedName) {
                return null
              }

              return (
                options.find((option) => option?.name === rawName) ??
                options.find((option) => option?.name === trimmedName) ??
                options.find(
                  (option) =>
                    typeof option?.name === 'string' &&
                    option.name.trim().toLowerCase() ===
                      trimmedName.toLowerCase(),
                ) ??
                null
              )
            }
      const dropdownItem = resolveDropdownItem(this.items, name)
      const item =
        this.isDroppedNonCampaignSelectionEnabled() &&
        typeof getAvailableDroppedArtefactByName === 'function'
          ? (getAvailableDroppedArtefactByName(name) ?? dropdownItem)
          : this.isCampaignSelectionEnabled() &&
              typeof getAvailableCampaignArtefactByName === 'function'
            ? (getAvailableCampaignArtefactByName(name) ?? dropdownItem)
            : typeof getItemByName === 'function'
              ? (getItemByName(name) ?? dropdownItem)
              : dropdownItem
      const selectedItemRecord =
        typeof getStaticItemById === 'function' && item?.id
          ? getStaticItemById(item.id)
          : null
      const persistedCampaignArtefactRecord = item?.campaign
        ? ((typeof getCampaignArtefactById === 'function' && item?.id
            ? (getCampaignArtefactById(item.id) ?? null)
            : null) ??
          (typeof getCampaignArtefactByName === 'function'
            ? (getCampaignArtefactByName(item?.name ?? name) ?? null)
            : null))
        : null
      const propertyItem = getItemPropertySource(item, getStaticItemById)
      const artefactBaseItem = this.getArtefactBaseItemById(
        item?.artefact_base_item ?? null,
      )
      const effectiveType = getEffectiveItemType(item, getStaticItemById)

      console.log('onItemSelect item = ', item)
      console.log('onItemSelect item fields:', {
        unit_name: item?.unit_name,
        max_unit_count: item?.max_unit_count,
        countable: item?.countable,
        campaign: item?.campaign,
        type: item?.type,
      })

      if (!item) {
        console.error('selected item not found in getStaticItemByName:', name)
        return
      } else {
        //------------------------------------
        // got static item - set local values

        const hasStaticShortDescription =
          typeof item.short_description === 'string' &&
          item.short_description.trim() !== ''

        this.save_item_id = item.id
        console.log('save_item_id = ', this.save_item_id)
        this.local_item.item = item.id ?? null
        this.local_item.name = item.name ?? name ?? ''
        this.local_item.type = item.type ?? ''
        this.local_item.actual_type = item.type ?? ''
        this.local_item.effective_type = effectiveType || item.type || ''
        this.local_item.artefact_type = item.artefact_type ?? ''
        this.local_item.enc = item.enc ?? ''
        this.local_item.currency_cost =
          item.currency_cost ??
          selectedItemRecord?.currency_cost ??
          propertyItem?.currency_cost ??
          ''
        this.local_item.equipment_type = getNormalisedEquipmentType(
          item.equipment_type ??
            selectedItemRecord?.equipment_type ??
            propertyItem?.equipment_type ??
            'n/a',
        )

        this.local_item.short_description = item.short_description ?? ''
        this.local_item.description = item.description ?? ''
        this.local_item.campaign_ref = item.campaign_ref ?? null
        this.local_item.pwr_storage = item.pwr_storage ?? 0
        this.local_item.trait = item.trait ?? null
        this.local_item.item_traits = Array.isArray(item.item_traits)
          ? [...item.item_traits]
          : []

        // -------------------------------------------------------
        // flags
        this.local_item.active = item.active ?? false
        this.local_item.campaign = item.campaign ?? false
        this.local_item.starter = item.starter ?? false
        const resolvedTallyable = this.toBooleanFlag(
          item.tallyable ??
            propertyItem?.tallyable ??
            this.propertySourceStaticItem?.tallyable ??
            this.selectedStaticItem?.tallyable ??
            false,
        )
        this.local_item.tallyable = resolvedTallyable
        if (resolvedTallyable) {
          this.local_item.tally_qty = item.tally_qty ?? 1
        }
        this.local_item.wearable = this.toBooleanFlag(
          item.wearable ?? artefactBaseItem?.wearable ?? false,
        )
        this.local_item.storeable = this.toBooleanFlag(
          item.storeable ?? artefactBaseItem?.storeable ?? false,
        )
        this.local_item.storage = this.toBooleanFlag(
          item.storage ?? artefactBaseItem?.storage ?? false,
        )
        this.local_item.attach_point = this.toBooleanFlag(
          item.attach_point ?? artefactBaseItem?.attach_point ?? false,
        )
        this.local_item.attachable = this.toBooleanFlag(
          item.attachable ?? artefactBaseItem?.attachable ?? false,
        )
        const resolvedCountable = this.toBooleanFlag(
          item.countable ??
            propertyItem?.countable ??
            this.propertySourceStaticItem?.countable ??
            this.selectedStaticItem?.countable ??
            false,
        )
        this.local_item.countable = resolvedCountable

        if (this.activeItem) {
          this.activeItem.item = this.local_item.item
          this.activeItem.name = this.local_item.name
          this.activeItem.type = this.local_item.type
          this.activeItem.equipment_type = this.local_item.equipment_type
          this.activeItem.currency_cost = this.local_item.currency_cost
          this.activeItem.campaign_ref = this.local_item.campaign_ref
          this.activeItem.active = this.local_item.active
          this.activeItem.campaign = this.local_item.campaign
          this.activeItem.starter = this.local_item.starter
          this.activeItem.tallyable = resolvedTallyable
          this.activeItem.countable = resolvedCountable
          this.activeItem.wearable = this.local_item.wearable
          this.activeItem.storeable = this.local_item.storeable
          this.activeItem.storage = this.local_item.storage
          this.activeItem.attach_point = this.local_item.attach_point
          this.activeItem.attachable = this.local_item.attachable
        }

        // -------------------------------------------------------
        // set countable values
        // - when countable=false: unit_name=null and unit counts are 0
        // - when unit_name is null: ensure unit counts are 0

        if (this.local_item.countable) {
          // For countable items, resolve unit_name with fallbacks:
          // 1. Direct unit_name from the item (campaign artefact)
          // 2. Fall back to base item's unit_name (for derived items like Poison)
          // 3. Fall back to selected static item's unit_name
          const staticUnitName =
            propertyItem?.unit_name ??
            this.propertySourceStaticItem?.unit_name ??
            this.selectedStaticItem?.unit_name ??
            null
          const unitName = item.unit_name ?? staticUnitName ?? null
          console.log(
            'countable logic: item.unit_name =',
            item.unit_name,
            'propertyItem.unit_name =',
            propertyItem?.unit_name,
            'staticUnitName =',
            staticUnitName,
            'unitName =',
            unitName,
          )
          this.local_item.unit_name = unitName
          this.local_item._static_unit_name_readonly =
            this.isNewItem && item.type !== 'Artefact' && !!unitName

          const baseMax =
            item.max_unit_count ?? propertyItem?.max_unit_count ?? 0
          const initialMax = unitName ? baseMax : 0

          // starting values (can be changed on the tally/healing tabs)
          this.local_item.max_unit_count = initialMax
          this.local_item.cur_unit_count = initialMax
        } else {
          this.local_item.unit_name = null
          this.local_item._static_unit_name_readonly = false
          this.local_item.max_unit_count = 0
          this.local_item.cur_unit_count = 0
        }

        // -------------------------------------------------------
        // set container/storage values

        this.local_item.container_capacity =
          item.container_size?.capacity ?? null
        this.local_item.container_size_short =
          item.container_size?.size_short ?? null
        this.local_item.container_size_long = item.container_size?.size ?? null
        this.local_item.container_size = item.container_size ?? null

        if (this.local_item.storage) {
          this.local_item.contents_count_direct = 0
          this.local_item.contents_count_total = 0
        } else {
          this.local_item.contents_count_direct = null
          this.local_item.contents_count_total = null
        }

        // -------------------------------------------------------
        // set armour values

        if (effectiveType === 'Armour' && propertyItem?.armour) {
          this.local_item.equipped = false
          this.local_item.broken = false
          this.local_item.coverage = propertyItem.armour.coverage ?? ''
          this.local_item.layer = propertyItem.armour.armour_material.layer ?? 0
          const materialAp = propertyItem.armour.armour_material.ap ?? 0
          const materialHp = propertyItem.armour.armour_material.hp ?? 0
          const hasExplicitArmourApOverride =
            item?.type === 'Artefact' &&
            item?.ap !== null &&
            item?.ap !== undefined
          const hasExplicitArmourHpOverride =
            item?.type === 'Artefact' &&
            item?.hp !== null &&
            item?.hp !== undefined
          const resolvedArmourAp = hasExplicitArmourApOverride
            ? item.ap
            : materialAp
          const resolvedArmourHp = hasExplicitArmourHpOverride
            ? item.hp
            : materialHp
          const baseDexPen = propertyItem.armour.dex_pen ?? 0
          const hasExplicitDexPenOverride =
            item?.type === 'Artefact' &&
            item?.dex_pen !== null &&
            item?.dex_pen !== undefined
          const resolvedDexPen =
            (hasExplicitDexPenOverride ? item.dex_pen : null) ??
            (materialAp > 0
              ? baseDexPen * (resolvedArmourAp / materialAp)
              : baseDexPen)
          this.local_item.ap = resolvedArmourAp
          this.local_item.hp = resolvedArmourHp
          this.local_item.dex_pen = resolvedDexPen
          this.max_ap = Math.max(materialAp, resolvedArmourAp)
          // capture baseline values for later dex-pen recalculation
          this.armour_base_ap = materialAp
          this.armour_base_dex_pen =
            typeof propertyItem.armour.dex_pen === 'number'
              ? propertyItem.armour.dex_pen
              : 0
          this.max_hp = Math.max(materialHp, resolvedArmourHp)
          this.local_item.coverage_locations =
            propertyItem.armour.coverage_locations ?? []

          if (this.activeItem) {
            this.activeItem.ap = this.local_item.ap
            this.activeItem.hp = this.local_item.hp
          }

          // armour locations (non-fixed coverage locations i.e. need selection e.g. left/right arm)
          const allCandidateLocs = Array.isArray(
            propertyItem.armour.coverage_locations,
          )
            ? propertyItem.armour.coverage_locations.filter(
                (a) => a && a.fixed == false,
              )
            : []

          const currentItemId = this.local_item.id ?? null
          const baseName = propertyItem.name || item.name || ''

          this.armour_locations = allCandidateLocs.filter((loc) => {
            if (!loc || !loc.name) return false
            const cal = Array.isArray(this.character.armour_locations)
              ? this.character.armour_locations.find(
                  (c) => c && c.armour_location_name === loc.name,
                )
              : null

            if (!cal || !Array.isArray(cal.equipped_armour)) return true

            const conflict = cal.equipped_armour.some((ea) => {
              if (!ea) return false
              if (currentItemId && ea.armour === currentItemId) return false
              return ea.armour_name === baseName
            })

            return !conflict
          })

          // -------------------------------------------------------
          // set weapon values
        } else if (
          effectiveType === 'Melee Weapon' ||
          effectiveType === 'Ranged Weapon'
        ) {
          const selectedArtefactWeapon =
            item?.type === 'Artefact' && Array.isArray(item?.skills)
              ? item
              : persistedCampaignArtefactRecord?.type === 'Artefact' &&
                  Array.isArray(persistedCampaignArtefactRecord?.skills)
                ? persistedCampaignArtefactRecord
                : null
          const resolvedWeaponItem =
            selectedArtefactWeapon ?? propertyItem ?? this.local_item ?? item
          const resolvedWeaponDamageDice =
            item?.damage_dice ??
            selectedItemRecord?.damage_dice ??
            persistedCampaignArtefactRecord?.damage_dice ??
            getWeaponDamageDice(resolvedWeaponItem, this.character?.skills)
          const existingWeaponAp = this.item?.ap ?? this.local_item.ap ?? null
          const existingWeaponHp = this.item?.hp ?? this.local_item.hp ?? null
          const resolvedWeaponAp =
            !this.isNewItem && existingWeaponAp !== null
              ? existingWeaponAp
              : (item?.ap ?? resolvedWeaponItem?.ap ?? 0)
          const resolvedWeaponHp =
            !this.isNewItem && existingWeaponHp !== null
              ? existingWeaponHp
              : (item?.hp ?? resolvedWeaponItem?.hp ?? 0)

          this.local_item.broken = false
          this.local_item.worn = false
          this.local_item.worn_location = null
          this.local_item.equipped = false
          this.local_item.damage_dice = resolvedWeaponDamageDice
          this.local_item.ap = resolvedWeaponAp
          this.local_item.hp = resolvedWeaponHp
          this.max_ap = Math.max(
            item?.ap ?? resolvedWeaponItem?.ap ?? 0,
            resolvedWeaponAp,
          )
          this.max_hp = Math.max(
            item?.hp ?? resolvedWeaponItem?.hp ?? 0,
            resolvedWeaponHp,
          )
          this.local_item.impale = resolvedWeaponItem?.impale ? 'Yes' : 'No'
          this.local_item.set_charge = resolvedWeaponItem?.set_charge
            ? 'Yes'
            : 'No'
          this.local_item.load = resolvedWeaponItem?.load ?? 0
          this.local_item.range = resolvedWeaponItem?.range ?? 0
          this.local_item.skills =
            resolvedWeaponItem?.skills ?? this.local_item.skills ?? []
          if (this.activeItem) {
            this.activeItem.damage_dice = this.local_item.damage_dice
            this.activeItem.ap = this.local_item.ap
            this.activeItem.hp = this.local_item.hp
          }
          // get cur str/dex chars
          this.local_item.str_cur =
            this.character?.characteristics?.find((c) => c.name === 'str')
              ?.cur ?? 0
          this.local_item.dex_cur =
            this.character?.characteristics?.find((c) => c.name === 'dex')
              ?.cur ?? 0

          // -------------------------------------------------------
          // set healing values
        } else if (item.type === 'Healing') {
          this.local_item.healing_type = item.healing_type ?? null
          this.local_item.recovers = item.recovers ?? null
          this.local_item.delay = item.delay ?? null
          this.setArtefactDetailReadOnlyFlags(item, [
            'healing_type',
            'recovers',
            'delay',
          ])
          if (!hasStaticShortDescription) {
            this.local_item.short_description =
              this.buildHealingShortDescription(this.local_item.recovers)
          }

          if (this.activeItem) {
            this.activeItem.short_description =
              this.local_item.short_description
          }
        } else if (item.type === 'Poison') {
          this.local_item.poison_type = item.poison_type ?? null
          this.local_item.pot = item.pot ?? null
          this.local_item.effect = item.effect ?? null
          this.local_item.delay = item.delay ?? null
          this.local_item.damage_dice = item.damage_dice ?? null
          this.local_item.duration = item.duration ?? null
          this.local_item.resist = item.resist ?? null
          this.setArtefactDetailReadOnlyFlags(item, [
            'poison_type',
            'pot',
            'effect',
            'delay',
            'damage_dice',
            'duration',
            'resist',
          ])
          if (!hasStaticShortDescription) {
            this.local_item.short_description =
              this.buildPoisonShortDescription(
                this.local_item.effect,
                this.local_item.damage_dice,
                this.local_item.duration,
              )
          }

          if (this.activeItem) {
            this.activeItem.resist = this.local_item.resist
            this.activeItem.short_description =
              this.local_item.short_description
          }
        } else if (item.type === 'Specialist') {
          this.local_item.effect = item.effect ?? null
          this.local_item.delay = item.delay ?? null
          this.local_item.damage_dice = item.damage_dice ?? null
          this.local_item.duration = item.duration ?? null
          this.local_item.resist = item.resist ?? null

          if (this.activeItem) {
            this.activeItem.effect = this.local_item.effect
            this.activeItem.delay = this.local_item.delay
            this.activeItem.damage_dice = this.local_item.damage_dice
            this.activeItem.duration = this.local_item.duration
            this.activeItem.resist = this.local_item.resist
          }
        }

        // -------------------------------------------------------
        // set artefact values
        if (item.type === 'Artefact') {
          const staticArtefactType = item.artefact_type ?? ''
          const normalisedArtefactType =
            typeof staticArtefactType === 'string'
              ? staticArtefactType.trim().toLowerCase()
              : ''
          const staticArtefactBaseItemId = item.artefact_base_item ?? null
          const staticMaterial = item.material ?? 'Unknown'
          const staticQuality = item.quality ?? 'Unknown'
          const staticAge = item.age ?? 'Unknown'
          const staticRace = item.race ?? 'Unknown'
          const staticCurrencyRaw = item.currency ?? null
          const staticEnc = item.enc
          const staticCountable = item.countable
          const staticUnitName = item.unit_name ?? null
          const staticMaxUnitCount = item.max_unit_count ?? null
          const rawValueLower = item.value_lower
          const rawValueUpper = item.value_upper
          const staticValueLower = rawValueLower ?? 0
          const staticValueUpper = rawValueUpper ?? 0
          const estimatedValue = item.estimated_value ?? false
          const image = item.image ?? selectedItemRecord?.image ?? null

          // normalise various currency representations (object/id/short code)
          // to a clean currency id so the v-select with :reduce="reduceObject"
          // can display the correct option.
          const normaliseCurrencyId = (raw) => {
            if (raw === null || raw === undefined || raw === '') return null
            if (typeof raw === 'object') return raw.id ?? null

            const list = Array.isArray(this.currencies) ? this.currencies : []
            const byId = list.find((c) => c && c.id === raw)
            if (byId) return byId.id

            const byCode = list.find(
              (c) => c && (c.short_name === raw || c.name === raw),
            )
            return byCode ? byCode.id : null
          }

          const staticCurrencyId = normaliseCurrencyId(staticCurrencyRaw)

          this.local_item.artefact_type = staticArtefactType
          this.local_item.artefact_base_item = staticArtefactBaseItemId
          this.local_item.material = staticMaterial
          this.local_item.quality = staticQuality
          this.local_item.age = staticAge
          this.local_item.race = this.normaliseRaceId(staticRace)
          this.local_item.currency =
            staticCurrencyId ?? this.local_item.currency ?? null
          this.local_item.value_lower = staticValueLower
          this.local_item.value_upper = staticValueUpper
          this.local_item.estimated_value = estimatedValue
          this.local_item.image = image

          if (this.activeItem) {
            this.activeItem.artefact_base_item = staticArtefactBaseItemId
            this.activeItem.image = image
          }

          if (normalisedArtefactType === 'poison') {
            this.local_item.poison_type = item.poison_type ?? null
            this.local_item.pot = item.pot ?? null
            this.local_item.effect = item.effect ?? null
            this.local_item.delay = item.delay ?? null
            this.local_item.damage_dice = item.damage_dice ?? null
            this.local_item.duration = item.duration ?? null
            this.local_item.resist = item.resist ?? null
            this.setArtefactDetailReadOnlyFlags(item, [
              'poison_type',
              'pot',
              'effect',
              'delay',
              'damage_dice',
              'duration',
              'resist',
            ])

            if (this.activeItem) {
              this.activeItem.poison_type = this.local_item.poison_type
              this.activeItem.pot = this.local_item.pot
              this.activeItem.effect = this.local_item.effect
              this.activeItem.delay = this.local_item.delay
              this.activeItem.damage_dice = this.local_item.damage_dice
              this.activeItem.duration = this.local_item.duration
              this.activeItem.resist = this.local_item.resist
            }
          } else if (normalisedArtefactType === 'healing') {
            this.local_item.healing_type = item.healing_type ?? null
            this.local_item.recovers = item.recovers ?? null
            this.local_item.delay = item.delay ?? null
            this.setArtefactDetailReadOnlyFlags(item, [
              'healing_type',
              'recovers',
              'delay',
            ])

            if (this.activeItem) {
              this.activeItem.healing_type = this.local_item.healing_type
              this.activeItem.recovers = this.local_item.recovers
              this.activeItem.delay = this.local_item.delay
            }
          }

          // For NEW campaign artefacts, remember which lookup
          // values came from an "Unknown" static default so the
          // Artefact tab can allow those specific fields to be
          // edited (and re-edited) until the item is saved.
          if (this.isNewItem && this.local_item.campaign) {
            const hasStaticLower =
              rawValueLower !== null &&
              rawValueLower !== undefined &&
              rawValueLower !== 0
            const hasStaticUpper =
              rawValueUpper !== null &&
              rawValueUpper !== undefined &&
              rawValueUpper !== 0
            const staticPwrStorageValue = Number(item?.pwr_storage ?? 0)
            const hasStaticPwrStorage = Number.isFinite(staticPwrStorageValue)
              ? staticPwrStorageValue !== 0
              : Boolean(item?.pwr_storage)
            const hasStaticEnc = staticEnc !== null && staticEnc !== undefined
            const hasStaticMaxUnitCount =
              staticMaxUnitCount !== null && staticMaxUnitCount !== undefined
            this.local_item._static_material_unknown =
              !staticMaterial || staticMaterial === 'Unknown'
            this.local_item._static_quality_unknown =
              !staticQuality || staticQuality === 'Unknown'
            this.local_item._static_age_unknown =
              !staticAge || staticAge === 'Unknown'
            this.local_item._static_race_unknown =
              !staticRace || staticRace === 'Unknown'
            this.local_item._session_material_editable =
              this.local_item._static_material_unknown
            this.local_item._session_quality_editable =
              this.local_item._static_quality_unknown
            this.local_item._session_age_editable =
              this.local_item._static_age_unknown
            this.local_item._session_race_editable =
              this.local_item._static_race_unknown
            this.local_item._static_currency_unknown = !staticCurrencyId
            this.local_item._static_pwr_storage_readonly = hasStaticPwrStorage
            // per-field flags so lower/upper can be independently
            // editable when their static value is zero/blank
            this.local_item._static_value_lower_unknown = !hasStaticLower
            this.local_item._static_value_upper_unknown = !hasStaticUpper
            // keep aggregate flag for any-static-value cases (backwards compat)
            this.local_item._static_value_unknown =
              !hasStaticLower && !hasStaticUpper
            // static-driven fields for campaign artefacts: when the
            // static item defines these, treat them as read-only on
            // the Tally tab for new campaign artefacts.
            this.local_item._static_enc_readonly = !!hasStaticEnc
            this.local_item._static_countable_readonly =
              staticCountable === true || staticCountable === false
            this.local_item._static_unit_name_readonly = !!staticUnitName
            console.log(
              'onItemSelect: static artefact unit_name = ',
              staticUnitName,
              ' readonly = ',
              this.local_item._static_unit_name_readonly,
            )
            this.local_item._static_max_unit_count_readonly =
              !!hasStaticMaxUnitCount
            // estimated flag from the static item: for new campaign
            // artefacts the Estimated/Actual toggle is always read-only
            // because the value is defined by the static item record.
            this.local_item._static_estimated_value_readonly = true
          }

          // -------------------------------------------------------
          // Handle artefact_base_item: if the artefact links to a
          // base item (e.g., a weapon), use base skills only when the
          // selected artefact does not already carry its own ItemSkill rows.
          const artefactBaseItemId = staticArtefactBaseItemId
          if (artefactBaseItemId) {
            const getStaticItemById =
              this.$store.getters['characters/getStaticItemById']
            if (typeof getStaticItemById === 'function') {
              const baseItem = getStaticItemById(artefactBaseItemId)
              if (baseItem) {
                // If the base item is a weapon with skills, propagate them
                // only as a fallback; artefact weapon skill dice may be
                // overridden on the artefact itself.
                if (
                  (baseItem.type === 'Melee Weapon' ||
                    baseItem.type === 'Ranged Weapon') &&
                  Array.isArray(baseItem.skills) &&
                  baseItem.skills.length > 0 &&
                  !(
                    Array.isArray(this.local_item.skills) &&
                    this.local_item.skills.length > 0
                  )
                ) {
                  this.local_item.skills = [...baseItem.skills]
                }
              }
            }
          }
        }
      }
    },
    //========================================================
    // on container select
    //---------------------
    onContainerSelect(containerId) {
      console.log('onContainerSelect containerId = ', containerId)

      // const selected = this.containers.find((c) => c.id === containerId) || null
      // const name = selected?.name ?? null
      console.log('onContainerSelect id = ', containerId, ' name = ', name)
      //--------------------------
      // ensure local item exists
      //--------------------------
      this.local_item = this.local_item || {}

      // last valid container selection before this change
      const prevContainer = this.previousContainerId ?? null

      // allow clearing container without capacity checks
      if (!containerId) {
        this.local_item.container = null
        if (!this.isNewItem) {
          this.emitUpdate('container', this.local_item.container)
        }
        this.previousContainerId = null
        this.clearInputError('container')
        return
      }

      console.log('onContainerSelect prevContainer = ', prevContainer)

      // if selecting the same container, just keep it and clear any error
      if (containerId === prevContainer) {
        this.local_item.container = containerId
        this.clearInputError('container')
        return
      }

      //--------------------------
      // check container capacity
      //--------------------------
      const container = calcContainerAvailability({
        container_id: containerId,
        character: this.character,
      })

      if (isEmpty(container)) {
        console.error(
          'Unable to calculate container availability for container id ',
          containerId,
        )
        // revert selection back to previous container
        this.local_item.container = prevContainer
        return
      }

      // total enc required for this item (tally_qty * enc per item)
      const encRequired = this.calcQtyTotEnc
      const roundedRequired =
        Math.round((encRequired + Number.EPSILON) * 100) / 100
      const roundedAvailability =
        Math.round((container.availability + Number.EPSILON) * 100) / 100

      console.log(
        'onContainerSelect: encRequired = ',
        roundedRequired,
        ' roundedAvailability = ',
        roundedAvailability,
      )

      if (roundedRequired - roundedAvailability > 0) {
        // insufficient capacity in the immediate container: revert selection
        this.clearInputError('container')

        this.errors.push(
          window.bbUiErrorMsg(
            `Cannot select ${container.name || 'Selected container'}: Capacity (${this.formatEnc(
              container.capacity,
            )}), available enc capacity (${this.formatEnc(
              container.availability,
            )}).\nRequired (${this.formatEnc(encRequired)})`,
          ),
        )
        this.errorFields.push('container')

        // revert v-model back to previous valid container
        this.local_item.container = prevContainer
        return
      }

      //------------------------------
      // check parent container chain
      //------------------------------
      const parentChain = this.getParentContainerChain(containerId)
      for (const parent of parentChain) {
        const parentAvailable =
          Math.round(((parent.availability ?? 0) + Number.EPSILON) * 100) / 100

        console.log(
          'onContainerSelect: parent container check: ',
          parent.name,
          ' available = ',
          parentAvailable,
          ' required = ',
          roundedRequired,
        )

        if (roundedRequired - parentAvailable > 0) {
          this.clearInputError('container')

          // build container chain string from the immediate container
          // up to the parent that exceeds capacity
          const immediateName = container.name || 'Container'
          const parentIndex = parentChain.findIndex((p) => p.id === parent.id)
          const chainUpToParent =
            parentIndex === -1
              ? parentChain
              : parentChain.slice(0, parentIndex + 1)
          const chainNames = [
            immediateName,
            ...chainUpToParent.map((p) => p.name).filter(Boolean),
          ]
          const chainLabel = chainNames.join(' -> ')

          this.errors.push(
            window.bbUiErrorMsg(
              `Cannot select ${container.name || 'Selected container'}: Container chain: ${chainLabel}.\nAvailable enc capacity of parent container (${this.formatEnc(
                parentAvailable,
              )}). Required (${this.formatEnc(encRequired)})`,
            ),
          )
          this.errorFields.push('container')

          // revert v-model back to previous valid container
          this.local_item.container = prevContainer
          return
        }
      }

      // capacity ok across chain: apply selection and emit update for existing items
      this.local_item.container = containerId
      // business rule: if an armour item is placed in a container,
      // it should no longer be equipped
      if (this.resolvedItemType === 'Armour') {
        this.activeItem.equipped = false
        // clear any armour-location validation error and selection when
        // switching from equipped armour to stored-in-container
        this.local_item.armour_location = null
        this.clearInputError('armour_location')
        if (!this.isNewItem) {
          this.emitUpdate('equipped', false)
          this.emitUpdate('armour_location', null)
        }
      }
      this.previousContainerId = containerId

      if (!this.isNewItem) {
        this.emitUpdate('container', this.local_item.container)
      }
      this.clearInputError('container')
    },
    //========================================================
    // on worn (item/armour) location select
    //---------------------------------------
    onWornLocationSelect(wornLocationId) {
      // const selected =
      //   this.worn_locations.find((c) => c.id === wornLocationId) || null
      // const name = selected?.name ?? null
      console.log('onWornLocationSelect id = ', wornLocationId)
      //--------------------------
      // ensure local item exists
      //--------------------------
      this.local_item = this.local_item || {}
      this.local_item.worn_location = wornLocationId || null
      if (this.activeItem) {
        this.activeItem.worn_location = this.local_item.worn_location
      }
      if (
        (this.resolvedItemType === 'Melee Weapon' ||
          this.resolvedItemType === 'Ranged Weapon') &&
        this.activeItem?.worn
      ) {
        this.showWeaponWornLocationField = true
      }
      // clear any prior validation error once a valid
      // worn_location has been chosen
      if (this.local_item.worn_location) {
        this.clearInputError('worn_location')
      }
      //--------------------------------------
      // real-time changes for existing items
      //--------------------------------------
      if (!this.isNewItem) {
        // emit id for processing; name is only for debug/logging
        this.emitUpdate('worn_location', this.local_item.worn_location)
      }
    },
    //========================================================
    // on attached location select
    //----------------------------
    onAttachedLocationSelect(attachLocationId) {
      // const selected =
      //   this.attach_locations.find((c) => c.id === attachLocationId) || null
      // const name = selected?.name ?? null
      console.log('onAttachedLocationSelect id = ', attachLocationId)
      //--------------------------
      // ensure local item exists
      //--------------------------
      this.local_item = this.local_item || {}
      this.local_item.attach_location = attachLocationId || null
      //--------------------------------------
      // real-time changes for existing items
      //--------------------------------------
      if (!this.isNewItem) {
        // emit id for processing; name is only for debug/logging
        this.emitUpdate('attach_location', this.local_item.attach_location)
      }
    },
    //========================================================
    // on armour location select
    //---------------------------
    onArmourLocationSelect(armourLocationId) {
      // const source = Array.isArray(this.armour_locations)
      //   ? this.armour_locations
      //   : []
      // const selected = source.find((c) => c.id === armourLocationId) || null
      // const name = selected?.name ?? null
      console.log('onArmourLocationSelect id = ', armourLocationId)
      //--------------------------
      // ensure local item exists
      //--------------------------
      this.local_item = this.local_item || {}
      this.local_item.armour_location = armourLocationId || null

      // clear any transient "select location" error when a valid selection is made
      if (armourLocationId) {
        this.clearInputError('armour_location')
      }
      //--------------------------------------
      // real-time changes for existing items
      //--------------------------------------
      if (!this.isNewItem) {
        // emit id for processing; name is only for debug/logging
        this.emitUpdate('armour_location', this.local_item.armour_location)

        // For flexible armour, if the user previously toggled
        // equipped=true but we deferred emitting that change
        // (pendingFlexibleEquip), now that a specific
        // armour_location has been chosen we can safely emit
        // the equipped=true update so that downstream mapping
        // (processAddEquippedArmour) uses the correct location.
        const requiresArmourLocation =
          Array.isArray(this.armour_locations) &&
          this.armour_locations.length > 0

        if (
          this.pendingFlexibleEquip &&
          requiresArmourLocation &&
          armourLocationId &&
          this.resolvedItemType === 'Armour'
        ) {
          console.log(
            'onArmourLocationSelect: emitting deferred equipped=true for flexible armour',
          )
          this.pendingFlexibleEquip = false
          this.emitUpdate('equipped', true)
        }
      }
    },
    //========================================================
    // on worn toggle (weapons)
    //---------------------------
    onWornToggle(value) {
      console.log('onWornToggle value = ', value)

      if (!this.activeItem) return
      if (!(this.activeItem?.wearable ?? this.local_item?.wearable)) return

      this.local_item = this.local_item || {}
      this.local_item.worn = value
      this.activeItem.worn = value
      if (
        this.resolvedItemType === 'Melee Weapon' ||
        this.resolvedItemType === 'Ranged Weapon'
      ) {
        this.showWeaponWornLocationField = value
      }

      if (!this.isNewItem) {
        this.emitUpdate('worn', value)
      }
    },
    //========================================================
    // on held toggle
    //-----------------
    onHeldToggle(value) {
      console.log('onHeldToggle value = ', value)

      this.local_item = this.local_item || {}
      this.local_item.held = value

      // apply held flag locally (v-model already updates activeItem.held)
      this.activeItem.held = value

      // for armour: held=true implies not equipped
      if (this.resolvedItemType === 'Armour' && value) {
        this.local_item.equipped = false
        this.activeItem.equipped = false
        // clear any armour-location validation error and selection when
        // switching from equipped armour to held
        this.local_item.armour_location = null
        this.clearInputError('armour_location')
        if (!this.isNewItem) {
          this.emitUpdate('equipped', false)
          this.emitUpdate('armour_location', null)
        }
      }

      // emit held change for existing items
      if (!this.isNewItem) {
        this.emitUpdate('held', value)
      }
    },
    //========================================================
    // ENC input handler (tally tab)
    //--------------------------------
    onEncInput(event) {
      if (!this.activeItem || !event || !event.target) return

      const raw = event.target.value ?? ''
      const num = raw === '' ? null : Number(raw)

      this.activeItem.enc = Number.isNaN(num) ? null : num
      this.local_item = this.local_item || {}
      this.local_item.enc = this.activeItem.enc
    },
    onEncBlur(value) {
      if (!this.activeItem) return

      const num = Number(value)
      if (Number.isNaN(num)) return

      const fixed = Number(num.toFixed(2))
      this.activeItem.enc = fixed

      if (this.local_item) {
        this.local_item.enc = fixed
      }

      if (!this.isNewItem) {
        this.delayedEmitUpdate('enc', fixed)
      }
    },
    //========================================================
    // on equipped toggle
    //---------------------
    onEquippedToggle(value) {
      console.log('onEquippedToggle value = ', value)

      // ensure local item exists
      this.local_item = this.local_item || {}
      this.local_item.equipped = value

      // apply equipped flag locally
      this.activeItem.equipped = value

      // business rule: if item is equipped, it cannot be in a container or held
      if (value) {
        // ---------------------------------------------
        // front-end validation: prevent overlapping armour
        // with same layer on the same character location
        // ---------------------------------------------
        const conflict = this.checkArmourLayerConflict()
        if (conflict) {
          this.errors.push(conflict.message)
          this.errorFields.push('equipped')

          // revert toggle
          this.activeItem.equipped = false
          return
        }

        // clear any container selection
        this.local_item.container = null
        this.previousContainerId = null

        if (!this.isNewItem) {
          this.emitUpdate('container', null)
        }

        // also clear held status
        this.activeItem.held = false
        if (!this.isNewItem) {
          this.emitUpdate('held', false)
        }

        // if this armour requires a specific location but none is yet selected,
        // surface a validation message immediately
        const requiresArmourLocation =
          Array.isArray(this.armour_locations) &&
          this.armour_locations.length > 0
        const hasArmourLocation =
          (this.local_item && this.local_item.armour_location) ||
          this.activeItem?.armour_location

        if (requiresArmourLocation && !hasArmourLocation) {
          this.errors.push(
            window.bbUiErrorMsg(
              'Equipped armour requires a specific armour location to be selected',
            ),
          )
          this.errorFields.push('armour_location')

          // For flexible armour, defer emitting the equipped=true
          // change until the user actually picks a specific
          // armour_location from the dropdown. This mirrors the
          // previous behaviour where equip processing only ran
          // after a location was chosen.
          if (!this.isNewItem) {
            this.pendingFlexibleEquip = true
          }
        }
      } else {
        // when unequipped, clear any equipped-related validation error
        this.clearInputError('equipped')
        // when unequipped, clear any selected armour location
        this.local_item.armour_location = null
        // clear any "equipped armour requires location" message
        this.clearInputError('armour_location')
        if (!this.isNewItem) {
          this.emitUpdate('armour_location', null)
        }
      }

      // emit equipped change for existing items
      if (!this.isNewItem) {
        const requiresArmourLocation =
          Array.isArray(this.armour_locations) &&
          this.armour_locations.length > 0
        const hasArmourLocation =
          (this.local_item && this.local_item.armour_location) ||
          this.activeItem?.armour_location

        // For flexible armour with no selected location yet, do not
        // emit equipped=true now; it will be emitted once the
        // armour_location is chosen in onArmourLocationSelect.
        if (value && requiresArmourLocation && !hasArmourLocation) {
          console.log(
            'onEquippedToggle: deferring equipped=true emit for flexible armour',
          )
        } else {
          this.emitUpdate('equipped', value)
        }
      }
    },
    isArtefactContainer(item) {
      if (!item || !item.type) return false
      return item.type === 'Artefact' && item.artefact_type === 'Container'
    },
    //========================================================
    // get dropdown values
    //---------------------
    async getDropDownValues() {
      console.log('getDropDownValues for item_type = ', this.itemType)
      const requestedItemType = this.item_type
      const isAllFilter = requestedItemType === 'All'
      const isArtefactFilter = requestedItemType === 'Artefact'
      const characterId = this.getResolvedCharacterId()
      const arenaId = this.getResolvedArenaId()
      const wantsCampaignArtefacts = this.isCampaignSelectionEnabled()
      const wantsDroppedArtefacts = this.isDroppedNonCampaignSelectionEnabled()
      const currentStaticItemId =
        this.activeItem?.static_id ??
        this.activeItem?.item ??
        this.item?.static_id ??
        this.item?.item ??
        null

      console.log(
        'getDropDownValues: isAllFilter = ',
        isAllFilter,
        ' isArtefactFilter = ',
        isArtefactFilter,
        ' wantsCampaignArtefacts = ',
        wantsCampaignArtefacts,
        ' wantsDroppedArtefacts = ',
        wantsDroppedArtefacts,
      )

      if (isArtefactFilter && wantsDroppedArtefacts) {
        console.log(
          'getDropDownValues: loading available dropped artefacts for characterId = ',
          characterId,
        )
        await this.$store.dispatch(
          'characters/loadAvailableDroppedArtefacts',
          characterId,
        )
      } else if (isArtefactFilter && wantsCampaignArtefacts) {
        console.log(
          'getDropDownValues: loading available campaign artefacts for characterId = ',
          characterId,
        )
        console.log(
          'getDropDownValues: loading available campaign artefacts for arenaId = ',
          arenaId,
        )
        await this.$store.dispatch(
          'characters/loadAvailableCampaignArtefacts',
          {
            characterId,
            arenaId,
            currentItemId: currentStaticItemId,
          },
        )
      }
      //----------------------------
      // active items (by type/all)
      //----------------------------
      const getStaticActiveItemsByType =
        this.$store.getters['characters/getStaticActiveItemsByType']
      const getStaticItemById =
        this.$store.getters['characters/getStaticItemById']
      const activeItemsByType =
        typeof getStaticActiveItemsByType === 'function'
          ? getStaticActiveItemsByType(requestedItemType)
          : []
      const allActiveItems =
        typeof getStaticActiveItemsByType === 'function'
          ? getStaticActiveItemsByType('All')
          : []
      const allItems = isArtefactFilter
        ? activeItemsByType
        : isAllFilter
          ? activeItemsByType.filter((it) => it?.type !== 'Artefact')
          : allActiveItems.filter(
              (it) =>
                getEffectiveItemType(it, getStaticItemById) ===
                requestedItemType,
            )

      // ARTEFACTS:
      // - only offer static artefacts flagged as campaign items
      // - enforce uniqueness of underlying static item across characters
      // - do not offer artefact items already in use by any CharacterItem except this one when editing

      let items = allItems
      if (isArtefactFilter) {
        const availableCampaignArtefacts =
          this.$store.getters['characters/getAvailableCampaignArtefacts']
        const campaignArtefactItems = Array.isArray(availableCampaignArtefacts)
          ? availableCampaignArtefacts
          : []

        const isCampaignStaticArtefact = (item) => {
          const campaignRef = Number(item?.campaign_ref ?? 0)
          return !Number.isNaN(campaignRef) && campaignRef > 0
        }

        const matchingCampaignItems = wantsCampaignArtefacts
          ? campaignArtefactItems
          : allItems.filter(
              (it) =>
                it && isCampaignStaticArtefact(it) === wantsCampaignArtefacts,
            )

        const characters = this.$store.getters['characters/getCharacters'] || []

        const usedItemIds = new Set()
        const collectUsedArtefactIds = (characterItems) => {
          const entries = Array.isArray(characterItems) ? characterItems : []
          entries.forEach((ci) => {
            if (ci && ci.item != null) {
              usedItemIds.add(ci.item)
            }
          })
        }

        if (Array.isArray(characters) && characters.length > 0) {
          characters.forEach((ch) => {
            collectUsedArtefactIds(ch.items)
          })
        }

        // Also reserve artefacts already selected in the current in-memory
        // character state, so pending local additions cannot pick the same
        // campaign artefact again before commit.
        collectUsedArtefactIds(this.character?.items)

        // When editing an existing artefact, keep its current static
        // Item selectable even though it is already in use.
        const currentStaticItemId =
          this.activeItem && this.activeItem.item ? this.activeItem.item : null

        items = wantsCampaignArtefacts
          ? matchingCampaignItems.filter((it) => {
              if (!it || it.id == null) return false
              if (currentStaticItemId && it.id === currentStaticItemId) {
                return true
              }
              return !usedItemIds.has(it.id)
            })
          : matchingCampaignItems
      }

      this.items = items

      // Build item-name options for the Basic tab. When the
      // filter is "All" or "Money", prefix each option label
      // with the item's concrete type so the dropdown reads
      // e.g. "Equipment - Bedroll" or "Money - Gold Pieces (gp)".
      // The underlying value remains the bare item name so
      // existing lookups by name still work correctly. Sort by
      // type then name for a stable, grouped list in the dropdown.
      const sortedItems = (this.items || []).slice().sort((a, b) => {
        if (requestedItemType === 'Artefact') {
          const campaignRefA = Number(a?.campaign_ref ?? 0)
          const campaignRefB = Number(b?.campaign_ref ?? 0)

          if (campaignRefA !== campaignRefB) {
            return campaignRefA - campaignRefB
          }

          const artefactNameA = (a?.name || '').toLowerCase()
          const artefactNameB = (b?.name || '').toLowerCase()
          if (artefactNameA < artefactNameB) return -1
          if (artefactNameA > artefactNameB) return 1
          return 0
        }

        if (requestedItemType === 'Money') {
          const moneySeqA = Number(a?.money_seq ?? a?.currency?.seq ?? 0)
          const moneySeqB = Number(b?.money_seq ?? b?.currency?.seq ?? 0)

          if (moneySeqA !== moneySeqB) {
            return moneySeqA - moneySeqB
          }

          const moneyNameA = (a?.name || '').toLowerCase()
          const moneyNameB = (b?.name || '').toLowerCase()
          if (moneyNameA < moneyNameB) return -1
          if (moneyNameA > moneyNameB) return 1
          return 0
        }

        const typeA = (
          getEffectiveItemType(a, getStaticItemById) ||
          a?.type ||
          ''
        ).toLowerCase()
        const typeB = (
          getEffectiveItemType(b, getStaticItemById) ||
          b?.type ||
          ''
        ).toLowerCase()
        if (typeA < typeB) return -1
        if (typeA > typeB) return 1

        const nameA = (a?.name || '').toLowerCase()
        const nameB = (b?.name || '').toLowerCase()
        if (nameA < nameB) return -1
        if (nameA > nameB) return 1
        return 0
      })

      this.item_names = sortedItems.map((it) => {
        const name = it?.name || ''
        const type =
          getEffectiveItemType(it, getStaticItemById) || it?.type || ''
        const campaignRef = Number(it?.campaign_ref ?? 0)

        const shouldPrefixType =
          (this.item_type === 'All' || this.item_type === 'Money') && type

        const label =
          requestedItemType === 'Artefact' && campaignRef > 0
            ? `${campaignRef} - ${name}`
            : shouldPrefixType
              ? `${type} - ${name}`
              : name

        return { label, value: name }
      })
      //--------------------------------------------------------------
      // containers (could be regular storage or artefact containers)
      //--------------------------------------------------------------
      console.log('XX character items = ', this.character.items)
      const containers = (this.character.items || []).filter((c) => {
        if (!c || c.id === this.activeItem.id) {
          return false
        }

        return c.storage === true || this.isArtefactContainer(c) === true
      })
      // sort containers
      this.containers = containers.slice().sort((a, b) => {
        const labelA = (a.display_name || a.name || '').toLowerCase()
        const labelB = (b.display_name || b.name || '').toLowerCase()
        if (labelA < labelB) return -1
        if (labelA > labelB) return 1
        return 0
      })
      //------------------
      // attach locations
      //------------------
      const attach_locations = (this.character.items || []).filter(
        (c) => c && c.attach_point === true && c.id !== this.activeItem.id,
      )
      this.attach_locations = attach_locations.slice().sort((a, b) => {
        const labelA = (a.display_name || a.name || '').toLowerCase()
        const labelB = (b.display_name || b.name || '').toLowerCase()
        if (labelA < labelB) return -1
        if (labelA > labelB) return 1
        return 0
      })
      //-----------------------------
      // get race for worn locations
      //-----------------------------
      const getRace = this.$store.getters['races/getRaceByName']
      const allRaces = this.$store.getters['races/getRaces'] || []
      const characterRaces =
        this.$store.getters['races/getCharacterRaces'] || []
      const availableRaces =
        characterRaces.length > 0 ? characterRaces : allRaces
      const raceName = this.character?.race_name
      const defaultHumanRace =
        availableRaces.find((entry) => entry?.name === 'Human') ||
        allRaces.find((entry) => entry?.name === 'Human') ||
        null
      const race =
        (typeof getRace === 'function' && raceName
          ? getRace(raceName)
          : null) ||
        availableRaces.find((entry) => entry?.name === raceName) ||
        allRaces.find((entry) => entry?.name === raceName) ||
        defaultHumanRace ||
        availableRaces[0] ||
        allRaces[0] ||
        null
      console.log('race for worn locations = ', race)
      //--------------------------
      // load worn item locations
      //--------------------------
      this.worn_locations = Array.isArray(race?.worn_item_locations)
        ? [...race.worn_item_locations].sort(
            (a, b) => (a.seq ?? 0) - (b.seq ?? 0),
          )
        : []
      //------------------------------------
      // load item lookups (where artefact)
      //------------------------------------
      if (this.isArtefactItem) {
        await this.$store.dispatch('characters/loadArtefactTypes')

        const artefactTypeRecords =
          this.$store.getters['characters/getArtefactTypes'] || []
        this.artefact_type_records = Array.isArray(artefactTypeRecords)
          ? artefactTypeRecords
          : []
        this.artefact_types = this.filteredArtefactTypes

        // get lookups
        const lookups = this.$store.getters['characters/getItemLookups'] || {}
        this.materials = Array.isArray(lookups.material) ? lookups.material : []
        this.ages = Array.isArray(lookups.age) ? lookups.age : []
        this.qualities = Array.isArray(lookups.quality) ? lookups.quality : []
        this.units = Array.isArray(lookups.count_unit) ? lookups.count_unit : []
        this.poison_types = Array.isArray(lookups.poison_type)
          ? lookups.poison_type
          : []
        this.healing_types = Array.isArray(lookups.healing_type)
          ? lookups.healing_type
          : []

        const getTraits = this.$store.getters['characters/getTraits'] || []
        this.traits = Array.isArray(getTraits) ? getTraits : []

        // get races
        const getRaces = this.$store.getters['races/getRaces'] || []
        const characterRaces =
          this.$store.getters['races/getCharacterRaces'] || []
        const mergedRaces = [...characterRaces, ...getRaces].filter(
          (race, index, list) => {
            if (!race) return false

            const key = race.id ?? race.name ?? index
            return (
              list.findIndex((entry) => {
                if (!entry) return false
                return (entry.id ?? entry.name) === key
              }) === index
            )
          },
        )
        this.races = Array.isArray(mergedRaces) ? mergedRaces : []
        console.log('races for artefact lookups = ', this.races)

        // get currencies
        const getCurrencies =
          this.$store.getters['characters/getCurrencies'] || []
        this.currencies = Array.isArray(getCurrencies) ? getCurrencies : []
        console.log('currencies for artefact lookups = ', this.currencies)
        // get default currency
        this.default_currency = this.currencies.find((c) => c.default) || null

        this.syncArtefactCurrencySelection()

        // for new artefact items with no currency yet, seed from default
        if (this.isNewItem) {
          const hasCurrency =
            this.local_item && this.local_item.currency != null
          if (!hasCurrency && this.default_currency) {
            this.local_item = this.local_item || {}
            this.local_item.currency = this.default_currency.id ?? null
          }
        }

        if (this.local_item?.artefact_type) {
          this.applyArtefactTypeFlags(this.local_item.artefact_type)

          const currentEnc = Number(
            this.local_item?.enc ?? this.activeItem?.enc ?? null,
          )
          const hasCurrentEnc = Number.isFinite(currentEnc) && currentEnc > 0

          if (this.isNewItem || !hasCurrentEnc) {
            this.applyArtefactTypeEnc(this.local_item.artefact_type)
          }

          if (
            this.requiresItemCardArtefactBaseItem(this.local_item.artefact_type)
          ) {
            this.$store
              .dispatch(
                'characters/loadArtefactBaseItems',
                this.local_item.artefact_type,
              )
              .catch(() => {})
          }
        }
      }

      if (!this.isNewItem && !this.hasInitialisedExistingItem) {
        this.syncOriginalItemComparableSnapshot()
      }
    },
    //========================================================
    // get linked adjustments
    //------------------------
    getLinkedAdjustments() {
      console.log('getLinkedAdjustments for item_type')

      this.item_characteristic_adj_reasons = ''
      this.item_ability_adj_reasons = ''
      this.item_skill_adj_reasons_applied = ''
      this.item_skill_adj_reasons_optional = ''

      // for new items or when no active item id yet,
      // there can be no linked adjustments â€“ safely skip
      if (!this.activeItem || !this.activeItem.id || !this.character) {
        return
      }

      const activeItemId = String(this.activeItem.id)
      const isAdjLinkedToActiveItem = (adj) => {
        if (!adj) return false

        const rawLinkedItem =
          adj.character_item_id ?? adj.character_item ?? null

        if (
          rawLinkedItem === null ||
          rawLinkedItem === undefined ||
          rawLinkedItem === ''
        ) {
          return false
        }

        if (typeof rawLinkedItem === 'object') {
          const objectId = rawLinkedItem.id ?? null
          return objectId !== null && objectId !== undefined
            ? String(objectId) === activeItemId
            : false
        }

        return String(rawLinkedItem) === activeItemId
      }

      //===============================================================
      // A. get all characteristic adjs
      //--------------------------------
      const characteristic_adjs = this.character.characteristic_adjs ?? []
      console.log('>>>> all characteristic_adjs = ', characteristic_adjs)
      //----------------------------
      // where characteristic adjs
      //----------------------------
      if (characteristic_adjs.length > 0) {
        //----------------------------------------------
        // load characteristic adjs for item as string
        //----------------------------------------------
        const item_characteristic_adjs = characteristic_adjs.filter((adj) => {
          return isAdjLinkedToActiveItem(adj)
        })
        if (item_characteristic_adjs.length > 0) {
          this.item_characteristic_adj_reasons = buildAdjReasonsString(
            item_characteristic_adjs,
          )
          console.log(
            '>>>> item_characteristic_adj_reasons = ',
            this.item_characteristic_adj_reasons,
          )
        }
      }
      //===============================================================
      // B. get all ability adjs
      //-------------------------
      const ability_adjs = this.character.ability_adjs ?? []
      console.log('>>>> all ability_adjs = ', ability_adjs)
      //--------------------
      // where ability adjs
      //--------------------
      if (ability_adjs.length > 0) {
        //--------------------------------------
        // load ability adjs for item as string
        //--------------------------------------
        const item_ability_adjs = ability_adjs.filter((adj) => {
          return isAdjLinkedToActiveItem(adj)
        })
        if (item_ability_adjs.length > 0) {
          this.item_ability_adj_reasons =
            buildAdjReasonsString(item_ability_adjs)
          console.log(
            '>>>> item_ability_adj_reasons = ',
            this.item_ability_adj_reasons,
          )
        }
      }
      //===============================================================
      // C. get all skill adjs
      //-----------------------
      const skill_adjs = this.character.skill_adjs ?? []
      console.log('>>>> all skill_adjs = ', skill_adjs)
      //------------------
      // where skill adjs
      //-----------------
      if (skill_adjs.length > 0) {
        //--------------------------------------------
        // load APPLIED skill adjs for item as string
        //--------------------------------------------
        const item_skill_adjs_applied = skill_adjs.filter((adj) => {
          return isAdjLinkedToActiveItem(adj) && adj.applied == true
        })
        if (item_skill_adjs_applied.length > 0) {
          this.item_skill_adj_reasons_applied = buildAdjReasonsString(
            item_skill_adjs_applied,
          )
          console.log(
            '>>>> item_skill_adj_reasons_applied = ',
            this.item_skill_adj_reasons_applied,
          )
        }
        //---------------------------------------------
        // load OPTIONAL skill adjs for item as string
        //---------------------------------------------
        const item_skill_adjs_optional = skill_adjs.filter((adj) => {
          return isAdjLinkedToActiveItem(adj) && adj.applied == false
        })
        if (item_skill_adjs_optional.length > 0) {
          this.item_skill_adj_reasons_optional = buildAdjReasonsString(
            item_skill_adjs_optional,
          )
          console.log(
            '>>>> item_skill_adj_reasons_optional = ',
            this.item_skill_adj_reasons_optional,
          )
        }
      }
    },
    //========================================================
    // get linked runes
    //------------------
    getLinkedRunes() {
      // for new items or when no active item id yet,
      // there can be no linked runes â€“ safely skip
      this.item_runes_integrated = ''
      this.item_runes_unintegrated = ''

      if (!this.activeItem || !this.activeItem.id || !this.character) {
        return
      }

      console.log('activeitem id = ', this.activeItem.id)
      const activeItemId = String(this.activeItem.id)

      const isRuneLinkedToActiveItem = (rune) => {
        if (!rune) return false

        const linkedId = rune.character_item ?? rune.character_item_id ?? null
        if (linkedId === null || linkedId === undefined || linkedId === '') {
          return false
        }

        return String(linkedId) === activeItemId
      }

      //---------------
      // get all runes
      //---------------
      const runes = this.character.runes ?? []
      console.log('>>>> all runes = ', runes)
      //-------------
      // where runes
      //-------------
      if (runes.length > 0) {
        //===============================================================
        // A. load INTEGRATED runes for item as string
        //---------------------------------------------
        const item_runes_integrated = runes.filter((r) => {
          return isRuneLinkedToActiveItem(r) && this.toBooleanFlag(r.integrated)
        })
        console.log('>>>> item_runes_integrated = ', item_runes_integrated)

        if (item_runes_integrated.length > 0) {
          this.item_runes_integrated = buildRunesString(item_runes_integrated)
          console.log(
            '>>>> item_runes_integrated = ',
            this.item_runes_integrated,
          )
        }
        //===============================================================
        // B. load UNINTEGRATED runes for item as string
        //-----------------------------------------------
        const item_runes_unintegrated = runes.filter((r) => {
          return (
            isRuneLinkedToActiveItem(r) && !this.toBooleanFlag(r.integrated)
          )
        })
        console.log('>>>> item_runes_unintegrated = ', item_runes_unintegrated)
        if (item_runes_unintegrated.length > 0) {
          this.item_runes_unintegrated = buildRunesString(
            item_runes_unintegrated,
          )
          console.log(
            '>>>> item_runes_unintegrated = ',
            this.item_runes_unintegrated,
          )
        }
      }
    },
    //========================================================
    // get linked spells (sorcery/divine)
    //------------------------------------
    getLinkedSpells() {
      if (!this.activeItem || !this.character) return

      const spells = this.character.spells ?? []
      if (!Array.isArray(spells) || spells.length === 0) {
        this.item_spells_sorcery = ''
        this.item_spells_divine = ''
        return
      }

      const itemId = this.activeItem.id
      const itemSpells = spells.filter((s) => {
        if (!s) {
          return false
        }

        const linkedItemId =
          s.character_item_id ??
          (typeof s.character_item === 'object'
            ? (s.character_item?.id ?? null)
            : (s.character_item ?? null))

        return linkedItemId !== null && String(linkedItemId) === String(itemId)
      })

      if (!Array.isArray(itemSpells) || itemSpells.length === 0) {
        this.item_spells_sorcery = ''
        this.item_spells_divine = ''
        return
      }

      const sorcerySpells = itemSpells.filter((s) => s.type === 'Sorcery')
      const divineSpells = itemSpells.filter((s) => s.type === 'Divine')

      const buildSpellString = (list) => {
        if (!Array.isArray(list) || list.length === 0) return ''
        let spellsString = ''
        list.forEach((s) => {
          const baseName = s.display_name || s.name || ''
          if (isEmpty(baseName)) return
          const mag =
            s.mag !== undefined && s.mag !== null ? ` (Mag ${s.mag})` : ''
          const entry = `${baseName}${mag}`
          if (isEmpty(spellsString)) {
            spellsString = entry
          } else {
            spellsString += '. ' + entry
          }
        })
        return spellsString
      }

      this.item_spells_sorcery = buildSpellString(sorcerySpells)
      this.item_spells_divine = buildSpellString(divineSpells)
    },
    getDeletionDisplayName(item = this.activeItem) {
      const baseName = String(item?.name ?? '').trim()
      const qty = Number.parseInt(item?.tally_qty, 10)

      if (!baseName) {
        return ''
      }

      if (!Number.isNaN(qty) && qty > 1) {
        return `${baseName} (x${qty})`
      }

      return baseName
    },
    async confirmArtefactDeleteStatus(statuses, subject) {
      while (true) {
        const itemStatus = await this.$refs.deleteArtefactDialog.show({
          title: 'Delete Artefact',
          message: 'Confirm Reason',
          subject,
          cancelButton: 'Cancel',
          statuses,
        })

        if (!itemStatus) {
          return null
        }

        if (String(itemStatus).trim().toLowerCase() !== 'dropped') {
          return itemStatus
        }

        const ok = await this.$refs.confirmDialog.show({
          title: 'Delete Artefact',
          message:
            'Dropped artefacts can be picked up/added\nby any character after saving your character.',
          subject,
          okButton: 'OK',
          cancelButton: 'Cancel',
        })

        if (ok) {
          return itemStatus
        }
      }
    },
    //=====================================================================
    // confirm deletion
    //------------------
    async confirmDeletion() {
      console.log('confirmDeletion for item = ', this.activeItem)
      //-----------------------------------------------
      // cannot delete item if container with contents
      //-----------------------------------------------
      if (
        this.activeItem.storage === true &&
        this.activeItem.contents_enc > 0
      ) {
        console.log('cannot delete item - container has contents...')
        await this.$refs.confirmDialog.show({
          title: 'Cannot Delete Container',
          message: 'Move contents before deletion.',
          cancelButton: 'Cancel',
        })
        return
      }
      //------------------------------------
      // proceed with deletion confirmation
      //------------------------------------
      const isArtefact = this.isArtefactItem
      let item_status = null

      if (isArtefact) {
        item_status = await this.confirmArtefactDeleteStatus(
          this.artefactStatusOptions,
          this.getDeletionDisplayName(),
        )

        if (!item_status) {
          this.delete = false
          return
        }
      } else {
        const ok = await this.$refs.confirmDialog.show({
          title: 'Delete Character Item',
          message: 'Confirm Deletion',
          subject: this.getDeletionDisplayName(),
          okButton: 'Delete',
          cancelButton: 'Cancel',
        })

        if (!ok) {
          this.delete = false
          return
        }
      }

      this.delete = true

      // when item has linked values, use ClearDialog so the
      // user can see exactly what will be removed
      if (this.needsClearing) {
        const resp = await this.$refs.clearDialog.show({
          title: 'Delete Character Item',
          subject: 'This will also remove the following linked values:',
          message: 'Confirm deletion of this item and all linked values:',
          item: this.activeItem,
          character: this.character,
          adjustments: {
            item_characteristic_adj_reasons:
              this.item_characteristic_adj_reasons,
            item_ability_adj_reasons: this.item_ability_adj_reasons,
            item_skill_adj_reasons_applied: this.item_skill_adj_reasons_applied,
            item_skill_adj_reasons_optional:
              this.item_skill_adj_reasons_optional,
          },
          okButton: 'Delete',
          cancelButton: 'Cancel',
        })

        if (resp && resp.cancel) {
          this.delete = false
          return
        }

        this.$emit('delete-character-item', {
          item: this.activeItem,
          delete_payload: item_status ? { status: item_status } : null,
        })
        this.closeSidePanel()
        return
      }

      this.$emit('delete-character-item', {
        item: this.activeItem,
        delete_payload: item_status ? { status: item_status } : null,
      })
      this.closeSidePanel()
    },
    //========================================================
    // on submit
    //-----------
    onSubmit() {
      console.log('---------------------------------')
      console.log('onSubmit')
      let hasErrors = false

      // reset validation state for this submit
      this.errors = []
      this.errorFields = []

      // -------------------------------------------------
      // shared validation: any countable item requires unit
      // -------------------------------------------------
      if (this.activeItem?.countable ?? false) {
        const unitName =
          this.activeItem?.unit_name ?? this.local_item?.unit_name ?? null
        if (!unitName) {
          const msg = window.bbUiErrorMsg(
            'Countable items require a unit to be selected',
          )
          if (!this.errors.includes(msg)) {
            this.errors.push(msg)
          }
          if (!this.errorFields.includes('unit_name')) {
            this.errorFields.push('unit_name')
          }
          hasErrors = true
        }
      }

      if (this.shortDescriptionRequired) {
        const shortDescription = this.activeItem?.short_description ?? ''
        if (
          typeof shortDescription !== 'string' ||
          shortDescription.trim() === ''
        ) {
          const msg = window.bbUiErrorMsg(
            'Short description is required for healing, poison, and specialist items',
          )
          if (!this.errors.includes(msg)) {
            this.errors.push(msg)
          }
          if (!this.errorFields.includes('short_description')) {
            this.errorFields.push('short_description')
          }
          hasErrors = true
        }
      }

      if (!this.validateRequiredArtefactBaseItem()) {
        hasErrors = true
      }

      //-----------------------
      // where adding new item
      //-----------------------
      if (this.isNewItem) {
        // artefact value/currency validation
        this.validateValueRange()
        if (this.errors.length > 0) {
          hasErrors = true
        }

        //---------------------------------------------------------------------------------
        // armour validation: if equipping and a specific location is required,
        // ensure an armour_location has been selected before save
        const isEquippedArmour =
          this.resolvedItemType === 'Armour' &&
          (this.activeItem?.equipped ?? false)
        const requiresArmourLocation =
          Array.isArray(this.armour_locations) &&
          this.armour_locations.length > 0
        const hasArmourLocation =
          (this.local_item && this.local_item.armour_location) ||
          this.activeItem?.armour_location

        if (isEquippedArmour && requiresArmourLocation && !hasArmourLocation) {
          this.errors.push(
            window.bbUiErrorMsg(
              'Equipped armour requires a specific armour location to be selected',
            ),
          )
          this.errorFields.push('armour_location')
          hasErrors = true
        }

        //---------------------------------------------------------------------------------
        // armour validation: prevent overlapping equipped armour at the same
        // character location with the same layer value (mirrors backend rule)
        if (isEquippedArmour) {
          const conflict = this.checkArmourLayerConflict()
          if (conflict) {
            this.errors.push(conflict.message)
            this.errorFields.push('equipped')
            hasErrors = true
          }
        }

        //---------------------------------------------------------------------------------
        // non-armour wearable items: if marked as worn and there are
        // available worn_locations, require the user to pick one
        if (this.item_type !== 'Armour' && (this.activeItem?.worn ?? false)) {
          const hasWornLocations =
            Array.isArray(this.worn_locations) && this.worn_locations.length > 0
          const hasWornLocation =
            (this.local_item && this.local_item.worn_location) ||
            this.activeItem?.worn_location

          if (hasWornLocations && !hasWornLocation) {
            const msg = window.bbUiErrorMsg(
              'Worn items require a specific worn location to be selected',
            )
            if (!this.errors.includes(msg)) {
              this.errors.push(msg)
            }
            if (!this.errorFields.includes('worn_location')) {
              this.errorFields.push('worn_location')
            }
            hasErrors = true
          }
        }

        //---------------------------------------------------------------------------------
        // artefact ENC validation: only validate non-campaign artefacts here.
        // Campaign artefact ENC is backend-driven.
        if (
          this.item_type === 'Artefact' &&
          !this.isEffectiveCampaignArtefact
        ) {
          const rawEnc = this.activeItem?.enc
          const encNum = Number(rawEnc)
          if (Number.isNaN(encNum) || encNum < 0.01) {
            const msg = window.bbUiErrorMsg(
              'Artefact Enc must be at least 0.01',
            )
            if (!this.errors.includes(msg)) {
              this.errors.push(msg)
            }
            if (!this.errorFields.includes('enc')) {
              this.errorFields.push('enc')
            }
            hasErrors = true
          }
        }

        //-----------------------
        // continue if no errors
        //-----------------------
        if (this.errors.length === 0) {
          console.log('ITEMCARD onSubmit....')

          // CRITICAL: sync local_item back to activeItem before building payload
          // This ensures that counter changes (ap, hp, etc) are captured
          if (this.local_item && this.activeItem) {
            this.activeItem.ap = this.local_item.ap ?? this.activeItem.ap
            this.activeItem.hp = this.local_item.hp ?? this.activeItem.hp
            this.activeItem.max_unit_count =
              this.local_item.max_unit_count ?? this.activeItem.max_unit_count
            this.activeItem.cur_unit_count =
              this.local_item.cur_unit_count ?? this.activeItem.cur_unit_count
            console.log(
              `DEBUG ItemCard.onSubmit AFTER SYNC: activeItem.ap=${this.activeItem.ap}, activeItem.hp=${this.activeItem.hp}, local_item.ap=${this.local_item.ap}, local_item.hp=${this.local_item.hp}`,
            )
          }

          // general fields
          const item_id =
            this.save_item_id ??
            this.local_item?.item ??
            this.activeItem?.item ??
            this.item?.item ??
            createTempId() // temp id for new artefacts (as item object created first in backend then linked character-item)
          const name =
            this.activeItem?.name ??
            this.local_item?.name ??
            this.item?.name ??
            '' // name for debug & to artefact parm (indicate static item creation)
          const character = this.getResolvedCharacterId()
          const type = this.activeItem?.type
            ? this.activeItem.type
            : (this.itemType ?? '') // use itemType as fallback for new artefacts (no static item)
          const tally_qty = this.activeItem?.tally_qty ?? 0
          const enc = this.local_item?.enc ?? this.activeItem?.enc ?? 0

          // descriptive fields
          const player_notes = this.activeItem?.player_notes ?? ''
          const description = this.activeItem?.description ?? '' // only updatable for artefacts (static item creation)
          const short_description = this.activeItem?.short_description ?? ''
          const image = this.activeItem?.image ?? null
          const image_file = this.activeItem?.image_file ?? null

          // equipment fields
          const equipment_type = getNormalisedEquipmentType(
            this.activeItem?.equipment_type,
          )

          // location/storage fields
          const container = this.activeItem?.container ?? null
          const worn_location = this.activeItem?.worn_location ?? null
          const attach_location = this.activeItem?.attach_location ?? null
          const armour_location = this.activeItem?.armour_location ?? null
          const coverage_locations = this.activeItem?.coverage_locations ?? []

          // container/contents fields
          const container_size = this.activeItem?.container_size ?? null
          const container_capacity = this.activeItem?.container_capacity ?? ''
          const container_size_short =
            this.activeItem?.container_size_short ?? ''
          const container_size_long = this.activeItem?.container_size_long ?? ''
          const contents_count_direct =
            this.activeItem?.contents_count_direct ?? null
          const contents_count_total =
            this.activeItem?.contents_count_total ?? null

          // magic fields
          const runes = this.activeItem?.runes ?? []
          const spells = this.activeItem?.spells ?? []

          // flags
          const starter = this.activeItem?.starter ?? false
          const active = this.activeItem?.active ?? false
          const campaign = this.activeItem?.campaign ?? false
          const tallyable = this.toBooleanFlag(
            this.local_item?.tallyable ?? this.activeItem?.tallyable,
          )
          const wearable = this.activeItem?.wearable ?? false
          // new artefacts should always be storeable so they can go in containers
          const storeable =
            this.item_type === 'Artefact'
              ? true
              : (this.activeItem?.storeable ?? false)
          const storage = this.activeItem?.storage ?? false
          const attach_point = this.activeItem?.attach_point ?? false
          const attachable = this.activeItem?.attachable ?? false
          const equipped = this.activeItem?.equipped ?? false
          const worn = this.activeItem?.worn ?? false
          const broken = this.activeItem?.broken ?? false
          const countable = this.activeItem?.countable ?? false
          const held = this.activeItem?.held ?? false

          // armour/weapon fields
          // Prefer local_item so counter edits are captured even if activeItem
          // has not yet reflected the latest reactive change.
          const ap = this.local_item?.ap ?? this.activeItem?.ap ?? 0
          const hp = this.local_item?.hp ?? this.activeItem?.hp ?? 0
          const coverage =
            this.local_item?.coverage ?? this.activeItem?.coverage ?? 0
          const dex_pen =
            this.local_item?.dex_pen ?? this.activeItem?.dex_pen ?? 0
          const layer = this.local_item?.layer ?? this.activeItem?.layer ?? 0

          // weapon fields
          const damage_dice = this.activeItem?.damage_dice ?? null
          const healing_type =
            this.local_item?.healing_type ??
            this.activeItem?.healing_type ??
            null
          const recovers =
            this.local_item?.recovers ?? this.activeItem?.recovers ?? null
          const poison_type =
            this.local_item?.poison_type ?? this.activeItem?.poison_type ?? null
          const pot = this.local_item?.pot ?? this.activeItem?.pot ?? null
          const effect =
            this.local_item?.effect ?? this.activeItem?.effect ?? null
          const delay = this.local_item?.delay ?? this.activeItem?.delay ?? null
          const duration =
            this.local_item?.duration ?? this.activeItem?.duration ?? null
          const resist =
            this.local_item?.resist ?? this.activeItem?.resist ?? null
          const impale = this.activeItem?.impale ?? null
          const set_charge = this.activeItem?.set_charge ?? null
          const load = this.activeItem?.load ?? null
          const range = this.activeItem?.range ?? null

          // countables
          // Prefer local_item so counter edits are captured even if activeItem
          // has not yet reflected the latest reactive change.
          const max_unit_count =
            this.local_item?.max_unit_count ??
            this.activeItem?.max_unit_count ??
            null
          const cur_unit_count =
            this.local_item?.cur_unit_count ??
            this.activeItem?.cur_unit_count ??
            null
          const unit_name =
            this.local_item?.unit_name ?? this.activeItem?.unit_name ?? null

          // selected artefact lookup values
          const artefact_type =
            this.local_item?.artefact_type ??
            this.activeItem?.artefact_type ??
            ''
          const race = this.local_item?.race ?? this.activeItem?.race ?? null
          const material = this.normaliseArtefactDescriptorForSave(
            'material',
            this.local_item?.material ?? this.activeItem?.material,
          )
          const quality = this.normaliseArtefactDescriptorForSave(
            'quality',
            this.local_item?.quality ?? this.activeItem?.quality,
          )
          const age = this.normaliseArtefactDescriptorForSave(
            'age',
            this.local_item?.age ?? this.activeItem?.age,
          )
          const currency =
            this.local_item?.currency ?? this.activeItem?.currency ?? ''
          const value_lower =
            this.local_item?.value_lower ?? this.activeItem?.value_lower ?? null
          const value_upper =
            this.local_item?.value_upper ?? this.activeItem?.value_upper ?? null
          const estimated_value =
            this.local_item?.estimated_value ??
            this.activeItem?.estimated_value ??
            false
          const pwr_storage =
            this.local_item?.pwr_storage ?? this.activeItem?.pwr_storage ?? 0
          const uniqueArtefactName = this.getUniqueArtefactName(name)

          this.local_item.name = uniqueArtefactName
          if (this.activeItem) {
            this.activeItem.name = uniqueArtefactName
          }

          // calc tot enc
          const tot_enc = parseFloat(enc) * parseInt(tally_qty, 10)

          //-----------------
          // set add payload
          //-----------------
          const add_parms = {
            name: uniqueArtefactName,
            payload: {
              id: createTempId(), // temp id for front-end tracking until backend creates real item and returns id

              // general fields
              item: item_id,
              name: uniqueArtefactName,
              character: character,
              type: type,
              tally_qty: tally_qty,
              enc: enc,
              tot_enc: tot_enc,

              // descriptive fields
              player_notes: player_notes,
              description: description,
              short_description: short_description,
              image: image,
              image_file: image_file,

              // equipment fields
              equipment_type: equipment_type,

              // location/storage fields
              container: container,
              worn_location: worn_location,
              attach_location: attach_location,
              armour_location: armour_location,
              coverage_locations: coverage_locations,

              // container/contents fields
              container_size: container_size,
              container_capacity: container_capacity,
              container_size_short: container_size_short,
              container_size_long: container_size_long,
              contents_count_direct: contents_count_direct,
              contents_count_total: contents_count_total,

              // magic fields
              runes: runes,
              spells: spells,

              // flags
              active: active,
              campaign: campaign,
              starter: starter,
              tallyable: tallyable,
              wearable: wearable,
              storeable: storeable,
              storage: storage,
              attach_point: attach_point,
              attachable: attachable,
              countable: countable,
              held: held,
              equipped: equipped,
              worn: worn,
              broken: broken,

              // armour/weapon fields
              ap: ap,
              hp: hp,
              coverage: coverage,
              dex_pen: dex_pen,
              layer: layer,

              // weapon fields
              skills: this.activeItem?.skills ?? this.local_item?.skills ?? [],
              damage_dice: damage_dice,
              impale: impale,
              set_charge: set_charge,
              load: load,
              range: range,

              // countables
              unit_name: unit_name,
              max_unit_count: max_unit_count,
              cur_unit_count: cur_unit_count,

              // healing/poison fields
              healing_type: healing_type,
              recovers: recovers,
              poison_type: poison_type,
              pot: pot,
              effect: effect,
              delay: delay,
              duration: duration,
              resist: resist,

              // artefact fields
              artefact_type: artefact_type,
              artefact_base_item:
                this.activeItem?.artefact_base_item ??
                this.local_item?.artefact_base_item ??
                null,
              trait: this.activeItem?.trait ?? this.local_item?.trait ?? null,
              race: race,
              material: material,
              quality: quality,
              age: age,
              pwr_storage: pwr_storage,
              currency: currency,
              value_lower: value_lower,
              value_upper: value_upper,
              estimated_value: estimated_value,
              item_traits: Array.isArray(this.local_item?.item_traits)
                ? [...this.local_item.item_traits]
                : [],

              // UI-only static-derived artefact flags persisted locally so
              // pending campaign artefacts reopen with the same per-field
              // editability before backend commit.
              _static_material_unknown:
                this.local_item._static_material_unknown,
              _static_quality_unknown: this.local_item._static_quality_unknown,
              _static_age_unknown: this.local_item._static_age_unknown,
              _static_race_unknown: this.local_item._static_race_unknown,
              _session_material_editable:
                this.local_item._session_material_editable,
              _session_quality_editable:
                this.local_item._session_quality_editable,
              _session_age_editable: this.local_item._session_age_editable,
              _session_race_editable: this.local_item._session_race_editable,
              _static_currency_unknown:
                this.local_item._static_currency_unknown,
              _static_value_unknown: this.local_item._static_value_unknown,
              _static_value_lower_unknown:
                this.local_item._static_value_lower_unknown,
              _static_value_upper_unknown:
                this.local_item._static_value_upper_unknown,
              _static_estimated_value_readonly:
                this.local_item._static_estimated_value_readonly,
              _static_enc_readonly: this.local_item._static_enc_readonly,
              _static_countable_readonly:
                this.local_item._static_countable_readonly,
              _static_unit_name_readonly:
                this.local_item._static_unit_name_readonly,
              _static_max_unit_count_readonly:
                this.local_item._static_max_unit_count_readonly,
            },
          }
          //----------------
          // emit add event
          //----------------
          console.log('emitting add-character-item with parms = ', add_parms)
          this.$emit('add-character-item', add_parms)
        }
      }

      // Existing items: keep manual item-name edits normalised so OK/save
      // does not persist a lowercase first letter after the field loses focus.
      if (!this.isNewItem && this.item_type === 'Artefact') {
        const currentName = this.activeItem?.name ?? ''
        const normalisedName = this.getUniqueArtefactName(currentName)

        if (normalisedName && normalisedName !== currentName) {
          this.local_item.name = normalisedName
          if (this.activeItem) {
            this.activeItem.name = normalisedName
          }
        }
      }
      //----------------------------------
      // where no errors close side panel
      //----------------------------------
      if (!hasErrors) {
        if (!this.isNewItem) {
          const itemDisplayName =
            (this.activeItem?.display_name ?? '').trim() ||
            (this.activeItem?.name ?? '').trim() ||
            (this.local_item?.display_name ?? '').trim() ||
            (this.local_item?.name ?? '').trim() ||
            'Item'
          this.$emit('confirm-item-ok', { displayName: itemDisplayName })
        }
        this.closeSidePanel()
      }
    },
    //========================================================
    // clear specific input error
    //----------------------------
    clearInputError(field) {
      if (!this.errorFields || this.errorFields.length === 0) return

      const index = this.errorFields.indexOf(field)
      if (index !== -1) {
        this.errors.splice(index, 1)
        this.errorFields.splice(index, 1)
      }
    },
    //========================================================
    // maintain worn-location error for new worn items
    //------------------------------------------------
    updateWornLocationErrorForNew() {
      const msg = window.bbUiErrorMsg(
        'Worn items require a specific worn location to be selected',
      )

      // only applies to new, non-armour items
      if (!this.isNewItem || this.resolvedItemType === 'Armour') {
        this.clearInputError('worn_location')
        return
      }

      const worn = this.activeItem?.worn ?? false
      if (!worn) {
        this.clearInputError('worn_location')
        return
      }

      const hasWornLocations =
        Array.isArray(this.worn_locations) && this.worn_locations.length > 0
      if (!hasWornLocations) {
        this.clearInputError('worn_location')
        return
      }

      const hasWornLocation =
        (this.local_item && this.local_item.worn_location) ||
        this.activeItem?.worn_location

      if (!hasWornLocation) {
        if (!this.errors.includes(msg)) {
          this.errors.push(msg)
        }
        if (!this.errorFields.includes('worn_location')) {
          this.errorFields.push('worn_location')
        }
      } else {
        this.clearInputError('worn_location')
      }
    },
    //========================================================
    // manage cancel
    //---------------
    manageCancel() {
      //-------------------------------------
      // where existing item & changes made
      //-------------------------------------
      if (!this.isNewItem && this.isChanged) {
        //----------------
        // revert changes
        //----------------
        if (this.local_item.tally_qty !== this.original_item.tally_qty) {
          this.local_item.tally_qty = this.original_item.tally_qty
          this.emitUpdate('tally_qty', this.local_item.tally_qty)
        }
        if (
          this.local_item.short_description !==
          this.original_item.short_description
        ) {
          this.local_item.short_description =
            this.original_item.short_description
          this.emitUpdate(
            'short_description',
            this.local_item.short_description,
          )
        }
        if (this.local_item.player_notes !== this.original_item.player_notes) {
          this.local_item.player_notes = this.original_item.player_notes
          this.emitUpdate('player_notes', this.local_item.player_notes)
        }
        if (this.local_item.description !== this.original_item.description) {
          this.local_item.description = this.original_item.description
          this.emitUpdate('description', this.local_item.description)
        }
        if (
          this.local_item.worn_location !== this.original_item.worn_location
        ) {
          this.local_item.worn_location = this.original_item.worn_location
          this.emitUpdate('worn_location', this.local_item.worn_location)
        }
        if (
          this.local_item.attach_location !== this.original_item.attach_location
        ) {
          this.local_item.attach_location = this.original_item.attach_location
          this.emitUpdate('attach_location', this.local_item.attach_location)
        }
        if (this.local_item.container !== this.original_item.container) {
          this.local_item.container = this.original_item.container
          this.emitUpdate('container', this.local_item.container)
        }
        if (this.local_item.runes !== this.original_item.runes) {
          this.local_item.runes = this.original_item.runes
          this.emitUpdate('runes', this.local_item.runes)
        }
        if (this.local_item.spells !== this.original_item.spells) {
          this.local_item.spells = this.original_item.spells
          this.emitUpdate('spells', this.local_item.spells)
        }
        if (
          this.local_item.armour_location !== this.original_item.armour_location
        ) {
          this.local_item.armour_location = this.original_item.armour_location
          this.emitUpdate('armour_location', this.local_item.armour_location)
        }
        if (this.local_item.ap !== this.original_item.ap) {
          this.local_item.ap = this.original_item.ap
          this.emitUpdate('ap', this.local_item.ap)
        }
        if (this.local_item.hp !== this.original_item.hp) {
          this.local_item.hp = this.original_item.hp
          this.emitUpdate('hp', this.local_item.hp)
        }
        if (this.local_item.enc !== this.original_item.enc) {
          this.local_item.enc = this.original_item.enc
          this.emitUpdate('enc', this.local_item.enc)
        }
        if (
          this.local_item.max_unit_count !== this.original_item.max_unit_count
        ) {
          this.local_item.max_unit_count = this.original_item.max_unit_count
          this.emitUpdate('max_unit_count', this.local_item.max_unit_count)
        }
        if (
          this.local_item.cur_unit_count !== this.original_item.cur_unit_count
        ) {
          this.local_item.cur_unit_count = this.original_item.cur_unit_count
          this.emitUpdate('cur_unit_count', this.local_item.cur_unit_count)
        }
        if (this.local_item.countable !== this.original_item.countable) {
          this.local_item.countable = this.original_item.countable
          this.emitUpdate('countable', this.local_item.countable)
        }
        if (this.local_item.unit_name !== this.original_item.unit_name) {
          this.local_item.unit_name = this.original_item.unit_name
          this.emitUpdate('unit_name', this.local_item.unit_name)
        }
        if (this.local_item.held !== this.original_item.held) {
          this.local_item.held = this.original_item.held
          this.emitUpdate('held', this.local_item.held)
        }
        if (
          this.activeItem &&
          this.activeItem.worn !== this.original_item.worn
        ) {
          this.activeItem.worn = this.original_item.worn
          this.emitUpdate('worn', this.activeItem.worn)
        }
        if (this.local_item.equipped !== this.original_item.equipped) {
          this.local_item.equipped = this.original_item.equipped
          this.emitUpdate('equipped', this.local_item.equipped)
        }
        if (
          this.local_item.artefact_type !== this.original_item.artefact_type
        ) {
          this.local_item.artefact_type = this.original_item.artefact_type
          this.emitUpdate('artefact_type', this.local_item.artefact_type)
        }
        if (
          this.local_item.artefact_base_item !==
          this.original_item.artefact_base_item
        ) {
          this.local_item.artefact_base_item =
            this.original_item.artefact_base_item
          this.emitUpdate(
            'artefact_base_item',
            this.local_item.artefact_base_item,
          )
        }
        if (this.local_item.trait !== this.original_item.trait) {
          this.local_item.trait = this.original_item.trait
          this.local_item.item_traits = Array.isArray(
            this.original_item.item_traits,
          )
            ? [...this.original_item.item_traits]
            : []
          this.emitUpdate('trait', this.local_item.trait)
        }
        if (this.local_item.material !== this.original_item.material) {
          this.local_item.material = this.original_item.material
          this.emitUpdate('material', this.local_item.material)
        }
        if (this.local_item.quality !== this.original_item.quality) {
          this.local_item.quality = this.original_item.quality
          this.emitUpdate('quality', this.local_item.quality)
        }
        if (this.local_item.age !== this.original_item.age) {
          this.local_item.age = this.original_item.age
          this.emitUpdate('age', this.local_item.age)
        }
        if (this.local_item.pwr_storage !== this.original_item.pwr_storage) {
          this.local_item.pwr_storage = this.original_item.pwr_storage
          this.emitUpdate('pwr_storage', this.local_item.pwr_storage)
        }
        if (this.local_item.race !== this.original_item.race) {
          this.local_item.race = this.original_item.race
          this.emitUpdate('race', this.local_item.race)
        }
        if (this.local_item.currency !== this.original_item.currency) {
          this.local_item.currency = this.original_item.currency
          this.emitUpdate('currency', this.local_item.currency)
        }
        if (
          this.activeItem &&
          this.activeItem.value_lower !== this.original_item.value_lower
        ) {
          this.activeItem.value_lower = this.original_item.value_lower
          this.emitUpdate('value_lower', this.activeItem.value_lower)
        }
        if (
          this.activeItem &&
          this.activeItem.value_upper !== this.original_item.value_upper
        ) {
          this.activeItem.value_upper = this.original_item.value_upper
          this.emitUpdate('value_upper', this.activeItem.value_upper)
        }
        if (
          this.activeItem &&
          this.activeItem.estimated_value !== this.original_item.estimated_value
        ) {
          this.activeItem.estimated_value = this.original_item.estimated_value
          this.emitUpdate('estimated_value', this.activeItem.estimated_value)
        }
        if (
          this.activeItem &&
          this.activeItem.image !== this.original_item.image
        ) {
          this.activeItem.image = this.original_item.image
          this.emitUpdate('image', this.activeItem.image)
        }
      }
      //------------------
      // close side panel
      //------------------
      this.closeSidePanel()
    },
    //========================================================
    // close side panel
    //------------------
    closeSidePanel() {
      this.$emit('close-item-panel')
    },
    //========================================================
    // delayed emit update
    //---------------------
    delayedEmitUpdate(key, value, delay = 600) {
      //--------------------------------------
      // only emit updates for existing items
      //--------------------------------------
      if (!this.activeItem || this.isNewItem) return
      //-----------------------------------
      // clear existing timer for this key
      //-----------------------------------
      if (this.pendingTimers[key]) {
        clearTimeout(this.pendingTimers[key])
      }
      //---------------
      // schedule emit
      //---------------
      this.pendingTimers[key] = setTimeout(() => {
        this.emitUpdate(key, value)
        delete this.pendingTimers[key]
      }, delay)
    },
    //========================================================
    // emit update
    //-------------
    emitUpdate(key, value) {
      //-----------------------------------------------
      // clear error for this field when value changes
      //-----------------------------------------------
      // this.clearError(key)
      //------------------------------
      // set additional args for emit
      //------------------------------
      const name = this.activeItem?.name ?? this.local_item.name ?? '' // for debug
      const id = this.activeItem?.id ?? this.local_item.id ?? null // for item processing (belt and braces)
      const localStaticFlags =
        this.item_type === 'Artefact'
          ? {
              trait: this.local_item.trait ?? null,
              item_traits: Array.isArray(this.local_item.item_traits)
                ? [...this.local_item.item_traits]
                : [],
              _static_material_unknown:
                this.local_item._static_material_unknown,
              _static_quality_unknown: this.local_item._static_quality_unknown,
              _static_age_unknown: this.local_item._static_age_unknown,
              _static_race_unknown: this.local_item._static_race_unknown,
              _session_material_editable:
                this.local_item._session_material_editable,
              _session_quality_editable:
                this.local_item._session_quality_editable,
              _session_age_editable: this.local_item._session_age_editable,
              _session_race_editable: this.local_item._session_race_editable,
              _static_currency_unknown:
                this.local_item._static_currency_unknown,
              _static_value_unknown: this.local_item._static_value_unknown,
              _static_value_lower_unknown:
                this.local_item._static_value_lower_unknown,
              _static_value_upper_unknown:
                this.local_item._static_value_upper_unknown,
              _static_estimated_value_readonly:
                this.local_item._static_estimated_value_readonly,
              _static_enc_readonly: this.local_item._static_enc_readonly,
              _static_countable_readonly:
                this.local_item._static_countable_readonly,
              _static_unit_name_readonly:
                this.local_item._static_unit_name_readonly,
              _static_max_unit_count_readonly:
                this.local_item._static_max_unit_count_readonly,
            }
          : null
      //-------------
      // emit update
      //-------------
      console.log(
        'emitUpdate key = ',
        key,
        ' value = ',
        value,
        ' name = ',
        name,
        ' id = ',
        id,
      )
      this.$emit('update-character-item', {
        type: 'items',
        key,
        value,
        name,
        id,
        ui_state: localStaticFlags,
      })
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {
    console.log('>>> item_type = ', this.item_type)
    this.emitter.on(
      'campaign-artefacts-updated',
      this.handleCampaignArtefactsUpdated,
    )
    //-----------------------------
    // set initial active data tab
    //-----------------------------
    const effectiveType =
      this.item_type === 'All'
        ? (this.item?.actual_type ?? this.item?.type ?? this.item_type)
        : this.item_type
    this.activeTab = effectiveType === 'Artefact' ? 'artefact' : 'basic'
    console.log('>>> activeTab = ', this.activeTab)
    //----------------
    // existing item
    //----------------
    if (this.item) {
      if (this.item_type === 'Artefact') {
        this.isHydratingExistingArtefact = true
      }

      console.log('>>> existing item = ', this.item)

      console.log('EXISTING ITEM = ', this.item)
      this.save_item_id = this.item?.item ?? null
      const staticItem = this.selectedStaticItem

      console.log('>>> static item = ', staticItem)

      const initialArtefactPwrStorage =
        this.resolveExistingArtefactPwrStorage(staticItem)

      //-------------------------------------------------------
      // copy item to local item for v-model (fields changing)
      //-------------------------------------------------------
      this.local_item = copyObjectNoRef(this.item) || {}
      this.local_item.pwr_storage = initialArtefactPwrStorage
      const staticArtefactImage = staticItem?.image ?? null
      const localItemImage = this.local_item?.image
      const activeItemImage = this.activeItem?.image
      const localItemImageMissing =
        localItemImage === null ||
        localItemImage === undefined ||
        (typeof localItemImage === 'string' && localItemImage.trim() === '')
      const activeItemImageMissing =
        activeItemImage === null ||
        activeItemImage === undefined ||
        (typeof activeItemImage === 'string' && activeItemImage.trim() === '')
      if (
        this.item_type === 'Artefact' &&
        localItemImageMissing &&
        staticArtefactImage
      ) {
        this.local_item.image = staticArtefactImage

        if (this.activeItem && activeItemImageMissing) {
          this.activeItem.image = staticArtefactImage
        }
      }
      if (
        this.local_item.player_notes === undefined &&
        this.local_item.notes !== undefined
      ) {
        this.local_item.player_notes = this.local_item.notes
      }
      console.log('COPIED this.local_item = ', this.local_item)

      // -----------------------------------------------------------------
      // existing armour item:
      // ensure armour-specific fields used by the ItemCard are populated
      // from static item so armour details & selectable locations
      // are available even when the item is currently unequipped
      console.log('>>> this.resolvedItemType = ', this.resolvedItemType)
      if (this.resolvedItemType === 'Armour' && this.item.name) {
        const staticItem = this.selectedStaticItem
        const propertyItem = this.propertySourceStaticItem

        if (propertyItem?.armour) {
          // headline armour stats (static, not equip-state dependent)
          this.local_item.coverage =
            propertyItem.armour.coverage ?? this.local_item.coverage ?? ''
          this.local_item.layer =
            propertyItem.armour.armour_material?.layer ??
            this.local_item.layer ??
            0
          const materialAp = propertyItem.armour.armour_material?.ap ?? 0
          const baseDexPen = propertyItem.armour.dex_pen ?? 0
          this.max_ap = materialAp
          // capture baseline armour values once so dex_pen can be
          // recomputed proportionally when character-item AP changes
          if (!this.armour_base_ap) {
            this.armour_base_ap = materialAp
          }
          if (
            !this.armour_base_dex_pen &&
            typeof propertyItem.armour.dex_pen === 'number'
          ) {
            this.armour_base_dex_pen = propertyItem.armour.dex_pen
          }
          // Only fall back to material AP if the character item
          // doesn't already have its own AP value (null/undefined).
          // A value of 0 is a valid per-character AP (e.g. fully destroyed armour)
          // and should be preserved.
          if (this.local_item.ap === null || this.local_item.ap === undefined) {
            this.local_item.ap = materialAp
          }

          // existing items - use character-item hp
          this.local_item.hp = this.local_item.hp ?? 0

          const numericDexPen = Number(this.local_item.dex_pen)
          const numericAp = Number(this.local_item.ap)
          const shouldRehydrateDexPen =
            this.local_item.dex_pen === null ||
            this.local_item.dex_pen === undefined ||
            (numericDexPen === 0 &&
              baseDexPen > 0 &&
              !Number.isNaN(numericAp) &&
              numericAp > 0)

          if (shouldRehydrateDexPen) {
            const ratio = materialAp > 0 ? numericAp / materialAp : 0
            this.local_item.dex_pen =
              materialAp > 0 && !Number.isNaN(ratio)
                ? baseDexPen * ratio
                : baseDexPen
          }

          // set max hp for validation of HP changes against max HP based on material (for existing items, assume backend has set this.item.hp to the correct current value)
          this.max_hp = propertyItem.armour.armour_material?.hp ?? 0

          // store full coverage_locations on the item for conflict checks
          if (!Array.isArray(this.local_item.coverage_locations)) {
            this.local_item.coverage_locations =
              propertyItem.armour.coverage_locations ?? []
          }

          // dropdown options for flexible (non-fixed) coverage locations,
          // excluding any locations already occupied by the same named
          // armour item on this character (except this item itself).
          const allCandidateLocs = Array.isArray(
            propertyItem.armour.coverage_locations,
          )
            ? propertyItem.armour.coverage_locations.filter(
                (loc) => loc && loc.fixed === false,
              )
            : []

          const currentItemId = this.local_item.id ?? this.item?.id ?? null
          const baseName =
            propertyItem.name || staticItem?.name || this.item?.name || ''

          this.armour_locations = allCandidateLocs.filter((loc) => {
            if (!loc || !loc.name) return false
            // find matching character armour location
            const cal = Array.isArray(this.character.armour_locations)
              ? this.character.armour_locations.find(
                  (c) => c && c.armour_location_name === loc.name,
                )
              : null

            if (!cal || !Array.isArray(cal.equipped_armour)) return true

            // is this location already used by the same named armour item
            // on this character (ignoring this item itself when editing)?
            const conflict = cal.equipped_armour.some((ea) => {
              if (!ea) return false
              if (currentItemId && ea.armour === currentItemId) return false
              return ea.armour_name === baseName
            })

            return !conflict
          })

          console.log('>>> armour_locations = ', this.armour_locations)
        }

        // -----------------------------------------------------------------
        // existing weapon item
      } else if (
        this.resolvedItemType === 'Melee Weapon' ||
        this.resolvedItemType === 'Ranged Weapon'
      ) {
        const selectedWeaponItem = this.selectedStaticItem
        const propertyWeaponItem = this.propertySourceStaticItem
        const selectedArtefactWeapon =
          selectedWeaponItem?.type === 'Artefact' &&
          Array.isArray(selectedWeaponItem?.skills)
            ? selectedWeaponItem
            : null
        const resolvedWeaponItem =
          selectedArtefactWeapon ??
          selectedWeaponItem ??
          propertyWeaponItem ??
          this.local_item
        const selectedWeaponDamageDice = selectedWeaponItem?.damage_dice ?? null
        const hasSelectedWeaponDamageDice =
          typeof selectedWeaponDamageDice === 'string'
            ? selectedWeaponDamageDice.trim() !== ''
            : selectedWeaponDamageDice !== null &&
              selectedWeaponDamageDice !== undefined
        const shouldPreferSelectedWeaponDamageDice =
          this.item_type === 'Artefact' &&
          selectedWeaponItem?.type === 'Artefact' &&
          hasSelectedWeaponDamageDice

        // existing items - use character-item hp
        this.local_item.hp = this.local_item.hp ?? 0

        if (shouldPreferSelectedWeaponDamageDice) {
          this.local_item.damage_dice = selectedWeaponDamageDice
        } else if (
          this.local_item.damage_dice === null ||
          this.local_item.damage_dice === undefined ||
          this.local_item.damage_dice === ''
        ) {
          this.local_item.damage_dice =
            selectedWeaponItem?.damage_dice ??
            getWeaponDamageDice(
              propertyWeaponItem ?? resolvedWeaponItem,
              this.character?.skills,
            )
        }
        const weaponAp = selectedWeaponItem?.ap ?? propertyWeaponItem?.ap ?? 0
        const resolvedWeaponAp = this.local_item.ap ?? weaponAp
        this.max_ap = Math.max(weaponAp, resolvedWeaponAp)
        // preserve any per-character AP already stored on the
        // CharacterItem; only fall back to static AP when missing
        if (this.local_item.ap === null || this.local_item.ap === undefined) {
          this.local_item.ap = weaponAp
        }
        this.max_hp = selectedWeaponItem?.hp ?? propertyWeaponItem?.hp ?? 0
        this.local_item.impale = resolvedWeaponItem?.impale ? 'Yes' : 'No'
        this.local_item.set_charge = resolvedWeaponItem?.set_charge
          ? 'Yes'
          : 'No'
        this.local_item.load = resolvedWeaponItem?.load ?? 0
        this.local_item.range = resolvedWeaponItem?.range ?? 0
        this.local_item.skills =
          resolvedWeaponItem?.skills ?? this.local_item.skills ?? []
        if (this.activeItem) {
          this.activeItem.damage_dice = this.local_item.damage_dice
        }

        // get cur str/dex chars
        this.local_item.str_cur =
          this.character?.characteristics?.find((c) => c.name === 'str')?.cur ??
          0
        this.local_item.dex_cur =
          this.character?.characteristics?.find((c) => c.name === 'dex')?.cur ??
          0

        // -----------------------------------------------------------------
        // existing healing item
      } else if (this.item_type === 'Healing') {
        console.log('EXISTING HEALING ITEM = ', this.item.name)

        const getItemByName =
          this.$store.getters['characters/getStaticItemByName']
        const staticItem =
          this.propertySourceStaticItem ||
          this.selectedStaticItem ||
          (typeof getItemByName === 'function'
            ? getItemByName(this.item.name)
            : null)

        // from static item
        this.local_item.healing_type = staticItem?.healing_type ?? ''
        this.local_item.recovers = staticItem?.recovers ?? ''
        this.local_item.delay = staticItem?.delay ?? ''
        if (this.local_item.countable && !this.local_item.unit_name) {
          this.local_item.unit_name = staticItem?.unit_name ?? null
        }

        // -----------------------------------------------------------------
        // existing poison item
      } else if (this.item_type === 'Poison') {
        console.log('EXISTING POISON ITEM = ', this.item.name)

        const getItemByName =
          this.$store.getters['characters/getStaticItemByName']
        const staticItem =
          this.propertySourceStaticItem ||
          this.selectedStaticItem ||
          (typeof getItemByName === 'function'
            ? getItemByName(this.item.name)
            : null)

        this.local_item.poison_type = staticItem?.poison_type ?? ''
        this.local_item.pot = staticItem?.pot ?? ''
        this.local_item.effect = staticItem?.effect ?? ''
        this.local_item.delay = staticItem?.delay ?? ''
        this.local_item.damage_dice = staticItem?.damage_dice ?? ''
        this.local_item.duration = staticItem?.duration ?? ''
        this.local_item.resist = staticItem?.resist ?? ''
        if (this.local_item.countable && !this.local_item.unit_name) {
          this.local_item.unit_name = staticItem?.unit_name ?? null
        }

        // -----------------------------------------------------------------
        // existing specialist item
      } else if (this.item_type === 'Specialist') {
        console.log('EXISTING SPECIALIST ITEM = ', this.item.name)

        const getItemByName =
          this.$store.getters['characters/getStaticItemByName']
        const staticItem =
          this.propertySourceStaticItem ||
          this.selectedStaticItem ||
          (typeof getItemByName === 'function'
            ? getItemByName(this.item.name)
            : null)

        this.local_item.effect = staticItem?.effect ?? ''
        this.local_item.delay = staticItem?.delay ?? ''
        this.local_item.damage_dice = staticItem?.damage_dice ?? ''
        this.local_item.duration = staticItem?.duration ?? ''
        this.local_item.resist = staticItem?.resist ?? ''
        if (this.local_item.countable && !this.local_item.unit_name) {
          this.local_item.unit_name = staticItem?.unit_name ?? null
        }

        // -----------------------------------------------------------------
        // existing artefact item
      } else if (this.item_type === 'Artefact') {
        console.log('>>> ARTEFACT this.item = ', this.item)

        console.log('EXISTING ARTEFACT ITEM = ', this.item.name)

        const staticItem = this.selectedStaticItem

        console.log('my staticItem = ', staticItem)

        // can be db committed artefact or pending commit (latter has not static item yet)
        // Prefer persisted character-item values for mutable artefact fields,
        // then fall back to the static item when the character item has none.
        const item = staticItem || this.item
        const rawCampaignRef = this.item?.campaign_ref ?? 0
        const campaignRef = Number(rawCampaignRef)
        const hasCampaignRef = !Number.isNaN(campaignRef) && campaignRef > 0
        const reopenedCampaignArtefact =
          (this.item?.campaign ?? hasCampaignRef) === true
        const resolvedTallyable = this.toBooleanFlag(
          this.item?.tallyable ??
            staticItem?.tallyable ??
            this.propertySourceStaticItem?.tallyable ??
            this.selectedStaticItem?.tallyable ??
            false,
        )
        const resolvedCountable = this.toBooleanFlag(
          this.item?.countable ??
            staticItem?.countable ??
            this.propertySourceStaticItem?.countable ??
            this.selectedStaticItem?.countable ??
            false,
        )

        this.local_item.active = this.item?.active ?? item?.active ?? false
        this.local_item.campaign = reopenedCampaignArtefact
        this.local_item.tallyable = resolvedTallyable
        this.local_item.countable = resolvedCountable

        if (this.activeItem) {
          this.activeItem.campaign = reopenedCampaignArtefact
          this.activeItem.tallyable = resolvedTallyable
          this.activeItem.countable = resolvedCountable
        }

        // from static item
        this.local_item.artefact_type =
          this.item?.artefact_type ?? staticItem?.artefact_type ?? ''
        this.local_item.trait = this.item?.trait ?? staticItem?.trait ?? null
        this.local_item.age = this.item?.age ?? staticItem?.age ?? ''
        this.local_item.quality =
          this.item?.quality ?? staticItem?.quality ?? ''
        this.local_item.material =
          this.item?.material ?? staticItem?.material ?? ''
        this.local_item.value_lower =
          this.item?.value_lower ?? staticItem?.value_lower ?? ''
        this.local_item.value_upper =
          this.item?.value_upper ?? staticItem?.value_upper ?? ''
        this.local_item.estimated_value =
          this.item?.estimated_value ?? staticItem?.estimated_value ?? false
        this.local_item.enc = this.item?.enc ?? staticItem?.enc ?? 0
        this.local_item.short_description =
          this.item?.short_description ?? staticItem?.short_description ?? ''
        this.local_item.description =
          this.item?.description ?? staticItem?.description ?? ''
        this.local_item.image = this.item?.image ?? staticItem?.image ?? null

        if (this.activeItem) {
          this.activeItem.enc = this.local_item.enc
          this.activeItem.short_description = this.local_item.short_description
        }

        // Restore base-item-derived props for persisted or pending artefact
        // items, including reopened local items that do not yet have a
        // backing static artefact record in the store.
        const artefactBaseItemId =
          staticItem?.artefact_base_item ??
          this.item?.artefact_base_item ??
          this.local_item?.artefact_base_item ??
          null

        if (artefactBaseItemId) {
          this.applyArtefactBaseItemDerivedProps(artefactBaseItemId)
        }

        // committed item - use static currency, existing but uncommitted - use already selected currency
        // for new artefacts - load default currency (handled in new-item branch / getDropDownValues)

        // normalise various currency representations (object/id/short code) to a clean id or null
        this.syncArtefactCurrencySelection()

        // Resolve the race id for this artefact. Prefer any pending
        // character-item override first, then fall back to the static item.
        const race_id = this.resolveArtefactRaceId(staticItem)

        // v-select with :reduce="reduceObject" expects the v-model to
        // hold a race id (not the full object or name). Preserve pending
        // local artefact race selections by normalising saved names to ids.
        this.local_item.race = race_id

        if (this.original_item) {
          this.original_item.artefact_type = this.local_item.artefact_type ?? ''
          this.original_item.age = this.local_item.age ?? ''
          this.original_item.quality = this.local_item.quality ?? ''
          this.original_item.material = this.local_item.material ?? ''
          this.original_item.value_lower = this.local_item.value_lower ?? null
          this.original_item.value_upper = this.local_item.value_upper ?? null
          this.original_item.estimated_value =
            this.local_item.estimated_value ?? false
          this.original_item.enc = this.local_item.enc ?? 0
          this.original_item.short_description =
            this.local_item.short_description ?? ''
          this.original_item.image = this.local_item.image ?? null
          this.original_item.race = this.local_item.race ?? null
          this.original_item.currency = this.local_item.currency ?? null
        }

        const isKnownStaticDescriptor = (rawValue) => {
          if (rawValue === null || rawValue === undefined) return false

          if (typeof rawValue === 'string') {
            const trimmed = rawValue.trim()
            return trimmed !== '' && trimmed !== 'Unknown'
          }

          if (typeof rawValue === 'object') {
            if (typeof rawValue.name === 'string') {
              const trimmedName = rawValue.name.trim()
              if (trimmedName !== '' && trimmedName !== 'Unknown') {
                return true
              }
            }

            return rawValue.id !== null && rawValue.id !== undefined
          }

          return true
        }

        const resolveStaticUnknownFlag = (
          persistedUnknown,
          staticValue,
          currentValue,
        ) => {
          // During an in-progress edit session we preserve the UI flag.
          // After a successful save/reload that flag is typically absent,
          // so infer lock state from the latest saved descriptor value.
          const resolvedValue =
            staticValue !== null && staticValue !== undefined
              ? staticValue
              : currentValue

          if (typeof persistedUnknown === 'boolean') {
            return persistedUnknown
          }

          return isKnownStaticDescriptor(resolvedValue) ? false : true
        }

        const persistedMaterialUnknown = this.item?._static_material_unknown
        const persistedQualityUnknown = this.item?._static_quality_unknown
        const persistedAgeUnknown = this.item?._static_age_unknown
        const persistedRaceUnknown = this.item?._static_race_unknown

        this.local_item._static_material_unknown = resolveStaticUnknownFlag(
          persistedMaterialUnknown,
          staticItem?.material,
          this.item?.material,
        )
        this.local_item._static_quality_unknown = resolveStaticUnknownFlag(
          persistedQualityUnknown,
          staticItem?.quality,
          this.item?.quality,
        )
        this.local_item._static_age_unknown = resolveStaticUnknownFlag(
          persistedAgeUnknown,
          staticItem?.age,
          this.item?.age,
        )
        this.local_item._static_race_unknown = resolveStaticUnknownFlag(
          persistedRaceUnknown,
          staticItem?.race,
          this.item?.race,
        )
        this.local_item._session_material_editable =
          this.item?._session_material_editable ??
          this.local_item._static_material_unknown
        this.local_item._session_quality_editable =
          this.item?._session_quality_editable ??
          this.local_item._static_quality_unknown
        this.local_item._session_age_editable =
          this.item?._session_age_editable ??
          this.local_item._static_age_unknown
        this.local_item._session_race_editable =
          this.item?._session_race_editable ??
          this.local_item._static_race_unknown

        if (reopenedCampaignArtefact) {
          this.local_item._static_currency_unknown =
            this.item?._static_currency_unknown ??
            this.local_item._static_currency_unknown
          this.local_item._static_value_unknown =
            this.item?._static_value_unknown ??
            this.local_item._static_value_unknown
          this.local_item._static_value_lower_unknown =
            this.item?._static_value_lower_unknown ??
            this.local_item._static_value_lower_unknown
          this.local_item._static_value_upper_unknown =
            this.item?._static_value_upper_unknown ??
            this.local_item._static_value_upper_unknown
          this.local_item._static_estimated_value_readonly =
            this.item?._static_estimated_value_readonly ??
            this.local_item._static_estimated_value_readonly
          this.local_item._static_enc_readonly =
            this.item?._static_enc_readonly ??
            this.local_item._static_enc_readonly
          this.local_item._static_countable_readonly =
            this.item?._static_countable_readonly ??
            this.local_item._static_countable_readonly
          this.local_item._static_unit_name_readonly =
            this.item?._static_unit_name_readonly ??
            this.local_item._static_unit_name_readonly
          this.local_item._static_max_unit_count_readonly =
            this.item?._static_max_unit_count_readonly ??
            this.local_item._static_max_unit_count_readonly
        }
        console.log(
          'mounted static_unit_name_readonly = ',
          this.local_item._static_unit_name_readonly,
        )

        // -------------------------------------------------------
        // ensure artefact countable/unit invariants on open
        // - when countable=false: unit_name=null and unit counts are 0
        // - when unit_name is null: unit counts are 0
        // (UI-only normalisation; does not emit backend updates)

        const countable =
          this.local_item.countable ??
          resolvedCountable ??
          item?.countable ??
          this.propertySourceStaticItem?.countable ??
          this.selectedStaticItem?.countable ??
          false
        this.local_item.countable = !!countable

        if (!this.local_item.countable) {
          // not countable: always clear unit information and counts
          this.local_item.unit_name = null
          this.local_item.max_unit_count = 0
          this.local_item.cur_unit_count = 0
          if (this.activeItem) {
            this.activeItem.unit_name = null
            this.activeItem.max_unit_count = 0
            this.activeItem.cur_unit_count = 0
          }
        } else {
          // countable: prefer any in-flight unit_name from the character item,
          // otherwise fall back to the static item's configured unit_name
          const staticUnitName =
            this.propertySourceStaticItem?.unit_name ??
            this.selectedStaticItem?.unit_name ??
            null
          const unitName =
            this.local_item.unit_name ??
            item?.unit_name ??
            staticUnitName ??
            null
          this.local_item.unit_name = unitName
          if (this.activeItem) {
            this.activeItem.unit_name = unitName
          }

          console.log('x staticUnitName = ', staticUnitName)
          console.log('x unitName = ', unitName)

          if (!unitName) {
            // countable but with no unit selected: do not show stale counts
            this.local_item.max_unit_count = 0
            this.local_item.cur_unit_count = 0
            if (this.activeItem) {
              this.activeItem.max_unit_count = 0
              this.activeItem.cur_unit_count = 0
            }
          } else {
            // CharacterItem max_unit_count is the editable/displayed value.
            // Static Item max is only a fallback for old rows with no
            // character-item value yet.
            const characterMax = Number(this.item?.max_unit_count)
            const localMax = Number(this.local_item?.max_unit_count)
            const staticMax = Number(item?.max_unit_count)
            const baseMax =
              !Number.isNaN(characterMax) && characterMax > 0
                ? characterMax
                : !Number.isNaN(localMax) && localMax > 0
                  ? localMax
                  : !Number.isNaN(staticMax) && staticMax > 0
                    ? staticMax
                    : 0
            this.local_item.max_unit_count = baseMax
            if (this.activeItem) {
              this.activeItem.max_unit_count = baseMax
            }

            // cur_unit_count is per-character; keep any value already loaded
            // from the CharacterItem, but if missing, default to baseMax
            if (
              this.local_item.cur_unit_count === null ||
              this.local_item.cur_unit_count === undefined
            ) {
              const charCur =
                this.item && typeof this.item.cur_unit_count === 'number'
                  ? this.item.cur_unit_count
                  : null
              this.local_item.cur_unit_count =
                charCur !== null ? charCur : baseMax
            }
            if (this.activeItem) {
              this.activeItem.cur_unit_count = this.local_item.cur_unit_count
            }
          }
        }
      }

      if (this.item_type === 'Artefact') {
        this.$nextTick(() => {
          this.syncOriginalItemComparableSnapshot({ force: true })
          this.isHydratingExistingArtefact = false
          this.hasInitialisedExistingItem = true
        })
      } else {
        this.$nextTick(() => {
          this.syncOriginalItemComparableSnapshot({ force: true })
          this.hasInitialisedExistingItem = true
        })
      }
      // -----------------------------------------------------------------
      // new item
    } else {
      this.local_item = setItemValues()
      this.hasInitialisedExistingItem = true
      console.log('NEW ITEM...')
      this.previousContainerId = this.local_item.container ?? null

      if (this.item_type === 'Artefact' && !this.local_item.campaign) {
        this.local_item.estimated_value = true
      }

      // for new artefacts, seed currency from the default
      if (this.item_type === 'Artefact' && this.default_currency) {
        this.local_item.currency = this.default_currency.id ?? null
      }

      // for new weapon items, seed current Str/Dex so that
      // weapon usability and mins-met UI have values
      if (
        this.resolvedItemType === 'Melee Weapon' ||
        this.resolvedItemType === 'Ranged Weapon'
      ) {
        const chars = Array.isArray(this.character?.characteristics)
          ? this.character.characteristics
          : []
        this.local_item.str_cur =
          chars.find((c) => c && c.name === 'str')?.cur ?? 0
        this.local_item.dex_cur =
          chars.find((c) => c && c.name === 'dex')?.cur ?? 0
      }
    }

    this.showWeaponWornLocationField = Boolean(
      (this.resolvedItemType === 'Melee Weapon' ||
        this.resolvedItemType === 'Ranged Weapon') &&
        this.activeItem?.worn,
    )
  },
  //========================================================
  // before unmount
  //----------------
  beforeUnmount() {
    // clear any outstanding timers
    Object.values(this.pendingTimers || {}).forEach((t) => clearTimeout(t))
    this.pendingTimers = {}
    this.emitter.off(
      'campaign-artefacts-updated',
      this.handleCampaignArtefactsUpdated,
    )
  },
}
</script>

<style scoped lang="css">
.bbActionIcon {
  opacity: 0.5;
  vertical-align: middle;
  /* constrain to a square icon that scales with font size */
  width: 5rem;
  height: 5rem;
  object-fit: contain;
  display: inline-block;
}

.bb-item-warning {
  /* match the orange warning/cancel tone */
  background-color: #cc7a00 !important;
  color: white !important;
}

.bb-item-warning-label {
  color: white !important;
}

/* Allow the right-hand data column to shrink inside the
   Bootstrap row so long, no-wrap headings don't force
   the entire column to wrap below the left button column. */
.bb-tooltip-wrapper {
  min-width: 0;
}

/* Ensure long item headings stay on one line and
   truncate with an ellipsis instead of wrapping. */
.bb-hdg-lg.no-wrap {
  display: block;
  width: 100%;
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bb-item-tabs {
  gap: 0.1rem;
}

.bb-item-tabs .nav-link {
  margin-right: 0 !important;
  padding: 0.9rem 1rem;
}
</style>

<style scoped>
/* Currency select (id=currency) - keep short_name lowercase */
:deep(#currency .vs__search),
:deep(#currency .vs__dropdown-menu),
:deep(#currency .vs__dropdown-option),
:deep(#currency .vs__selected) {
  text-transform: none !important;
}
</style>
